import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Gavel, Users, Vote, X, Play, Pause, ChevronDown, ChevronUp, Plus, Trophy, Settings, StickyNote } from 'lucide-react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { useModuleAccess } from './auth/useModuleAccess';
import { membershipHasModule } from './auth/moduleAccess';
import AuthPage from './pages/AuthPage';
import LandingPage from './pages/LandingPage';
import LegalPage from './pages/LegalPage';
import OrgPickerPage from './pages/OrgPickerPage';
import OrgHomePage from './pages/OrgHomePage';
import JoinPage from './pages/JoinPage';
import AdminPage from './pages/AdminPage';
import RosterPage from './pages/RosterPage';
import OrgSettingsPage from './pages/OrgSettingsPage';
import MeetingLogPage from './pages/MeetingLogPage';
import { getSupabaseClient, hasSupabaseConfig } from './lib/supabaseClient';
import {
  ensureOrgMeeting,
  loadCommitteesFromLocal,
  upsertCommitteeRemote,
} from './db/sync/membersSync';
import {
  loadCommitteesCloud,
  createCommitteeReferral,
} from './db/sync/committeesSync';
import { listOrgRoster } from './db/sync/rosterSync';
import ContextHelpTip from './components/common/ContextHelpTip';
import { downloadMinutesExport } from './utils/exportMinutesFile';
import MeetingHeader from './components/meeting/MeetingHeader';
import { TourProvider, useTours } from './tours/TourProvider';
import { isTourActive } from './tours/runTour';
import { useReportContextStore } from './store/reportContextStore';
import ReportIssueButton from './components/support/ReportIssueButton';
import ElevatedBanner from './components/platform/ElevatedBanner';
import DevBanner from './components/support/DevBanner';
import {
  buildTourDemoMotion,
  buildTourDemoSpeakers,
  buildTourDemoMinutes,
  TOUR_DEMO_MOTION_ID,
  TOUR_DEMO_JOIN_CODE,
} from './tours/tourDemoData';
import MotionMenu from './components/meeting/MotionMenu';
import MotionManagement from './components/meeting/MotionManagement';
import MotionBreadcrumbs from './components/meeting/MotionBreadcrumbs';
import ChairScript from './components/meeting/ChairScript';
import MeetingAssistantCard from './components/ai/MeetingAssistantCard';
import RollCallModal from './components/meeting/RollCallModal';
import AgendaWidget from './components/common/AgendaWidget';
import Timer from './components/common/Timer';
import ObserverView from './pages/ObserverView';
import ChairPromptView from './pages/ChairPromptView';
import ParticipantRemotePage from './pages/ParticipantRemotePage';
import {
  closeVoteSession,
  electionVotesFromCounts,
  ensureMeetingRoom,
  getVoteSessionTally,
  openVoteSession,
} from './utils/voteRemote';
import {
  ensureLiveMeetingSession,
  fetchMeetingSessionById,
  pushMeetingSessionState,
  postMeetingSessionSignal,
  postDriverMessageToChair,
  postChairMessageToDriver,
  postMeetingSessionEvent,
  endMeetingSession,
  subscribeMeetingSession,
  mapCloudSeatToUi,
  mapUiSeatToCloud,
  officerSessionUrl,
} from './utils/officerSession';
import Committees from './pages/Committees';
import ElectionsInterface from './components/voting/ElectionsInterface';
import {
  formatTime,
  mergeRemoteAscendingTimer,
  mergeRemoteDescendingTimer,
} from './utils/timeUtils';
import { getQuorumStatus } from './utils/quorumUtils';
import {
  getElectionChairScript,
  getElectionOfficeLabel,
} from './utils/electionChairScript';
import { processMotionResult, hasAutomaticEffects, getMotionEffectDescription, takeFromTable, resumePostponedMotion } from './utils/motionEffects';
import { MOTION_TYPES, getMotionsByClass, getMotionsBySmartContext, isMotionInOrder, MOTION_CLASSES } from './utils/motionTypes';
import {
  formatStatedMotionMinuteLine,
  getMinuteEntryDisplayText,
  formatCompletedMotionMinuteLine,
  formatDispositionMinuteLine,
  formatAmendmentDispositionText,
  getMinuteEntryKindLabel,
  getMinuteEntryOutcomeLabel,
  getMinuteEntryOutcomePillClass,
  getMinuteEntryOriginalSubtext,
  buildActivityMinuteGroups,
  isCanonicalNestParentForActivity,
  minuteExportSuppressesAdoptedDefeatedSuffix,
} from './utils/motionMinuteFormatting';
import { insertMotionByPrecedence, canApplyMotion, getImmediatelyPendingMotion } from './utils/motionPrecedence';
import { generateAIChairScript } from './utils/aiChairScript';
import { refineChairScript } from './utils/refineChairScript';
import { buildChairNoteSuggestions } from './utils/chairNoteSuggestions';
import { MotionEvent, getNextLegacyStatus } from './parliament/motionLifecycle';
import { analyzeVoteResult, getMotionVotingRequirements } from './utils/votingThresholds';
import {
  applyConsentToggleToAgenda,
  cloneAgendaForNewSession,
  cloneAgendaTemplateFromOrder,
  cloneConsentLinesForMeeting,
  cloneConsentLinesTemplate,
  createConsentLineItem,
  createStandardOrderOfBusiness,
  defaultOrganizationSettings,
  getActiveConsentItems,
  isConsentCalendarAgendaRow,
  MAIN_FROM_CONSENT_AGENDA_TYPE,
  meetingAgendaFromOrganizationSettings,
  meetingAgendaHasConsentCalendar,
  organizationSettingsFromBylaws,
  createStandardUnanimousConsentRequests,
} from './utils/consentAgenda';
import {
  applyPassedAmendmentToMain,
  proposeAmendedMainText,
} from './utils/amendmentText';
import {
  clearMeetingSessionFlag,
  isMeetingSessionStarted,
  markMeetingSessionStarted,
} from './utils/meetingSession';
import {
  archiveAndEndLiveSession,
  createAdjournmentMinuteEntry,
  isAdjournMotion,
  minutesIncludeAdjournment,
  textLooksLikeAdjourn,
} from './utils/finalizeMeeting';
import { formatSupabaseError } from './lib/supabaseErrors';

// Lazy so non-admins never download the platform console bundle.
const PlatformConsolePage = React.lazy(() => import('./pages/platform/PlatformConsolePage'));

const FLOOR_AND_MOTIONS_SECTION_HINT =
  'Everything you need to recognize business, run debate, and record votes—organized the way a meeting actually flows.';

const ACTIVITY_FEED_HINT =
  'Live feed of the session. Downloaded files are formal minutes (activity-only lines are omitted).';

function createDefaultMeetingSetup() {
  const orderOfBusiness = createStandardOrderOfBusiness();
  return {
    organizationName: '',
    totalMembers: 50,
    presentMembers: 29,
    quorumRequirement: 50,
    quorumType: 'percentage',
    orderOfBusiness,
    organizationSettings: defaultOrganizationSettings(orderOfBusiness),
    consentAgendaItems: [],
    consentPulledToFloorQueue: [],
    consentRemovalSuggestions: [],
    enableQrDeviceVoting: false,
    /** 'solo' = chair drives alone; 'assisted' = secretary drives, chair uses Prompt */
    sessionMode: 'solo',
    /** null = inherit org default (organizationSettings.enableRollCallRoster); true/false = this-meeting-only override. Resets to null on each new meeting. */
    rollCallOverride: null,
  };
}

/** Whether roll call is on for this meeting: a per-meeting override wins over the org default. */
function isRollCallEnabledFor(meetingSetup) {
  return meetingSetup.rollCallOverride ?? Boolean(meetingSetup.organizationSettings?.enableRollCallRoster);
}

const PocketParliamentarianApp = ({ forcedView = null, orgIdFromRoute = null }) => {
  const { activeMembership, activeOrganizationId, switchOrganization, ready: authReady, session } = useAuth();
  const { openCatalog } = useTours();
  const { canMeeting, canCommittees, canElections, canAdmin, skipAuth } = useModuleAccess();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Global app state
  const [currentView, setCurrentView] = useState(forcedView || 'meeting');
  /** Chair primary nav (Meeting / Committees / Elections) for projector window + ObserverView routing */
  const [chairNavView, setChairNavView] = useState('meeting');
  const globalTheme = 'light'; // Always use light theme

  // Meeting timer state
  const [meetingTimer, setMeetingTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  
  // Shared meeting state
  const [meetingSetup, setMeetingSetup] = useState(() => createDefaultMeetingSetup());
  const [parliamentaryMode, setParliamentaryMode] = useState('standard');
  
  const [currentAgendaIndex, setCurrentAgendaIndex] = useState(0);
  const [showAgendaWidget, setShowAgendaWidget] = useState(true);

  // Meeting timestamps for call to order and adjournment
  const [meetingStartTime, setMeetingStartTime] = useState(null);
  const [meetingEndTime, setMeetingEndTime] = useState(null);
  
  // Motion state
  const [motions, setMotions] = useState([]);
  const [currentMotion, setCurrentMotion] = useState(null);
  const [motionStack, setMotionStack] = useState([]);
  const [pendingUCRequest, setPendingUCRequest] = useState(null);

  // Motion disposal state
  const [tabledMotions, setTabledMotions] = useState([]);
  const [postponedMotions, setPostponedMotions] = useState([]);
  const [referredMotions, setReferredMotions] = useState([]);

  /** Chair confirms AI/deterministic proposed main-motion text after an amendment passes */
  const [amendTextConfirm, setAmendTextConfirm] = useState(null);
  const [amendConfirmDraft, setAmendConfirmDraft] = useState('');

  // Committee state — loaded per organization when Supabase is configured
  const [committees, setCommittees] = useState([]);
  // Reserved for future minutes sync keyed by cloud meeting row
  // eslint-disable-next-line no-unused-vars
  const [cloudMeetingId, setCloudMeetingId] = useState(null);
  const [voteRoomId, setVoteRoomId] = useState(null);
  const [voteJoinCode, setVoteJoinCode] = useState(null);
  const [voteRoomError, setVoteRoomError] = useState(null);
  const [voteRoomBusy, setVoteRoomBusy] = useState(false);
  const [remoteVoteSession, setRemoteVoteSession] = useState(null);
  const [remoteVoteTally, setRemoteVoteTally] = useState(null);
  const [remoteVoteBusy, setRemoteVoteBusy] = useState(false);
  const [remoteVoteError, setRemoteVoteError] = useState(null);
  const meetingSyncSnapshotRef = useRef({});
  /** Assisted mode: who has write authority for this browser session */
  const [driverSeat, setDriverSeat] = useState('local'); // 'local' | 'remote' (UI); cloud driver|chair
  const [chairSignal, setChairSignal] = useState(null); // { type, at, id } | null
  const [driverMessageToChair, setDriverMessageToChair] = useState(null); // { id, body, at }
  const [chairMessageToDriver, setChairMessageToDriver] = useState(null); // { id, body, at }
  const [driverMessageDraft, setDriverMessageDraft] = useState('');
  const [driverMessageBusy, setDriverMessageBusy] = useState(false);
  const [officerSessionId, setOfficerSessionId] = useState(() => searchParams.get('session') || null);
  const sessionRevisionRef = useRef(0);
  const applyingRemoteRef = useRef(false);
  /** Prefer postMessage timer ticks over lagging cloud state on Observer / Prompt. */
  const lastDriverTimersAtRef = useRef(0);
  const processedEventIdsRef = useRef(new Set());
  const eventsSeededRef = useRef(false);
  const chairSignalDismissRef = useRef(null);
  const officerClientSeat =
    searchParams.get('seat') === 'prompt' ||
    searchParams.get('chairPrompt') === '1' ||
    (typeof window !== 'undefined' &&
      (window.location.hash === '#chair-prompt' ||
        document.body.classList.contains('chair-prompt-window')))
      ? 'prompt'
      : searchParams.get('seat') === 'observer' ||
          searchParams.get('observer') === '1' ||
          (typeof window !== 'undefined' &&
            (window.location.hash === '#observer' ||
              document.body.classList.contains('observer-window')))
        ? 'observer'
        : 'driver';

  const isChairPromptMode = () =>
    typeof window !== 'undefined' &&
    (window.location.hash === '#chair-prompt' ||
      document.body.classList.contains('chair-prompt-window') ||
      searchParams.get('chairPrompt') === '1' ||
      searchParams.get('seat') === 'prompt');

  const isObserverMode = () =>
    typeof window !== 'undefined' &&
    (window.location.hash === '#observer' ||
      document.body.classList.contains('observer-window') ||
      searchParams.get('observer') === '1' ||
      searchParams.get('seat') === 'observer');

  // Sync route org + module-gated default view
  useEffect(() => {
    if (orgIdFromRoute && orgIdFromRoute !== activeOrganizationId) {
      void switchOrganization(orgIdFromRoute);
    }
  }, [orgIdFromRoute, activeOrganizationId, switchOrganization]);

  useEffect(() => {
    if (forcedView) {
      // Observer / Chair Dashboard popups — don't force chair "meeting" view over them
      if (isObserverMode()) {
        document.body.classList.add('observer-window');
        setCurrentView('observer');
        if (forcedView !== 'observer') setChairNavView(forcedView);
        return;
      }
      if (isChairPromptMode()) {
        document.body.classList.add('chair-prompt-window');
        setCurrentView('chair-prompt');
        if (forcedView !== 'observer') setChairNavView(forcedView);
        return;
      }
      setCurrentView(forcedView);
      if (forcedView !== 'observer') setChairNavView(forcedView);
      return;
    }
    const qView = searchParams.get('view');
    if (qView === 'elections' && (skipAuth || canElections)) {
      setCurrentView('elections');
      setChairNavView('elections');
    }
  }, [forcedView, searchParams, skipAuth, canElections]);

  useEffect(() => {
    const orgName = activeMembership?.organization_name;
    if (orgName) {
      setMeetingSetup((prev) =>
        prev.organizationName === orgName ? prev : { ...prev, organizationName: orgName }
      );
    }
  }, [activeMembership?.organization_name]);

  const ensureVoteRoom = useCallback(async () => {
    const orgId = orgIdFromRoute || activeOrganizationId;
    if (!orgId || !hasSupabaseConfig()) return null;
    if (!skipAuth && (!authReady || !session)) return null;

    setVoteRoomBusy(true);
    setVoteRoomError(null);
    try {
      const meeting = await ensureOrgMeeting(orgId);
      if (meeting?.id) setCloudMeetingId(meeting.id);
      const room = await ensureMeetingRoom(orgId, meeting?.id || null);
      setVoteRoomId(room.roomId);
      setVoteJoinCode(room.joinCode);
      return room;
    } catch (err) {
      const message = err?.message || 'Could not prepare voting room';
      setVoteRoomError(message);
      console.warn('ensure_meeting_room failed', err);
      return null;
    } finally {
      setVoteRoomBusy(false);
    }
  }, [orgIdFromRoute, activeOrganizationId, skipAuth, authReady, session]);

  const putSessionInUrl = useCallback(
    (sessionId, seat = null) => {
      const next = new URLSearchParams(searchParams);
      if (sessionId) next.set('session', sessionId);
      else next.delete('session');
      if (seat === 'prompt') {
        next.set('seat', 'prompt');
        next.set('chairPrompt', '1');
        next.delete('observer');
      } else if (seat === 'observer') {
        next.set('seat', 'observer');
        next.set('observer', '1');
        next.delete('chairPrompt');
      } else if (seat === 'driver') {
        next.delete('seat');
        next.delete('chairPrompt');
        next.delete('observer');
      }
      navigate({ search: next.toString() }, { replace: true });
    },
    [navigate, searchParams]
  );

  const ensureOfficerSession = useCallback(async () => {
    const orgId = orgIdFromRoute || activeOrganizationId;
    if (!orgId || !hasSupabaseConfig()) return null;
    if (!skipAuth && (!authReady || !session)) return null;

    try {
      const meeting = await ensureOrgMeeting(orgId);
      if (meeting?.id) setCloudMeetingId(meeting.id);
      const urlSession = searchParams.get('session');
      if (urlSession) {
        const existing = await fetchMeetingSessionById(urlSession);
        if (existing && existing.status === 'live') {
          setOfficerSessionId(existing.id);
          setDriverSeat(mapCloudSeatToUi(existing.driverSeat));
          // Seed known events so we don't replay Ready/Hold banners on join
          (Array.isArray(existing.events) ? existing.events : []).forEach((ev) => {
            if (ev?.id) processedEventIdsRef.current.add(ev.id);
          });
          if (existing.chairSignal?.id) {
            processedEventIdsRef.current.add(existing.chairSignal.id);
          } else if (existing.chairSignal?.type) {
            processedEventIdsRef.current.add(
              `${existing.chairSignal.type}:${existing.chairSignal.ts || existing.chairSignal.at || ''}`
            );
          }
          eventsSeededRef.current = true;
          // Always hydrate local state from the saved session on initial join — even for
          // a fresh driver join — so a resumed meeting doesn't start from blank defaults
          // (empty minutes, no start time). This is a one-time apply; subsequent polls
          // already gate on revision, so it can't clobber later live edits.
          if (
            existing.state &&
            typeof existing.state === 'object' &&
            Object.keys(existing.state).length > 0
          ) {
            // Apply BEFORE advancing revision so a failed apply can be retried by poll
            applyingRemoteRef.current = true;
            try {
              if (typeof updateMeetingStateFromMessageRef.current === 'function') {
                updateMeetingStateFromMessageRef.current(existing.state);
                markMeetingSessionStarted();
                sessionRevisionRef.current = existing.revision;
              }
              // If apply fn not ready yet, leave revision at 0 so poll retries
            } finally {
              applyingRemoteRef.current = false;
            }
          } else {
            sessionRevisionRef.current = existing.revision;
          }
          return existing;
        }
      }
      if (officerClientSeat !== 'driver') return null;
      const live = await ensureLiveMeetingSession(orgId, meeting?.id || null);
      setOfficerSessionId(live.id);
      sessionRevisionRef.current = live.revision;
      setDriverSeat(mapCloudSeatToUi(live.driverSeat));
      putSessionInUrl(live.id, 'driver');
      markMeetingSessionStarted();
      setTimeout(() => {
        try {
          syncMeetingStateRef.current?.();
        } catch {
          /* ignore */
        }
      }, 400);
      return live;
    } catch (err) {
      console.warn('ensure_live_meeting_session failed', err?.message || err);
      return null;
    }
  }, [
    orgIdFromRoute,
    activeOrganizationId,
    skipAuth,
    authReady,
    session,
    searchParams,
    officerClientSeat,
    putSessionInUrl,
  ]);

  // Ensure cloud officer session once per org/auth (QR vote room stays separate)
  const officerSessionBootRef = useRef('');
  useEffect(() => {
    const orgId = orgIdFromRoute || activeOrganizationId;
    if (!orgId || !hasSupabaseConfig()) return;
    if (!skipAuth && (!authReady || !session)) return;
    // A guided tour navigating here to preview screens must never create a real,
    // persistent live meeting session — that would leave a phantom "join meeting"
    // prompt on org home long after the tour ends.
    if (isTourActive()) return;
    const bootKey = `${orgId}:${searchParams.get('session') || ''}:${officerClientSeat}`;
    if (officerSessionBootRef.current === bootKey) return;
    officerSessionBootRef.current = bootKey;
    void ensureOfficerSession();
  }, [
    orgIdFromRoute,
    activeOrganizationId,
    skipAuth,
    authReady,
    session,
    searchParams,
    officerClientSeat,
    ensureOfficerSession,
  ]);

  const updateMeetingStateFromMessageRef = useRef(null);
  const syncMeetingStateRef = useRef(null);

  // Apply org bylaws_config when entering / switching orgs (or when admin updates settings).
  // Always refresh organizationSettings (UC presets, etc.). Only reset agenda/quorum for a new session,
  // and never overwrite a follower’s synced agenda from org defaults.
  const appliedOrgBylawsRef = useRef(null);
  useEffect(() => {
    const orgId = orgIdFromRoute || activeOrganizationId;
    if (!orgId || !activeMembership || activeMembership.organization_id !== orgId) return;

    const orgSettings = organizationSettingsFromBylaws(activeMembership.bylaws_config);
    const orgKey = `${orgId}:${JSON.stringify(activeMembership.bylaws_config || {})}`;
    if (appliedOrgBylawsRef.current === orgKey) return;
    appliedOrgBylawsRef.current = orgKey;

    const isFollowerSeat =
      officerClientSeat === 'prompt' || officerClientSeat === 'observer';
    const sessionStarted = isMeetingSessionStarted();
    // Followers (and any client joined via ?session=) keep their synced agenda; still take UC presets.
    const preserveSessionAgenda =
      sessionStarted || Boolean(officerSessionId) || Boolean(searchParams.get('session')) || isFollowerSeat;

    const { orderOfBusiness, consentAgendaItems } =
      meetingAgendaFromOrganizationSettings(orgSettings);

    setMeetingSetup((prev) => ({
      ...prev,
      organizationName: activeMembership.organization_name || prev.organizationName,
      organizationSettings: orgSettings,
      ...(preserveSessionAgenda
        ? {}
        : {
            orderOfBusiness,
            consentAgendaItems,
            consentPulledToFloorQueue: [],
            consentRemovalSuggestions: [],
            quorumType: orgSettings.defaultQuorumType || 'percentage',
            quorumRequirement:
              typeof orgSettings.defaultQuorumValue === 'number'
                ? orgSettings.defaultQuorumValue
                : 50,
          }),
    }));

    if (!preserveSessionAgenda) {
      setParliamentaryMode(orgSettings.defaultParliamentaryMode || 'standard');
    }
  }, [
    orgIdFromRoute,
    activeOrganizationId,
    activeMembership?.organization_id,
    activeMembership?.bylaws_config,
    activeMembership?.organization_name,
    officerSessionId,
    officerClientSeat,
    searchParams,
  ]);

  useEffect(() => {
    const orgId = orgIdFromRoute || activeOrganizationId;
    if (!orgId || !hasSupabaseConfig()) {
      if (!hasSupabaseConfig() && committees.length === 0) {
        setCommittees([
          {
            id: 1,
            name: 'Finance Committee',
            type: 'standing',
            chair: '',
            chairUserId: null,
            members: [],
            memberships: [],
            description: 'Oversees budget and financial matters',
            referredItems: [],
            created: new Date().toLocaleDateString(),
          },
          {
            id: 2,
            name: 'Governance Committee',
            type: 'standing',
            chair: '',
            chairUserId: null,
            members: [],
            memberships: [],
            description: 'Reviews bylaws and organizational structure',
            referredItems: [],
            created: new Date().toLocaleDateString(),
          },
        ]);
      }
      return;
    }
    // Wait for Supabase session before cloud meeting / voting room RPCs
    if (!skipAuth && (!authReady || !session)) return;

    let cancelled = false;
    (async () => {
      try {
        const cloud = await loadCommitteesCloud(orgId);
        if (!cancelled && Array.isArray(cloud)) {
          setCommittees(cloud);
          return;
        }
      } catch (err) {
        console.warn('[App] loadCommitteesCloud failed', err?.message || err);
      }
      if (cancelled) return;
      try {
        const local = await loadCommitteesFromLocal(orgId);
        if (!cancelled) setCommittees(local);
      } catch (err) {
        console.warn('[App] loadCommitteesFromLocal failed', err?.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgIdFromRoute, activeOrganizationId, skipAuth, authReady, session]);

  const setCommitteesPersisted = useCallback(
    (updater) => {
      setCommittees((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const orgId = orgIdFromRoute || activeOrganizationId;
        if (orgId && hasSupabaseConfig() && Array.isArray(next)) {
          // Persist only committees that changed vs prev (by id + JSON fingerprint of editable fields)
          const prevById = new Map((prev || []).map((c) => [c.id, c]));
          next.forEach((c) => {
            const before = prevById.get(c.id);
            const changed =
              !before ||
              before.name !== c.name ||
              before.description !== c.description ||
              before.chairUserId !== c.chairUserId ||
              JSON.stringify(before.memberships || []) !== JSON.stringify(c.memberships || []) ||
              JSON.stringify(before.referredItems || []) !== JSON.stringify(c.referredItems || []);
            if (changed) {
              void upsertCommitteeRemote(orgId, c);
            }
          });
        }
        return next;
      });
    },
    [orgIdFromRoute, activeOrganizationId]
  );
  
  // Meeting minutes state
  const [meetingMinutes, setMeetingMinutes] = useState([]);

  // Update existing minute entry (for live feed updates)
  function updateMinuteEntry(entryId, updates) {
    setMeetingMinutes(prev => prev.map(entry =>
      entry.id === entryId
        ? { ...entry, ...updates, lastUpdated: Date.now() }
        : entry
    ));
  }
  
  // Elections state
  const [elections, setElections] = useState([]);
  const [currentElection, setCurrentElection] = useState(null);
  const [candidateSpeechQueue, setCandidateSpeechQueue] = useState([]);
  const [currentCandidate, setCurrentCandidate] = useState(null);
  const [candidateSpeechTimeLimit, setCandidateSpeechTimeLimit] = useState(3);
  const [inElectionMode, setInElectionMode] = useState(false);

  // Election interface state (lifted from ElectionsInterface)
  const [electionScreen, setElectionScreen] = useState(1); // 1: setup, 2: nominations, 3: speeches, 4: voting, 5: results
  const [electionNominees, setElectionNominees] = useState([]);
  const [electionBallotVotes, setElectionBallotVotes] = useState({});
  const [electionWriteInCandidates, setElectionWriteInCandidates] = useState([]);
  const [electionInvalidBallots, setElectionInvalidBallots] = useState(0);

  // Push a lightweight context snapshot for the persistent Report Issue button
  // (it lives outside this component's tree and has no other way to see this state).
  useEffect(() => {
    useReportContextStore.getState().setReportContext({
      screenName: currentView,
      organizationName: meetingSetup.organizationName || null,
      parliamentaryMode,
      sessionMode: meetingSetup.sessionMode || null,
      quorumRequirement: meetingSetup.quorumRequirement ?? null,
      quorumType: meetingSetup.quorumType || null,
      inMeeting: Boolean(meetingStartTime) && !meetingEndTime,
      inElection: inElectionMode,
    });
  }, [
    currentView,
    meetingSetup.organizationName,
    meetingSetup.sessionMode,
    meetingSetup.quorumRequirement,
    meetingSetup.quorumType,
    parliamentaryMode,
    meetingStartTime,
    meetingEndTime,
    inElectionMode,
  ]);

  // Speaker management
  const [speakerQueue, setSpeakerQueue] = useState([]);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [speakerTime, setSpeakerTime] = useState(0);
  const [globalSpeakerTimeLimit, setGlobalSpeakerTimeLimit] = useState(120); // Default 2 minutes in seconds
  
  // UI state — wizard opens when starting a meeting (session flag not set)
  const [showSetupWizard, setShowSetupWizard] = useState(() => !isMeetingSessionStarted());

  // Prepare QR voting room only when enabled and wizard is complete
  useEffect(() => {
    if (!meetingSetup.enableQrDeviceVoting) {
      setVoteRoomId(null);
      setVoteJoinCode(null);
      setVoteRoomError(null);
      return;
    }
    if (showSetupWizard) return;
    void ensureVoteRoom();
  }, [meetingSetup.enableQrDeviceVoting, showSetupWizard, ensureVoteRoom]);

  const [showSettings, setShowSettings] = useState(false);
  const [showQuorumAdjust, setShowQuorumAdjust] = useState(false);
  const [showRollCallModal, setShowRollCallModal] = useState(false);
  const [orgRoster, setOrgRoster] = useState([]);
  const rollCallPromptedRef = useRef(null);
  const [referralMotion, setReferralMotion] = useState(null);
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [selectedCommitteeForReferral, setSelectedCommitteeForReferral] = useState(null);
  const [referralInstructions, setReferralInstructions] = useState('');
  const [creatingAdHocCommittee, setCreatingAdHocCommittee] = useState(false);
  const [adHocCommitteeName, setAdHocCommitteeName] = useState('');
  const [adHocCommitteeBusy, setAdHocCommitteeBusy] = useState(false);

  const submitReferral = async () => {
    let committee = selectedCommitteeForReferral;

    if (creatingAdHocCommittee) {
      const name = adHocCommitteeName.trim();
      if (!name) return;
      setAdHocCommitteeBusy(true);
      try {
        const orgId = orgIdFromRoute || activeOrganizationId;
        const draft = {
          id: hasSupabaseConfig() ? undefined : Date.now(),
          name,
          type: 'special',
          description: '',
          chairUserId: null,
          chair: '',
          memberships: [],
          members: [],
          referredItems: [],
          created: new Date().toLocaleDateString(),
        };
        committee = await upsertCommitteeRemote(orgId, draft);
        setCommittees((prev) => [...prev, committee]);
      } catch (err) {
        console.warn('[App] create ad hoc committee failed', err?.message || err);
        setAdHocCommitteeBusy(false);
        return;
      }
      setAdHocCommitteeBusy(false);
    }

    if (!committee) return;
    handleReferMotion(referralMotion, committee, referralInstructions);
    setSelectedCommitteeForReferral(null);
    setReferralInstructions('');
    setCreatingAdHocCommittee(false);
    setAdHocCommitteeName('');
  };

  // Settings
  const [motionSortMode, setMotionSortMode] = useState('smart');
  const [showObserverMotionCharacteristics, setShowObserverMotionCharacteristics] = useState(true);

  // Observer View Layout Management
  const [observerLayoutConfig, setObserverLayoutConfig] = useState(() => {
    // Try to load saved layout from localStorage
    const savedLayout = localStorage.getItem('observerLayoutConfig');
    if (savedLayout) {
      try {
        const parsed = JSON.parse(savedLayout);
        if (parsed?.layout) {
          // Self-heal: a layout saved before a new card existed won't have it —
          // append rather than silently hiding newly-added cards forever.
          if (!parsed.layout.some((item) => item.i === 'in-order-guide')) {
            parsed.layout.push({ i: 'in-order-guide', x: 0, y: 26, w: 12, h: 11, minW: 6, minH: 6 });
            parsed.visibleCards = { ...parsed.visibleCards, 'in-order-guide': true };
          }
          // Self-heal: the original motion-progress height (h:4) was too short
          // for its actual content, clipping the breadcrumb steppers out of
          // view. Bump any old saved layout still stuck at that height.
          const motionProgressItem = parsed.layout.find((item) => item.i === 'motion-progress');
          if (motionProgressItem && motionProgressItem.h < 14) {
            motionProgressItem.h = 14;
          }
        }
        return parsed;
      } catch (e) {
        console.warn('Failed to parse saved layout config, using default');
      }
    }

    // Default layout configuration
    return {
      editMode: false,
      gridCols: 12,
      layout: [
        { i: 'motion-progress', x: 0, y: 0, w: 12, h: 14, minW: 6, minH: 6 },
        { i: 'current-agenda', x: 0, y: 14, w: 4, h: 6, minW: 3, minH: 4 },
        { i: 'speaker-timer', x: 4, y: 14, w: 4, h: 6, minW: 3, minH: 4 },
        { i: 'speaker-queue', x: 8, y: 14, w: 4, h: 6, minW: 3, minH: 4 },
        { i: 'recent-activity', x: 0, y: 20, w: 12, h: 6, minW: 6, minH: 4 },
        { i: 'in-order-guide', x: 0, y: 26, w: 12, h: 11, minW: 6, minH: 6 },
        // Additional cards (hidden by default)
        { i: 'quorum-status', x: 0, y: 37, w: 3, h: 5, minW: 3, minH: 4 },
        { i: 'meeting-clock', x: 3, y: 37, w: 3, h: 5, minW: 3, minH: 4 },
        { i: 'motion-text', x: 6, y: 37, w: 6, h: 5, minW: 4, minH: 4 },
        { i: 'vote-tally', x: 0, y: 42, w: 4, h: 5, minW: 3, minH: 4 },
        { i: 'next-agenda', x: 4, y: 42, w: 4, h: 5, minW: 3, minH: 4 },
        { i: 'action-items', x: 8, y: 42, w: 4, h: 5, minW: 3, minH: 4 }
      ],
      visibleCards: {
        'motion-progress': true,
        'current-agenda': true,
        'speaker-timer': true,
        'speaker-queue': true,
        'recent-activity': true,
        'in-order-guide': true,
        'uc-requests': true,
        'quorum-status': false,
        'meeting-clock': false,
        'motion-text': false,
        'vote-tally': false,
        'next-agenda': false,
        'action-items': false
      }
    };
  });

  // Save layout to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('observerLayoutConfig', JSON.stringify(observerLayoutConfig));
  }, [observerLayoutConfig]);

  // Older synced payloads may omit organizationSettings; seed from current agenda.
  useEffect(() => {
    setMeetingSetup((prev) => {
      if (prev.organizationSettings?.defaultOrderOfBusiness?.length) return prev;
      const order =
        prev.orderOfBusiness?.length > 0
          ? prev.orderOfBusiness
          : createDefaultMeetingSetup().orderOfBusiness;
      return {
        ...prev,
        organizationSettings: defaultOrganizationSettings(order),
        consentAgendaItems: Array.isArray(prev.consentAgendaItems) ? prev.consentAgendaItems : [],
      };
    });
  }, []);

  useEffect(() => {
    setMeetingSetup((prev) => {
      let changed = false;
      const next = { ...prev };
      if (!Array.isArray(next.consentAgendaItems)) {
        next.consentAgendaItems = [];
        changed = true;
      }
      if (!Array.isArray(next.consentPulledToFloorQueue)) {
        next.consentPulledToFloorQueue = [];
        changed = true;
      }
      if (!Array.isArray(next.consentRemovalSuggestions)) {
        next.consentRemovalSuggestions = [];
        changed = true;
      }
      const org = next.organizationSettings;
      if (org && !Array.isArray(org.defaultConsentLines)) {
        next.organizationSettings = { ...org, defaultConsentLines: [] };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, []);

  // Layout management functions
  const updateObserverLayout = (newLayout) => {
    setObserverLayoutConfig(prev => ({
      ...prev,
      layout: newLayout
    }));
  };

  const toggleObserverEditMode = () => {
    setObserverLayoutConfig(prev => ({
      ...prev,
      editMode: !prev.editMode
    }));
  };

  const toggleObserverCard = (cardId) => {
    setObserverLayoutConfig(prev => ({
      ...prev,
      visibleCards: {
        ...prev.visibleCards,
        [cardId]: !prev.visibleCards[cardId]
      }
    }));
  };

  const resetObserverLayout = () => {
    const defaultConfig = {
      editMode: false,
      gridCols: 12,
      layout: [
        { i: 'motion-progress', x: 0, y: 0, w: 12, h: 14, minW: 6, minH: 6 },
        { i: 'current-agenda', x: 0, y: 14, w: 4, h: 6, minW: 3, minH: 4 },
        { i: 'speaker-timer', x: 4, y: 14, w: 4, h: 6, minW: 3, minH: 4 },
        { i: 'speaker-queue', x: 8, y: 14, w: 4, h: 6, minW: 3, minH: 4 },
        { i: 'recent-activity', x: 0, y: 20, w: 12, h: 6, minW: 6, minH: 4 },
        { i: 'in-order-guide', x: 0, y: 26, w: 12, h: 11, minW: 6, minH: 6 }
      ],
      visibleCards: {
        'motion-progress': true,
        'current-agenda': true,
        'speaker-timer': true,
        'speaker-queue': true,
        'recent-activity': true,
        'in-order-guide': true,
        'uc-requests': true,
        'quorum-status': false,
        'meeting-clock': false,
        'motion-text': false,
        'vote-tally': false,
        'next-agenda': false,
        'action-items': false
      }
    };
    setObserverLayoutConfig(defaultConfig);
  };

  // Handle URL hash routing for observer / chair-prompt windows
  useEffect(() => {
    const checkHash = () => {
      if (isObserverMode()) {
        document.body.classList.add('observer-window');
        setCurrentView('observer');
      } else if (isChairPromptMode()) {
        document.body.classList.add('chair-prompt-window');
        setCurrentView('chair-prompt');
      }
    };

    // Check on mount
    checkHash();

    // Listen for hash changes
    window.addEventListener('hashchange', checkHash);

    // Listen for messages from parent window or other windows
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      const isObserverPopup = isObserverMode();
      const isPromptPopup = isChairPromptMode();
      const isDisplayClient = isObserverPopup || isPromptPopup;

      if (event.data.type === 'SET_OBSERVER_MODE') {
        document.body.classList.add('observer-window');
        document.body.classList.remove('chair-prompt-window');
        setCurrentView('observer');
      } else if (event.data.type === 'SET_CHAIR_PROMPT_MODE') {
        document.body.classList.add('chair-prompt-window');
        document.body.classList.remove('observer-window');
        setCurrentView('chair-prompt');
      } else if (event.data.type === 'VIEW_CHANGE') {
        if (event.data.view === 'observer' || event.data.view === 'chair-prompt') return;
        if (isObserverPopup) {
          setChairNavView(event.data.view);
        } else if (!isPromptPopup) {
          setCurrentView(event.data.view);
        }

        // Also update meeting data if provided
        if (event.data.meetingData) {
          updateMeetingStateFromMessage(event.data.meetingData);
        }
      } else if (event.data.type === 'MEETING_TIMERS_UPDATE') {
        // High-frequency timer updates from the driver (authoritative for display windows)
        const timers = event.data.timers || {};
        lastDriverTimersAtRef.current = Date.now();
        if (typeof timers.meetingTimer === 'number') setMeetingTimer(timers.meetingTimer);
        if (typeof timers.speakerTime === 'number') setSpeakerTime(timers.speakerTime);
        if (typeof timers.isTimerRunning === 'boolean') setIsTimerRunning(timers.isTimerRunning);
      } else if (event.data.type === 'MEETING_STATE_UPDATE') {
        // Sync all meeting state changes from driver window
        if (event.data.meetingData) {
          updateMeetingStateFromMessage(event.data.meetingData);
        }
      } else if (event.data.type === 'CONSENT_SUGGEST_PULL' && !isDisplayClient) {
        const { lineId, text } = event.data;
        if (!lineId) return;
        setMeetingSetup((prev) => ({
          ...prev,
          consentRemovalSuggestions: [
            ...(prev.consentRemovalSuggestions || []),
            {
              id: `obs-${Date.now()}`,
              lineId,
              text: text || '',
              suggestedAt: new Date().toLocaleTimeString(),
            },
          ],
        }));
      } else if (event.data.type === 'CHAIR_SIGNAL' && !isDisplayClient) {
        const signalType = event.data.signal === 'hold' ? 'hold' : 'ready';
        setChairSignal({
          type: signalType,
          at: new Date().toLocaleTimeString(),
          id: `local-${Date.now()}`,
        });
      } else if (event.data.type === 'DRIVER_MESSAGE' && isDisplayClient) {
        const body = String(event.data.body || '').trim();
        if (body) {
          setDriverMessageToChair({
            id: `local-${Date.now()}`,
            body,
            at: new Date().toLocaleTimeString(),
          });
        }
      } else if (event.data.type === 'CHAIR_MESSAGE' && !isDisplayClient) {
        const body = String(event.data.body || '').trim();
        if (body) {
          setChairMessageToDriver({
            id: event.data.id || `local-${Date.now()}`,
            body,
            at: new Date().toLocaleTimeString(),
          });
        }
      } else if (event.data.type === 'CHAIR_NOTE' && !isDisplayClient) {
        const body = String(event.data.body || '').trim();
        const noteId = event.data.id || `local-${Date.now()}`;
        if (body) {
          const entryId = String(noteId).startsWith('chair-note-')
            ? noteId
            : `chair-note-${noteId}`;
          setMeetingMinutes((prev) => {
            if (prev.some((e) => e.id === entryId)) return prev;
            return [
              ...prev,
              {
                id: entryId,
                text: body,
                timestamp: new Date().toLocaleTimeString(),
                chronologicalOrder: Date.now(),
                type: 'chair_note',
                status: 'completed',
                result: null,
                activityOnly: false,
                lastUpdated: Date.now(),
              },
            ];
          });
        }
      } else if (event.data.type === 'CHAIR_TAKE_CONTROL' && !isDisplayClient) {
        setDriverSeat('remote');
        setChairSignal({
          type: 'take_control',
          at: new Date().toLocaleTimeString(),
        });
        try {
          window.focus();
        } catch {
          /* ignore */
        }
      } else if (event.data.type === 'CHAIR_RETURN_CONTROL' && !isDisplayClient) {
        setDriverSeat('local');
        setChairSignal({
          type: 'return_control',
          at: new Date().toLocaleTimeString(),
        });
      } else if (event.data.type === 'REQUEST_CURRENT_STATE') {
        // Use ref so this listener (mounted once) always sends latest joinCode / state
        const snap = meetingSyncSnapshotRef.current || {};
        event.source.postMessage(
          {
            type: 'MEETING_STATE_UPDATE',
            meetingData: snap,
          },
          window.location.origin
        );
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('hashchange', checkHash);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Observer popup: keep asking chair for state until join code arrives (when QR voting enabled)
  useEffect(() => {
    const isObserverPopup =
      typeof window !== 'undefined' &&
      (window.location.hash === '#observer' ||
        document.body.classList.contains('observer-window') ||
        searchParams.get('observer') === '1');
    if (!isObserverPopup || !meetingSetup.enableQrDeviceVoting || voteJoinCode || !window.opener) {
      return undefined;
    }

    const request = () => {
      try {
        window.opener.postMessage({ type: 'REQUEST_CURRENT_STATE' }, window.location.origin);
      } catch {
        /* opener closed */
      }
    };
    request();
    const id = setInterval(request, 2000);
    return () => clearInterval(id);
  }, [voteJoinCode, meetingSetup.enableQrDeviceVoting, searchParams]);

  // Chair Dashboard popup: keep asking driver for state until opener responds
  useEffect(() => {
    const isPromptPopup =
      typeof window !== 'undefined' &&
      (window.location.hash === '#chair-prompt' ||
        document.body.classList.contains('chair-prompt-window') ||
        searchParams.get('chairPrompt') === '1');
    if (!isPromptPopup || !window.opener) {
      return undefined;
    }

    const request = () => {
      try {
        window.opener.postMessage({ type: 'REQUEST_CURRENT_STATE' }, window.location.origin);
      } catch {
        /* opener closed */
      }
    };
    request();
    const id = setInterval(request, 2000);
    return () => clearInterval(id);
  }, [searchParams]);

  // Keep a fresh snapshot for observer REQUEST_CURRENT_STATE (avoids stale closures)
  useEffect(() => {
    meetingSyncSnapshotRef.current = {
      meetingSetup,
      meetingTimer,
      isTimerRunning,
      currentView: chairNavView || currentView,
      currentMotion,
      motions,
      motionStack,
      speakerQueue,
      currentSpeaker,
      speakerTime,
      speakerTimeLimit: globalSpeakerTimeLimit,
      meetingMinutes,
      currentAgendaIndex,
      currentElection,
      elections,
      candidateSpeechQueue,
      currentCandidate,
      inElectionMode,
      electionScreen,
      electionNominees,
      electionBallotVotes,
      electionWriteInCandidates,
      electionInvalidBallots,
      committees,
      voteJoinCode,
      pendingUCRequest,
      driverSeat,
    };
  }, [
    meetingSetup,
    meetingTimer,
    isTimerRunning,
    chairNavView,
    currentView,
    currentMotion,
    motions,
    motionStack,
    speakerQueue,
    currentSpeaker,
    speakerTime,
    globalSpeakerTimeLimit,
    meetingMinutes,
    currentAgendaIndex,
    currentElection,
    elections,
    candidateSpeechQueue,
    currentCandidate,
    inElectionMode,
    electionScreen,
    electionNominees,
    electionBallotVotes,
    electionWriteInCandidates,
    electionInvalidBallots,
    committees,
    voteJoinCode,
    pendingUCRequest,
    driverSeat,
  ]);

  // Function to update meeting state from observer / chair-prompt window messages
  const updateMeetingStateFromMessage = (meetingData) => {
    const isObserverPopup = isObserverMode();
    const isPromptPopup = isChairPromptMode();
    const isDisplayClient = isObserverPopup || isPromptPopup;

    // Update all state from the received data
    setMeetingSetup(meetingData.meetingSetup || {});
    if (isDisplayClient) {
      if (typeof meetingData.meetingTimer === 'number') {
        setMeetingTimer((prev) => mergeRemoteAscendingTimer(prev, meetingData.meetingTimer));
      }
    } else {
      setMeetingTimer(meetingData.meetingTimer || 0);
      setSpeakerTime(meetingData.speakerTime || 0);
    }
    setIsTimerRunning(meetingData.isTimerRunning || false);
    if (isObserverPopup) {
      const incoming = meetingData.currentView || 'meeting';
      if (incoming !== 'observer' && incoming !== 'chair-prompt') {
        setChairNavView(incoming);
      }
      setCurrentView('observer');
    } else if (isPromptPopup) {
      setCurrentView('chair-prompt');
    } else {
      setCurrentView(meetingData.currentView || 'meeting');
    }

    setCurrentMotion(meetingData.currentMotion || null);
    setMotions(meetingData.motions || []);
    setMotionStack(meetingData.motionStack || []);

    setSpeakerQueue(meetingData.speakerQueue || []);
    setCurrentSpeaker(meetingData.currentSpeaker || null);
    if (!isDisplayClient) {
      setSpeakerTime(meetingData.speakerTime || 0);
    } else if (typeof meetingData.speakerTime === 'number') {
      setSpeakerTime((prev) => mergeRemoteDescendingTimer(prev, meetingData.speakerTime));
    }

    setMeetingMinutes(meetingData.meetingMinutes || []);
    setCurrentAgendaIndex(meetingData.currentAgendaIndex || 0);
    if (meetingData.meetingStartTime !== undefined) {
      setMeetingStartTime(meetingData.meetingStartTime ? new Date(meetingData.meetingStartTime) : null);
    }
    if (meetingData.meetingEndTime !== undefined) {
      setMeetingEndTime(meetingData.meetingEndTime || null);
    }

    setCurrentElection(meetingData.currentElection || null);
    setElections(meetingData.elections || []);
    setCandidateSpeechQueue(meetingData.candidateSpeechQueue || []);
    setCurrentCandidate(meetingData.currentCandidate || null);
    setInElectionMode(meetingData.inElectionMode || false);

    // Election interface state
    setElectionScreen(meetingData.electionScreen || 1);
    setElectionNominees(meetingData.electionNominees || []);
    setElectionBallotVotes(meetingData.electionBallotVotes || {});
    setElectionWriteInCandidates(meetingData.electionWriteInCandidates || []);
    setElectionInvalidBallots(meetingData.electionInvalidBallots || 0);

    setCommittees(meetingData.committees || []);

    if (meetingData.pendingUCRequest !== undefined) {
      setPendingUCRequest(meetingData.pendingUCRequest || null);
    }

    if (meetingData.voteJoinCode !== undefined) {
      setVoteJoinCode(meetingData.voteJoinCode || null);
    }

    if (meetingData.driverSeat === 'local' || meetingData.driverSeat === 'remote') {
      setDriverSeat(meetingData.driverSeat);
    }
  };
  updateMeetingStateFromMessageRef.current = updateMeetingStateFromMessage;

  const applyOfficerSessionRow = useCallback((row) => {
    if (!row) return;

    const events = Array.isArray(row.events) ? row.events : [];

    // First snapshot for this session: remember event ids without firing UI
    if (!eventsSeededRef.current) {
      events.forEach((ev) => {
        if (ev?.id) processedEventIdsRef.current.add(ev.id);
      });
      if (row.chairSignal?.id) {
        processedEventIdsRef.current.add(row.chairSignal.id);
      } else if (row.chairSignal?.type) {
        processedEventIdsRef.current.add(
          `${row.chairSignal.type}:${row.chairSignal.ts || row.chairSignal.at || ''}`
        );
      }
      eventsSeededRef.current = true;
    } else {
      // Process append-only officer events (Ready/Hold + Driver messages)
      events.forEach((ev) => {
        const id = ev?.id;
        if (!id || processedEventIdsRef.current.has(id)) return;
        processedEventIdsRef.current.add(id);
        const kind = String(ev.kind || '').toLowerCase();
        if (kind === 'ready' || kind === 'hold' || kind === 'take_control' || kind === 'return_control') {
          setChairSignal({
            type: kind,
            at: ev.ts || ev.at || new Date().toLocaleTimeString(),
            id,
          });
          if (kind === 'take_control') setDriverSeat('remote');
          if (kind === 'return_control') setDriverSeat('local');
        }
        if (kind === 'driver_message') {
          setDriverMessageToChair({
            id,
            body: ev.body || '',
            at: ev.ts || new Date().toLocaleTimeString(),
          });
        }
        if (kind === 'chair_message') {
          setChairMessageToDriver({
            id,
            body: ev.body || '',
            at: ev.ts || new Date().toLocaleTimeString(),
          });
        }
        if (kind === 'chair_note') {
          const text = String(ev.body || '').trim();
          if (text) {
            const entryId = `chair-note-${id}`;
            setMeetingMinutes((prev) => {
              if (prev.some((e) => e.id === entryId)) return prev;
              return [
                ...prev,
                {
                  id: entryId,
                  text,
                  timestamp: new Date().toLocaleTimeString(),
                  chronologicalOrder: Date.now(),
                  type: 'chair_note',
                  status: 'completed',
                  result: null,
                  activityOnly: false,
                  lastUpdated: Date.now(),
                },
              ];
            });
          }
        }
      });

      // Legacy chair_signal when events column is empty / migration not applied
      if (events.length === 0 && row.chairSignal?.type) {
        const sigId =
          row.chairSignal.id ||
          `${row.chairSignal.type}:${row.chairSignal.ts || row.chairSignal.at || ''}`;
        if (!processedEventIdsRef.current.has(sigId)) {
          processedEventIdsRef.current.add(sigId);
          setChairSignal({
            type: row.chairSignal.type,
            at: row.chairSignal.at || row.chairSignal.ts || null,
            id: sigId,
          });
        }
      }
    }

    if (row.driverSeat === 'driver' || row.driverSeat === 'chair') {
      setDriverSeat(mapCloudSeatToUi(row.driverSeat));
    }

    const seat =
      searchParams.get('seat') === 'prompt' ||
      searchParams.get('chairPrompt') === '1' ||
      document.body.classList.contains('chair-prompt-window')
        ? 'prompt'
        : searchParams.get('seat') === 'observer' ||
            searchParams.get('observer') === '1' ||
            document.body.classList.contains('observer-window')
          ? 'observer'
          : 'driver';
    const follower =
      seat === 'prompt' || seat === 'observer' || (seat === 'driver' && row.driverSeat === 'chair');

    // Always refresh floor timers/queue for followers (even when revision unchanged)
    if (follower && row.state && typeof row.state === 'object' && Object.keys(row.state).length > 0) {
      const s = row.state;
      const preferDriverTimers = Date.now() - lastDriverTimersAtRef.current < 2500;
      if (!preferDriverTimers) {
        if (typeof s.meetingTimer === 'number') {
          setMeetingTimer((prev) => mergeRemoteAscendingTimer(prev, s.meetingTimer));
        }
        if (typeof s.speakerTime === 'number') {
          setSpeakerTime((prev) => mergeRemoteDescendingTimer(prev, s.speakerTime));
        }
        if (typeof s.isTimerRunning === 'boolean') setIsTimerRunning(s.isTimerRunning);
      }
      if (typeof s.speakerTimeLimit === 'number') setGlobalSpeakerTimeLimit(s.speakerTimeLimit);
      if ('speakerQueue' in s) setSpeakerQueue(Array.isArray(s.speakerQueue) ? s.speakerQueue : []);
      if ('currentSpeaker' in s) setCurrentSpeaker(s.currentSpeaker || null);
    }

    // Same revision: skip full parliamentary re-apply (avoids script flicker)
    if (typeof row.revision === 'number' && row.revision <= sessionRevisionRef.current) return;

    if (follower && row.state && typeof row.state === 'object' && Object.keys(row.state).length > 0) {
      if (typeof updateMeetingStateFromMessageRef.current !== 'function') {
        return;
      }
      applyingRemoteRef.current = true;
      try {
        updateMeetingStateFromMessageRef.current(row.state);
        const preferDriverTimers = Date.now() - lastDriverTimersAtRef.current < 2500;
        if (!preferDriverTimers) {
          if (typeof row.state.meetingTimer === 'number') {
            setMeetingTimer((prev) => mergeRemoteAscendingTimer(prev, row.state.meetingTimer));
          }
          if (typeof row.state.speakerTime === 'number') {
            setSpeakerTime((prev) => mergeRemoteDescendingTimer(prev, row.state.speakerTime));
          }
          if (typeof row.state.isTimerRunning === 'boolean') setIsTimerRunning(row.state.isTimerRunning);
        }
        if (typeof row.state.speakerTimeLimit === 'number') {
          setGlobalSpeakerTimeLimit(row.state.speakerTimeLimit);
        }
        markMeetingSessionStarted();
        sessionRevisionRef.current = row.revision || sessionRevisionRef.current;
      } finally {
        applyingRemoteRef.current = false;
      }
      return;
    }

    if (typeof row.revision === 'number') {
      sessionRevisionRef.current = row.revision;
    }
  }, [searchParams]);

  // Reset event tracking when the live session id changes
  useEffect(() => {
    processedEventIdsRef.current = new Set();
    eventsSeededRef.current = false;
  }, [officerSessionId]);

  // Auto-dismiss Ready/Hold banners after a few seconds (keep take/return until dismissed)
  useEffect(() => {
    if (!chairSignal) return undefined;
    if (chairSignal.type !== 'ready' && chairSignal.type !== 'hold') return undefined;
    if (chairSignalDismissRef.current) clearTimeout(chairSignalDismissRef.current);
    chairSignalDismissRef.current = setTimeout(() => {
      setChairSignal((prev) => (prev && prev.id === chairSignal.id ? null : prev));
    }, 5000);
    return () => {
      if (chairSignalDismissRef.current) clearTimeout(chairSignalDismissRef.current);
    };
  }, [chairSignal]);

  // Auto-dismiss driver→chair messages on Prompt
  useEffect(() => {
    if (!driverMessageToChair) return undefined;
    const id = setTimeout(() => {
      setDriverMessageToChair((prev) => (prev && prev.id === driverMessageToChair.id ? null : prev));
    }, 8000);
    return () => clearTimeout(id);
  }, [driverMessageToChair]);

  // Auto-dismiss chair→secretary messages on Driver
  useEffect(() => {
    if (!chairMessageToDriver) return undefined;
    const id = setTimeout(() => {
      setChairMessageToDriver((prev) => (prev && prev.id === chairMessageToDriver.id ? null : prev));
    }, 8000);
    return () => clearTimeout(id);
  }, [chairMessageToDriver]);

  // Realtime subscribe for multi-device officer sync
  useEffect(() => {
    if (!officerSessionId || !hasSupabaseConfig()) return undefined;
    if (isObserverMode() && !officerSessionId) return undefined;
    return subscribeMeetingSession(officerSessionId, (row) => {
      applyOfficerSessionRow(row);
    });
  }, [officerSessionId, applyOfficerSessionRow]);

  // Auto-switch to elections view when entering election mode
  useEffect(() => {
    if (isObserverMode() || isChairPromptMode()) return;
    if (inElectionMode && currentView !== 'elections') {
      handleViewChange('elections');
    }
  }, [inElectionMode]);

  const observerSyncTimeoutRef = useRef(null);

  const broadcastToDisplayWindows = (payload) => {
    const lists = [window.observerWindows, window.chairPromptWindows];
    lists.forEach((list) => {
      if (!list) return;
      for (let index = list.length - 1; index >= 0; index -= 1) {
        try {
          list[index].postMessage(payload, window.location.origin);
        } catch {
          list.splice(index, 1);
        }
      }
    });
  };

  // High-frequency: sync timers only (keeps Observer / Prompt smooth without sending large payloads)
  useEffect(() => {
    if (isObserverMode() || isChairPromptMode()) return;
    broadcastToDisplayWindows({
      type: 'MEETING_TIMERS_UPDATE',
      timers: {
        meetingTimer,
        speakerTime,
        isTimerRunning,
      },
    });
  }, [meetingTimer, speakerTime, isTimerRunning]);

  // Low-frequency: sync heavy meeting state (debounced) — always prefer latest snapshot
  useEffect(() => {
    if (isObserverMode() || isChairPromptMode()) return;
    if (observerSyncTimeoutRef.current) {
      clearTimeout(observerSyncTimeoutRef.current);
    }
    observerSyncTimeoutRef.current = setTimeout(() => {
      observerSyncTimeoutRef.current = null;
      syncMeetingStateRef.current?.();
    }, 150);
    return () => {
      if (observerSyncTimeoutRef.current) {
        clearTimeout(observerSyncTimeoutRef.current);
        observerSyncTimeoutRef.current = null;
      }
    };
  }, [
    meetingSetup, currentView,
    currentMotion, motions, motionStack,
    speakerQueue, currentSpeaker,
    meetingMinutes, currentAgendaIndex,
    currentElection, elections, candidateSpeechQueue, currentCandidate, inElectionMode,
    committees,
    electionScreen, electionNominees, electionBallotVotes, electionWriteInCandidates, electionInvalidBallots,
    voteJoinCode,
    pendingUCRequest,
    driverSeat,
    officerSessionId,
  ]);

  // Driver heartbeat: keep cloud session fresh for other signed-in devices
  useEffect(() => {
    if (!officerSessionId || !hasSupabaseConfig()) return undefined;
    if (isObserverMode() || isChairPromptMode()) return undefined;
    if (officerClientSeat !== 'driver' || driverSeat !== 'local') return undefined;
    const id = setInterval(() => {
      syncMeetingStateRef.current?.();
    }, 2000);
    return () => clearInterval(id);
  }, [officerSessionId, officerClientSeat, driverSeat]);

  // Cloud poll for all officer seats (Driver needs Ready/Hold; Prompt/Observer need state)
  useEffect(() => {
    if (!officerSessionId || !hasSupabaseConfig()) return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        const row = await fetchMeetingSessionById(officerSessionId);
        if (!cancelled && row) applyOfficerSessionRow(row);
      } catch (err) {
        console.warn('officer session poll failed', err?.message || err);
      }
    };
    void tick();
    const id = setInterval(tick, 1500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [officerSessionId, applyOfficerSessionRow]);

  // Handle view changes and sync with observer windows
  const handleViewChange = (newView) => {
    if (newView === 'meeting' && !skipAuth && !canMeeting) return;
    if (newView === 'committees' && !skipAuth && !canCommittees) return;
    if (newView === 'elections' && !skipAuth && !canElections) return;

    if (newView === 'chair-prompt') {
      document.body.classList.add('chair-prompt-window');
      document.body.classList.remove('observer-window');
      setCurrentView('chair-prompt');
      return;
    }

    if (newView === 'observer') {
      document.body.classList.add('observer-window');
      document.body.classList.remove('chair-prompt-window');
      setCurrentView('observer');
      return;
    }

    document.body.classList.remove('chair-prompt-window');
    document.body.classList.remove('observer-window');
    setCurrentView(newView);
    if (newView !== 'observer' && newView !== 'chair-prompt') {
      setChairNavView(newView);
    }

    // Broadcast view change to observer windows
    if (window.observerWindows) {
      window.observerWindows.forEach((observerWindow, index) => {
        try {
          observerWindow.postMessage({
            type: 'VIEW_CHANGE',
            view: newView,
            electionData: {
              currentElection,
              candidateSpeechQueue,
              currentCandidate,
              inElectionMode
            }
          }, window.location.origin);
        } catch (error) {
          window.observerWindows.splice(index, 1);
        }
      });
    }
  };

  const launchChairPromptWindow = () => {
    const orgId = orgIdFromRoute || activeOrganizationId;
    let promptUrl;
    if (officerSessionId && orgId) {
      promptUrl = officerSessionUrl({
        orgId,
        sessionId: officerSessionId,
        seat: 'prompt',
      });
    } else {
      const params = new URLSearchParams(window.location.search);
      params.set('chairPrompt', '1');
      params.set('seat', 'prompt');
      params.delete('observer');
      const qs = params.toString();
      promptUrl = `${window.location.origin}${window.location.pathname}?${qs}#chair-prompt`;
    }
    const promptWindow = window.open(
      promptUrl,
      'ChairPromptView',
      'width=900,height=1100,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
    );
    if (promptWindow) {
      promptWindow.focus();
      promptWindow.addEventListener('load', () => {
        promptWindow.postMessage({ type: 'SET_CHAIR_PROMPT_MODE' }, window.location.origin);
        setTimeout(() => {
          promptWindow.postMessage({ type: 'REQUEST_CURRENT_STATE' }, window.location.origin);
        }, 500);
      });
      if (window.chairPromptWindows) {
        window.chairPromptWindows.push(promptWindow);
      } else {
        window.chairPromptWindows = [promptWindow];
      }
      syncMeetingState();
    } else {
      alert('Allow pop-ups for this site to open the Chair Dashboard.');
    }
  };

  const sendChairSignalFromPrompt = (signal) => {
    const normalized = signal === 'hold' ? 'hold' : 'ready';
    if (officerSessionId && hasSupabaseConfig()) {
      void postMeetingSessionSignal(officerSessionId, normalized)
        .then((res) => {
          const latest = Array.isArray(res?.events) ? res.events[res.events.length - 1] : null;
          const id = latest?.id || res?.chairSignal?.id || `${normalized}:${Date.now()}`;
          if (id) processedEventIdsRef.current.add(id);
          setChairSignal({
            type: normalized,
            at: new Date().toLocaleTimeString(),
            id,
          });
        })
        .catch((err) => console.warn('post_meeting_session_signal failed', err));
      return;
    }
    const payload = { type: 'CHAIR_SIGNAL', signal: normalized };
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(payload, window.location.origin);
        setChairSignal({ type: normalized, at: new Date().toLocaleTimeString(), id: `local-${Date.now()}` });
        return;
      } catch {
        /* fall through */
      }
    }
    setChairSignal({ type: normalized, at: new Date().toLocaleTimeString(), id: `local-${Date.now()}` });
  };

  const sendDriverMessageToChair = () => {
    const body = String(driverMessageDraft || '').trim();
    if (!body) return;
    if (officerSessionId && hasSupabaseConfig()) {
      setDriverMessageBusy(true);
      void postDriverMessageToChair(officerSessionId, body)
        .then((res) => {
          const latest = Array.isArray(res?.events) ? res.events[res.events.length - 1] : null;
          if (latest?.id) processedEventIdsRef.current.add(latest.id);
          setDriverMessageDraft('');
        })
        .catch((err) => {
          console.warn('driver message failed', err);
          alert(err?.message || 'Could not send message to chair.');
        })
        .finally(() => setDriverMessageBusy(false));
      return;
    }
    const payload = { type: 'DRIVER_MESSAGE', body };
    broadcastToDisplayWindows(payload);
    setDriverMessageDraft('');
  };

  const sendChairMessageToDriver = (text) => {
    const body = String(text || '').trim();
    if (!body) return Promise.resolve(false);

    if (officerSessionId && hasSupabaseConfig()) {
      return postChairMessageToDriver(officerSessionId, body)
        .then((res) => {
          const latest = Array.isArray(res?.events) ? res.events[res.events.length - 1] : null;
          const id = latest?.id || `local-${Date.now()}`;
          processedEventIdsRef.current.add(id);
          if (window.opener && !window.opener.closed) {
            try {
              window.opener.postMessage(
                { type: 'CHAIR_MESSAGE', id, body },
                window.location.origin
              );
            } catch {
              /* ignore */
            }
          }
          return true;
        })
        .catch((err) => {
          console.warn('chair message failed', err);
          throw err;
        });
    }

    const id = `local-${Date.now()}`;
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage({ type: 'CHAIR_MESSAGE', id, body }, window.location.origin);
      } catch {
        /* ignore */
      }
    } else {
      setChairMessageToDriver({ id, body, at: new Date().toLocaleTimeString() });
    }
    return Promise.resolve(true);
  };

  const submitChairNoteFromDashboard = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return Promise.resolve(false);

    const insertLocal = (noteId) => {
      const entryId = String(noteId).startsWith('chair-note-')
        ? noteId
        : `chair-note-${noteId}`;
      setMeetingMinutes((prev) => {
        if (prev.some((e) => e.id === entryId)) return prev;
        return [
          ...prev,
          {
            id: entryId,
            text: trimmed,
            timestamp: new Date().toLocaleTimeString(),
            chronologicalOrder: Date.now(),
            type: 'chair_note',
            status: 'completed',
            result: null,
            activityOnly: false,
            lastUpdated: Date.now(),
          },
        ];
      });
      return entryId;
    };

    if (officerSessionId && hasSupabaseConfig()) {
      return postMeetingSessionEvent(officerSessionId, 'chair_note', trimmed)
        .then((res) => {
          const latest = Array.isArray(res?.events) ? res.events[res.events.length - 1] : null;
          const id = latest?.id || `local-${Date.now()}`;
          processedEventIdsRef.current.add(id);
          const entryId = insertLocal(id);
          if (window.opener && !window.opener.closed) {
            try {
              window.opener.postMessage(
                { type: 'CHAIR_NOTE', id: entryId, body: trimmed },
                window.location.origin
              );
            } catch {
              /* ignore */
            }
          }
          return true;
        })
        .catch((err) => {
          console.warn('chair_note event failed', err);
          const entryId = insertLocal(`local-${Date.now()}`);
          if (window.opener && !window.opener.closed) {
            try {
              window.opener.postMessage(
                { type: 'CHAIR_NOTE', id: entryId, body: trimmed },
                window.location.origin
              );
            } catch {
              /* ignore */
            }
          }
          return true;
        });
    }

    const entryId = insertLocal(`local-${Date.now()}`);
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          { type: 'CHAIR_NOTE', id: entryId, body: trimmed },
          window.location.origin
        );
      } catch {
        /* ignore */
      }
    }
    return Promise.resolve(true);
  };

  const handleChairTakeControl = () => {
    const finishLocal = () => {
      setDriverSeat('remote');
      setChairSignal({
        type: 'take_control',
        at: new Date().toLocaleTimeString(),
      });
      document.body.classList.remove('chair-prompt-window');
      setCurrentView('meeting');
      setChairNavView('meeting');
    };

    if (officerSessionId && hasSupabaseConfig()) {
      void postMeetingSessionSignal(officerSessionId, 'take_control')
        .then((res) => {
          if (res?.revision) sessionRevisionRef.current = res.revision;
          finishLocal();
        })
        .catch((err) => {
          console.warn('take_control signal failed', err);
          finishLocal();
        });
      return;
    }

    const isPopup = Boolean(window.opener && !window.opener.closed);
    if (isPopup) {
      try {
        window.opener.postMessage({ type: 'CHAIR_TAKE_CONTROL' }, window.location.origin);
        window.opener.focus();
      } catch {
        /* ignore */
      }
      setDriverSeat('remote');
      return;
    }
    finishLocal();
  };

  const handleChairReturnControl = () => {
    const finishLocal = () => {
      setDriverSeat('local');
      setChairSignal({
        type: 'return_control',
        at: new Date().toLocaleTimeString(),
      });
      if (officerClientSeat === 'prompt' || isChairPromptMode()) {
        document.body.classList.add('chair-prompt-window');
        setCurrentView('chair-prompt');
      }
    };

    if (officerSessionId && hasSupabaseConfig()) {
      void postMeetingSessionSignal(officerSessionId, 'return_control')
        .then((res) => {
          if (res?.revision) sessionRevisionRef.current = res.revision;
          finishLocal();
        })
        .catch((err) => {
          console.warn('return_control signal failed', err);
          finishLocal();
        });
      return;
    }

    const isPopup = Boolean(window.opener && !window.opener.closed);
    if (isPopup) {
      try {
        window.opener.postMessage({ type: 'CHAIR_RETURN_CONTROL' }, window.location.origin);
      } catch {
        /* ignore */
      }
      setDriverSeat('local');
      return;
    }
    finishLocal();
  };

  const handleReturnToChairPrompt = () => {
    setDriverSeat('local');
    setChairSignal({
      type: 'return_control',
      at: new Date().toLocaleTimeString(),
    });
    if (officerSessionId && hasSupabaseConfig()) {
      void postMeetingSessionSignal(officerSessionId, 'return_control').catch(() => {});
    }
    document.body.classList.add('chair-prompt-window');
    setCurrentView('chair-prompt');
  };

  // Sync all meeting state to observer + chair-prompt windows (+ cloud officer session)
  const syncMeetingState = () => {
    const onPromptSurface = isChairPromptMode() && currentView === 'chair-prompt';
    if (isObserverMode() || onPromptSurface) return;

    const MAX_MINUTES_TO_SYNC = 200;
    const minutesToSync =
      meetingMinutes.length > MAX_MINUTES_TO_SYNC
        ? meetingMinutes.slice(-MAX_MINUTES_TO_SYNC)
        : meetingMinutes;

    const meetingData = {
      meetingSetup,
      currentView: chairNavView || currentView,
      currentMotion,
      motions,
      motionStack,
      speakerQueue,
      currentSpeaker,
      meetingMinutes: minutesToSync,
      currentAgendaIndex,
      currentElection,
      elections,
      candidateSpeechQueue,
      currentCandidate,
      inElectionMode,
      electionScreen,
      electionNominees,
      electionBallotVotes,
      electionWriteInCandidates,
      electionInvalidBallots,
      committees,
      voteJoinCode,
      pendingUCRequest,
      driverSeat,
      meetingTimer,
      speakerTime,
      speakerTimeLimit: globalSpeakerTimeLimit,
      isTimerRunning,
      meetingStartTime,
      meetingEndTime,
    };

    broadcastToDisplayWindows({
      type: 'MEETING_STATE_UPDATE',
      meetingData,
    });

    const canPushCloud =
      Boolean(officerSessionId) &&
      hasSupabaseConfig() &&
      !applyingRemoteRef.current &&
      ((officerClientSeat === 'driver' && driverSeat === 'local') ||
        (officerClientSeat === 'prompt' && driverSeat === 'remote' && currentView === 'meeting'));

    if (canPushCloud) {
      let payload = meetingData;
      try {
        payload = JSON.parse(JSON.stringify(meetingData));
      } catch (err) {
        console.warn('meeting state not JSON-serializable', err);
      }
      void pushMeetingSessionState(officerSessionId, payload, {
        // Prefer LWW: omit expected revision so Prompt poll never starves on conflicts
        expectedRevision: null,
        driverSeat: mapUiSeatToCloud(driverSeat),
      })
        .then((res) => {
          if (res?.revision) sessionRevisionRef.current = res.revision;
        })
        .catch((err) => {
          console.warn('push_meeting_session_state failed', err);
        });
    }
  };
  syncMeetingStateRef.current = syncMeetingState;

  const refreshRemoteVoteTally = useCallback(async (sessionId) => {
    if (!sessionId || !hasSupabaseConfig()) return;
    try {
      const tally = await getVoteSessionTally(sessionId);
      setRemoteVoteTally(tally);
    } catch (err) {
      console.warn('get_vote_session_tally failed', err);
    }
  }, []);

  useEffect(() => {
    if (!remoteVoteSession?.id || remoteVoteSession.status !== 'open') return undefined;
    void refreshRemoteVoteTally(remoteVoteSession.id);
    const id = setInterval(() => void refreshRemoteVoteTally(remoteVoteSession.id), 1500);
    return () => clearInterval(id);
  }, [remoteVoteSession?.id, remoteVoteSession?.status, refreshRemoteVoteTally]);

  const handleCloseRemoteVote = useCallback(async () => {
    if (!remoteVoteSession?.id) {
      setRemoteVoteSession(null);
      setRemoteVoteTally(null);
      return;
    }
    setRemoteVoteBusy(true);
    setRemoteVoteError(null);
    try {
      await closeVoteSession(remoteVoteSession.id);
      setRemoteVoteSession((prev) => (prev ? { ...prev, status: 'closed' } : null));
      await refreshRemoteVoteTally(remoteVoteSession.id);
    } catch (err) {
      setRemoteVoteError(err?.message || 'Could not close remote vote');
    } finally {
      setRemoteVoteBusy(false);
    }
  }, [remoteVoteSession?.id, refreshRemoteVoteTally]);

  const handleOpenRemoteMotionVote = useCallback(async () => {
    if (!voteRoomId || !currentMotion || !hasSupabaseConfig()) {
      setRemoteVoteError('Voting room is not ready yet.');
      return;
    }
    setRemoteVoteBusy(true);
    setRemoteVoteError(null);
    try {
      const question =
        currentMotion.text ||
        MOTION_TYPES[currentMotion.type]?.name ||
        'Motion vote';
      const session = await openVoteSession({
        roomId: voteRoomId,
        kind: 'motion',
        questionText: question,
        options: ['aye', 'no', 'abstain'],
        clientRef: String(currentMotion.id),
      });
      setRemoteVoteSession(session);
      setRemoteVoteTally({ counts: {}, total: 0, status: 'open', sessionId: session.id });
      await refreshRemoteVoteTally(session.id);
    } catch (err) {
      setRemoteVoteError(err?.message || 'Could not open remote vote');
    } finally {
      setRemoteVoteBusy(false);
    }
  }, [voteRoomId, currentMotion, refreshRemoteVoteTally]);

  const handleOpenRemoteElectionBallot = useCallback(async () => {
    if (!voteRoomId || !hasSupabaseConfig()) {
      setRemoteVoteError('Voting room is not ready yet.');
      return;
    }
    const accepted = (electionNominees || []).filter((n) => n.status === 'accepted');
    if (!accepted.length) {
      setRemoteVoteError('Add accepted nominees before opening a remote ballot.');
      return;
    }
    setRemoteVoteBusy(true);
    setRemoteVoteError(null);
    try {
      const office =
        currentElection?.office ||
        electionNominees[0]?.office ||
        'Office';
      const options = accepted.map((n) => ({ id: String(n.id), label: n.name }));
      if (currentElection?.allowWriteIns) {
        options.push({ id: '__write_in__', label: 'Write in a candidate' });
      }
      const session = await openVoteSession({
        roomId: voteRoomId,
        kind: 'election',
        questionText: `Election: ${office}`,
        options,
        clientRef: String(currentElection?.id || office),
      });
      setRemoteVoteSession(session);
      setRemoteVoteTally({ counts: {}, total: 0, status: 'open', sessionId: session.id });
      await refreshRemoteVoteTally(session.id);
    } catch (err) {
      setRemoteVoteError(err?.message || 'Could not open remote ballot');
    } finally {
      setRemoteVoteBusy(false);
    }
  }, [
    voteRoomId,
    electionNominees,
    currentElection,
    refreshRemoteVoteTally,
  ]);

  const handleUseRemoteElectionTally = useCallback(() => {
    const accepted = (electionNominees || []).filter((n) => n.status === 'accepted');
    const next = electionVotesFromCounts(remoteVoteTally?.counts || {}, accepted);
    setElectionBallotVotes((prev) => ({ ...prev, ...next }));
  }, [electionNominees, remoteVoteTally]);

  // Unanimous Consent handlers
  const handleUnanimousConsent = (requestText, motion = null) => {
    // Set the pending UC request instead of using a popup
    const timestamp = new Date().toLocaleTimeString();
    setPendingUCRequest({
      text: requestText,
      motion: motion,
      id: Date.now(),
      timestamp: timestamp
    });

    // Don't add to minutes yet - only add when UC is resolved (granted or objected)
  };

  const markStatedMinuteAmended = useCallback((mainMotionId) => {
    setMeetingMinutes((prev) =>
      prev.map((entry) =>
        entry.id === `motion_${mainMotionId}`
          ? {
              ...entry,
              status: 'amended',
              isAmended: true,
              lastUpdated: Date.now(),
            }
          : entry
      )
    );
  }, []);

  const requestAmendTextConfirm = useCallback(
    async ({ mainMotion, amendmentMotion, result = 'passed' }) => {
      if (!mainMotion || !amendmentMotion) return;

      setAmendTextConfirm({
        mainMotion,
        amendmentMotion,
        result,
        proposedText: mainMotion.text,
        summary: 'Working out the main motion as amended…',
        source: 'pending',
        loading: true,
      });
      setAmendConfirmDraft(mainMotion.text || '');

      // Restore main on the floor immediately (old text) so the meeting isn't stuck on the amendment
      setCurrentMotion({
        ...mainMotion,
        status: 'open_debate',
        awaitingAmendTextConfirm: true,
      });
      // Only drop the amendment that just resolved — a different, still-pending amendment
      // further down the stack (e.g. the main motion's primary amendment, while this was a
      // secondary amendment to it) must stay.
      setMotionStack((prev) => prev.filter((m) => m.id !== amendmentMotion.id));

      let accessToken = null;
      try {
        const supabase = getSupabaseClient();
        if (supabase) {
          const { data } = await supabase.auth.getSession();
          accessToken = data.session?.access_token ?? null;
        }
      } catch {
        /* ignore */
      }

      const proposal = await proposeAmendedMainText({
        currentMainText: mainMotion.text,
        originalMainText: mainMotion.originalText || mainMotion.text,
        amendmentText: amendmentMotion.text,
        member: amendmentMotion.member,
        accessToken,
        organizationId: orgIdFromRoute || activeOrganizationId,
      });

      setAmendTextConfirm((prev) =>
        prev
          ? {
              ...prev,
              proposedText: proposal.amendedText,
              summary: proposal.summary,
              source: proposal.source,
              loading: false,
            }
          : null
      );
      setAmendConfirmDraft(proposal.amendedText || mainMotion.text || '');
    },
    []
  );

  const confirmAmendText = useCallback(() => {
    if (!amendTextConfirm) return;
    const { mainMotion, amendmentMotion, result } = amendTextConfirm;
    const updatedMain = applyPassedAmendmentToMain(
      mainMotion,
      amendmentMotion,
      amendConfirmDraft,
      result
    );
    delete updatedMain.awaitingAmendTextConfirm;

    setMotions((prev) =>
      prev.map((m) => {
        if (m.id === updatedMain.id) return updatedMain;
        if (m.id === amendmentMotion.id) {
          return {
            ...m,
            status: 'completed',
            result: result === 'passed_by_uc' ? 'passed_by_uc' : 'passed',
          };
        }
        return m;
      })
    );
    setCurrentMotion(updatedMain);
    markStatedMinuteAmended(updatedMain.id);
    setAmendTextConfirm(null);
    setAmendConfirmDraft('');
  }, [amendTextConfirm, amendConfirmDraft, markStatedMinuteAmended]);

  const handleUCGrant = () => {
    if (!pendingUCRequest) return;

    // No objection - UC granted
    if (pendingUCRequest.motion) {
      const pending = pendingUCRequest.motion;

      // Adopting an amendment by UC only disposes the amendment; main remains pending as amended.
      if (pending.type === 'AMEND') {
        const amendmentMotion = motions.find((m) => m.id === pending.id) || pending;
        // LIFO: the real parent is whatever this amendment was pending on top of — the
        // main motion for a primary amendment, or a still-pending primary amendment for
        // a secondary one. Not necessarily type 'MAIN'.
        const mainMotionIndex = motionStack.length - 1;

        if (motionStack.length > 0) {
          const mainSnapshot = motionStack[mainMotionIndex];
          const updatedAmendment = {
            ...amendmentMotion,
            status: 'completed',
            result: 'passed_by_uc',
            ucTime: new Date().toLocaleTimeString(),
          };

          setMotions((prev) =>
            prev.map((m) => (m.id === updatedAmendment.id ? updatedAmendment : m))
          );

          addMinuteEntry({
            text: formatAmendmentDispositionText(amendmentMotion, 'Amendment adopted by unanimous consent.'),
            type: 'amendment_result',
            status: 'completed',
            result: 'passed_by_uc',
            motionData: updatedAmendment,
            parentMotionId: amendmentMotion.parentMotionId,
          });

          setPendingUCRequest(null);
          void requestAmendTextConfirm({
            mainMotion: mainSnapshot,
            amendmentMotion: updatedAmendment,
            result: 'passed_by_uc',
          });
          return;
        }

        const mainFromTarget =
          amendmentMotion.targetMotionId != null
            ? motions.find((m) => m.id === amendmentMotion.targetMotionId)
            : null;
        if (mainFromTarget) {
          const updatedAmendment = {
            ...amendmentMotion,
            status: 'completed',
            result: 'passed_by_uc',
            ucTime: new Date().toLocaleTimeString(),
          };

          setMotions((prev) =>
            prev.map((m) => (m.id === updatedAmendment.id ? updatedAmendment : m))
          );

          addMinuteEntry({
            text: formatAmendmentDispositionText(amendmentMotion, 'Amendment adopted by unanimous consent.'),
            type: 'amendment_result',
            status: 'completed',
            result: 'passed_by_uc',
            motionData: updatedAmendment,
            parentMotionId: amendmentMotion.parentMotionId,
          });

          setPendingUCRequest(null);
          void requestAmendTextConfirm({
            mainMotion: mainFromTarget,
            amendmentMotion: updatedAmendment,
            result: 'passed_by_uc',
          });
          return;
        }
      }

      // If this was for a specific motion (not an amendment, or amendment fallback), dispose it by UC
      const updatedMotion = {
        ...pending,
        status: 'completed',
        result: 'passed_by_uc',
        ucTime: new Date().toLocaleTimeString()
      };

      setMotions(prev => prev.map(m => m.id === pending.id ? updatedMotion : m));
      setCurrentMotion(null);
      setMotionStack([]);

      // Add to minutes
      addMinuteEntry({
        text: 'Motion passed by unanimous consent: "' + pending.text + '"',
        type: 'unanimous_consent',
        result: 'passed_by_uc',
        motionData: updatedMotion
      });

      if (isAdjournMotion(pending)) {
        const endIso = new Date().toISOString();
        setMeetingEndTime(endIso);
        setIsTimerRunning(false);
        setMeetingSetup((prev) => ({ ...prev, meetingEndTime: endIso }));
        const minutesAfter = [
          ...meetingMinutes,
          {
            id: Date.now(),
            text: 'Motion passed by unanimous consent: "' + pending.text + '"',
            type: 'unanimous_consent',
            result: 'passed_by_uc',
            motionData: updatedMotion,
            timestamp: new Date().toLocaleTimeString(),
            chronologicalOrder: Date.now(),
            status: 'completed',
          },
        ];
        window.setTimeout(() => {
          promptFinalizeAfterAdjourn(minutesAfter, endIso);
        }, 0);
      }
    } else {
      // General UC request
      addMinuteEntry({
        text: 'Unanimous consent granted: ' + pendingUCRequest.text,
        type: 'unanimous_consent'
      });

      if (textLooksLikeAdjourn(pendingUCRequest.text)) {
        const endIso = new Date().toISOString();
        setMeetingEndTime(endIso);
        setIsTimerRunning(false);
        setMeetingSetup((prev) => ({ ...prev, meetingEndTime: endIso }));
        const minutesAfter = [
          ...meetingMinutes,
          {
            id: Date.now(),
            text: 'Unanimous consent granted: ' + pendingUCRequest.text,
            type: 'unanimous_consent',
            timestamp: new Date().toLocaleTimeString(),
            chronologicalOrder: Date.now(),
            status: 'completed',
          },
        ];
        window.setTimeout(() => {
          promptFinalizeAfterAdjourn(minutesAfter, endIso);
        }, 0);
      }
    }

    // Clear the pending request
    setPendingUCRequest(null);
  };

  const handleUCObjection = () => {
    if (!pendingUCRequest) return;

    // Add objection to minutes
    addMinuteEntry({
      text: 'Objection raised to unanimous consent request: "' + pendingUCRequest.text + '"',
      type: 'objection',
      activityOnly: true,
      status: 'completed'
    });

    // Clear the pending request
    setPendingUCRequest(null);
  };

  // UC presets: prefer live org bylaws (admin settings) so a live ?session= meeting still picks up edits.
  const unanimousConsentRequests = (() => {
    const fromBylaws =
      activeMembership?.organization_id &&
      (orgIdFromRoute || activeOrganizationId) === activeMembership.organization_id
        ? organizationSettingsFromBylaws(activeMembership.bylaws_config)
            .defaultUnanimousConsentRequests
        : null;
    if (fromBylaws?.length) return fromBylaws;
    const fromMeeting = meetingSetup.organizationSettings?.defaultUnanimousConsentRequests;
    if (fromMeeting?.length) return fromMeeting;
    return createStandardUnanimousConsentRequests();
  })();

  // Motion Types - now imported from comprehensive system
  const motionTypes = MOTION_TYPES;

  // Timer effects — driver/console owns the clocks; Observer / Prompt only display synced values
  useEffect(() => {
    let interval;
    if (isTimerRunning && !isObserverMode() && !isChairPromptMode()) {
      interval = setInterval(() => {
        setMeetingTimer((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, searchParams]);

  useEffect(() => {
    let interval;
    if (isObserverMode() || isChairPromptMode()) {
      return undefined;
    }
    // Timer runs for regular speakers (when currentSpeaker is set but no election candidate)
    // OR election candidates when timer is running
    if (
      speakerTime > 0 &&
      ((currentSpeaker && !currentCandidate) || (currentCandidate && isTimerRunning))
    ) {
      interval = setInterval(() => {
        setSpeakerTime((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentSpeaker, speakerTime, currentCandidate, isTimerRunning, searchParams]);

  // Helper functions (formatTime moved to utils)

  // getQuorumStatus moved to utils

  const getCurrentAgendaItem = () => {
    if (currentAgendaIndex >= meetingSetup.orderOfBusiness.length) return null;
    return meetingSetup.orderOfBusiness[currentAgendaIndex];
  };


  const getAgendaDisplayText = () => {
    const currentItem = getCurrentAgendaItem();
    if (!currentItem) return 'Complete';
    return currentItem.title;
  };

  const advanceAgenda = () => {
    const currentItem = getCurrentAgendaItem();
    if (!currentItem) return;
    if (isConsentCalendarAgendaRow(currentItem) && !currentItem.completed) {
      return;
    }

    if (!currentItem.completed) {
      const titleStr = String(currentItem.title || '').trim();
      const isCallToOrderItem = /call\s+to\s+order/i.test(titleStr);
      if (isCallToOrderItem && !meetingStartTime) {
        handleCallToOrder();
        setIsTimerRunning(true);
      }

      // Complete agenda item and add to minutes
      const completionText = `${currentItem.title} completed`;
      handleAddAgendaEntry({
        id: Date.now(),
        text: completionText,
        timestamp: new Date().toLocaleTimeString(),
        type: 'agenda_completion'
      });
      
      const updatedItems = meetingSetup.orderOfBusiness.map(item =>
        item.id === currentItem.id ? {...item, completed: true, completedAt: new Date().toLocaleTimeString()} : item
      );
      setMeetingSetup({ ...meetingSetup, orderOfBusiness: updatedItems });
      setCurrentAgendaIndex(Math.min(meetingSetup.orderOfBusiness.length - 1, currentAgendaIndex + 1));
    } else {
      setCurrentAgendaIndex(Math.min(meetingSetup.orderOfBusiness.length - 1, currentAgendaIndex + 1));
    }
  };

  // Chair Script Generation (template baseline; Claude may refine asynchronously)
  const baselineChairScript = React.useMemo(() => {
    if (inElectionMode) {
      return getElectionChairScript({
        electionScreen,
        officeLabel: getElectionOfficeLabel(currentElection),
        electionNominees,
        currentCandidate,
        speechTimeLimit:
          currentElection?.speechTimeLimit || candidateSpeechTimeLimit || 3,
        secretBallot: currentElection?.secretBallot !== false,
        showVoteBreakdown: Boolean(currentElection?.showVoteBreakdown),
        electionWriteInCandidates,
        electionBallotVotes,
        seatsToFill: currentElection?.seatsToFill || 1,
      });
    }

    const currentAgendaItem = getCurrentAgendaItem();
    const quorumStatus = getQuorumStatus(meetingSetup);

    return generateAIChairScript({
      currentMotion,
      motionStack,
      motions,
      currentSpeaker,
      recognizedMember: null, // Could be enhanced to track recognized members
      recentActions: [], // Could track recent meeting actions for context
      speakerQueue,
      meetingSetup,
      pendingUCRequest,
      currentAgendaItem,
      quorumStatus,
      tabledMotions,
      postponedMotions,
      isQuorumPresent: quorumStatus.present,
      meetingStartTime
    });
  }, [
    inElectionMode,
    electionScreen,
    currentElection,
    electionNominees,
    currentCandidate,
    candidateSpeechTimeLimit,
    electionWriteInCandidates,
    electionBallotVotes,
    currentMotion,
    motionStack,
    motions,
    currentSpeaker,
    speakerQueue,
    meetingSetup.presentMembers,
    meetingSetup.totalMembers,
    meetingSetup.quorumRequirement,
    meetingSetup.quorumType,
    pendingUCRequest,
    currentAgendaIndex,
    tabledMotions,
    postponedMotions,
    meetingStartTime,
  ]);

  const [enhancedChairScript, setEnhancedChairScript] = useState(null);
  const [chairScriptSource, setChairScriptSource] = useState('template');
  const [chairScriptRefining, setChairScriptRefining] = useState(false);
  const chairScriptRequestRef = useRef(0);
  const chairScriptCacheRef = useRef(new Map()); // fingerprint -> { script, source }
  const baselineChairScriptRef = useRef(baselineChairScript);
  baselineChairScriptRef.current = baselineChairScript;

  // Stable procedural key only — do not include baseline wording (that can churn on sync).
  const chairScriptFingerprint = React.useMemo(() => {
    if (inElectionMode) return `election:${electionScreen}:${currentElection?.id || ''}`;
    return [
      currentMotion?.id || 'none',
      currentMotion?.status || '',
      currentMotion?.type || '',
      String(currentMotion?.text || '').slice(0, 200),
      currentMotion?.chairPutToAssembly ? 'assembly' : '',
      pendingUCRequest?.text || '',
      currentAgendaIndex,
      currentSpeaker || '',
    ].join('|');
  }, [
    inElectionMode,
    electionScreen,
    currentElection?.id,
    currentMotion?.id,
    currentMotion?.status,
    currentMotion?.type,
    currentMotion?.text,
    currentMotion?.chairPutToAssembly,
    pendingUCRequest?.text,
    currentAgendaIndex,
    currentSpeaker,
  ]);

  useEffect(() => {
    if (inElectionMode) {
      setEnhancedChairScript(null);
      setChairScriptSource('template');
      setChairScriptRefining(false);
      return undefined;
    }

    const cached = chairScriptCacheRef.current.get(chairScriptFingerprint);
    if (cached?.script) {
      setEnhancedChairScript(cached.script);
      setChairScriptSource(cached.source || 'claude');
      setChairScriptRefining(false);
      return undefined;
    }

    const baseline = String(baselineChairScriptRef.current || '').trim();
    if (!baseline) {
      setChairScriptRefining(false);
      return undefined;
    }

    // Show the fresh baseline immediately (never leave the previous fingerprint's
    // stale refined text on screen while the new refine round-trips) — then upgrade
    // to AI-refined phrasing once it arrives.
    setEnhancedChairScript(baseline);
    setChairScriptSource('template');
    setChairScriptRefining(true);

    const orgId = orgIdFromRoute || activeOrganizationId || null;
    const requestId = ++chairScriptRequestRef.current;
    const controller = new AbortController();
    const fpAtRequest = chairScriptFingerprint;

    const timer = window.setTimeout(() => {
      const agendaItem = getCurrentAgendaItem();
      const quorumStatus = getQuorumStatus(meetingSetup);
      const baselineNow = String(baselineChairScriptRef.current || baseline).trim() || baseline;
      void refineChairScript({
        organizationId: orgId,
        baselineScript: baselineNow,
        meetingContext: {
          organizationName: meetingSetup.organizationName || '',
          agendaTitle: agendaItem?.title || getAgendaDisplayText?.() || '',
          currentMotion: currentMotion
            ? {
                type: currentMotion.type,
                status: currentMotion.status,
                text: currentMotion.text,
                member: currentMotion.member,
              }
            : null,
          motionStackSummary: (motionStack || []).map(
            (m) => `${m.type || '?'}:${m.status || 'pending'}`
          ),
          pendingUC: pendingUCRequest || null,
          quorumPresent: Boolean(quorumStatus?.present),
          currentSpeaker: currentSpeaker || null,
        },
        signal: controller.signal,
      })
        .then((result) => {
          if (requestId !== chairScriptRequestRef.current) return;
          const script = String(result.script || baselineNow).trim() || baselineNow;
          const source = result.source || 'template';
          chairScriptCacheRef.current.set(fpAtRequest, { script, source });
          if (chairScriptCacheRef.current.size > 40) {
            const first = chairScriptCacheRef.current.keys().next().value;
            chairScriptCacheRef.current.delete(first);
          }
          setEnhancedChairScript(script);
          setChairScriptSource(source);
        })
        .catch((err) => {
          if (err?.name === 'AbortError') return;
          if (requestId !== chairScriptRequestRef.current) return;
        })
        .finally(() => {
          if (requestId !== chairScriptRequestRef.current) return;
          setChairScriptRefining(false);
        });
    }, 700);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [chairScriptFingerprint, inElectionMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const chairScript =
    !inElectionMode && enhancedChairScript ? enhancedChairScript : baselineChairScript;

  // Integration callbacks
  const handleReferMotion = (commitMotion, committee, instructions) => {
    // Find the main motion being referred (from motion stack or current main motion)
    let mainMotion = null;
    
    if (motionStack.length > 0) {
      // Find the main motion in the stack
      mainMotion = motionStack.find(m => m.type === 'MAIN');
    }
    
    if (!mainMotion) {
      console.error('No main motion found to refer to committee');
      return;
    }

    const referralDraft = {
      id: Date.now(),
      type: 'motion',
      motionId: mainMotion.id,
      title: mainMotion.text,
      description: mainMotion.text,
      referredBy: commitMotion.member,
      dateReferred: new Date().toLocaleDateString(),
      instructions: instructions || 'No specific instructions',
      status: 'pending',
      recommendations: '',
    };

    const orgId = orgIdFromRoute || activeOrganizationId;
    const persistCommittees = hasSupabaseConfig() ? setCommitteesPersisted : setCommittees;
    persistCommittees((prev) =>
      prev.map((c) =>
        c.id === committee.id
          ? { ...c, referredItems: [...(c.referredItems || []), referralDraft] }
          : c
      )
    );

    if (hasSupabaseConfig() && orgId) {
      void createCommitteeReferral({
        organizationId: orgId,
        committeeId: committee.id,
        title: mainMotion.text,
        description: mainMotion.text,
        instructions: instructions || 'No specific instructions',
        referredBy: commitMotion.member,
        sourceMotionId: mainMotion.id,
      })
        .then((remote) => {
          if (!remote?.id) return;
          persistCommittees((prev) =>
            prev.map((c) => {
              if (c.id !== committee.id) return c;
              return {
                ...c,
                referredItems: (c.referredItems || []).map((item) =>
                  item.id === referralDraft.id ? { ...remote } : item
                ),
              };
            })
          );
        })
        .catch((err) => {
          console.warn('[App] createCommitteeReferral failed', err?.message || err);
        });
    }

    // Update the main motion as referred
    const updatedMainMotion = {
      ...mainMotion,
      status: 'referred_to_committee',
      referredTo: committee.name,
      referredDate: new Date().toLocaleDateString()
    };
    
    // Update the commit motion as passed
    const updatedCommitMotion = {
      ...commitMotion,
      status: 'voted_passed',
      result: 'passed',
      voteTime: new Date().toLocaleTimeString()
    };
    
    setMotions(prev => prev.map(m => {
      if (m.id === mainMotion.id) return updatedMainMotion;
      if (m.id === commitMotion.id) return updatedCommitMotion;
      return m;
    }));

    const commitMinuteText = formatCompletedMotionMinuteLine(updatedCommitMotion, motionTypes, 'passed');
    const mainReferralText = `Motion "${mainMotion.text}" referred to ${committee.name}${
      instructions && String(instructions).trim() ? ` (${String(instructions).trim()})` : ''
    }.`;

    setMeetingMinutes(prev => {
      const mapped = prev.map(entry => {
        if (entry.id === `motion_${commitMotion.id}`) {
          return {
            ...entry,
            text: commitMinuteText,
            type: 'motion_completed',
            status: 'completed',
            result: 'passed',
            motionData: updatedCommitMotion,
            lastUpdated: Date.now()
          };
        }
        if (entry.id === `motion_${mainMotion.id}`) {
          return {
            ...entry,
            text: mainReferralText,
            type: 'motion_completed',
            status: 'completed',
            result: 'referred_to_committee',
            motionData: updatedMainMotion,
            lastUpdated: Date.now()
          };
        }
        return entry;
      });
      return mapped.filter(
        e => !(e.type === 'referral' && e.motionData?.id === mainMotion.id)
      );
    });

    // Clear current motion and motion stack since main motion is now referred
    setCurrentMotion(null);
    setMotionStack([]);

    // Close referral dialog
    setShowReferralDialog(false);
    setReferralMotion(null);
  };

  const handleDischargeItem = (referralItem, committee) => {
    // Remove from committee
    setCommittees(prev => prev.map(c =>
      c.id === committee.id
        ? { ...c, referredItems: c.referredItems.filter(item => item.id !== referralItem.id) }
        : c
    ));

    // Find and restore the original motion
    const originalMotion = motions.find(m => m.id === referralItem.motionId);
    if (originalMotion) {
      const updatedMotion = {
        ...originalMotion,
        status: 'stated',
        referredTo: null,
        referredDate: null,
        dischargedDate: new Date().toLocaleDateString()
      };
      setMotions(prev => prev.map(m => m.id === originalMotion.id ? updatedMotion : m));
      setCurrentMotion(updatedMotion);
    }

    handleAddAgendaEntry({
      id: Date.now(),
      text: `"${referralItem.title}" discharged from ${committee.name} and returned to main body`,
      timestamp: new Date().toLocaleTimeString(),
      type: 'discharge'
    });
  };

  // Enhanced unified minute entry system
  const addMinuteEntry = (entry) => {
    const enhancedEntry = {
      id: entry.id || Date.now(),
      text: entry.text,
      timestamp: entry.timestamp || new Date().toLocaleTimeString(),
      chronologicalOrder:
        entry.chronologicalOrder != null ? entry.chronologicalOrder : Date.now(), // For proper chronological sorting
      type: entry.type || 'general', // agenda_completion, motion, election, unanimous_consent, etc.
      parentMotionId: entry.parentMotionId || null, // For nesting subsidiary motions
      motionData: entry.motionData || null, // Full motion object for motion-related entries
      electionData: entry.electionData || null, // Election details for election entries
      metadata: entry.metadata || {}, // Additional contextual information
      status: entry.status || 'completed', // pending, in_progress, completed
      result: entry.result || null, // Motion outcome: passed, failed, referred, etc.
      activityOnly: entry.activityOnly === true,
      lastUpdated: Date.now() // For live feed updates
    };

    setMeetingMinutes(prev => [...prev, enhancedEntry]);
  };

  // Load org roster when roll-call setting is on (or for manual open).
  useEffect(() => {
    const orgId = orgIdFromRoute || activeOrganizationId;
    const enabled = isRollCallEnabledFor(meetingSetup);
    if (!enabled || !orgId || !hasSupabaseConfig()) {
      setOrgRoster([]);
      return undefined;
    }
    let cancelled = false;
    void listOrgRoster(orgId, { activeOnly: true })
      .then((rows) => {
        if (!cancelled) setOrgRoster(rows || []);
      })
      .catch((err) => {
        console.warn('[roll call] roster load failed', err?.message || err);
        if (!cancelled) setOrgRoster([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    orgIdFromRoute,
    activeOrganizationId,
    meetingSetup.organizationSettings?.enableRollCallRoster,
    meetingSetup.rollCallOverride,
  ]);

  // Auto-open roll call modal when agenda lands on Roll Call (once per item).
  useEffect(() => {
    const enabled = isRollCallEnabledFor(meetingSetup);
    if (!enabled || meetingSetup.rollCallCompleted) return;
    const item = meetingSetup.orderOfBusiness?.[currentAgendaIndex];
    if (!item || item.completed) return;
    if (!/roll\s*call/i.test(String(item.title || ''))) return;
    if (!orgRoster.length) return;
    const key = `${item.id || currentAgendaIndex}`;
    if (rollCallPromptedRef.current === key) return;
    rollCallPromptedRef.current = key;
    setShowRollCallModal(true);
  }, [
    currentAgendaIndex,
    meetingSetup.organizationSettings?.enableRollCallRoster,
    meetingSetup.rollCallOverride,
    meetingSetup.rollCallCompleted,
    meetingSetup.orderOfBusiness,
    orgRoster.length,
  ]);

  const handleRollCallConfirm = (result) => {
    setMeetingSetup((prev) => ({
      ...prev,
      presentMembers: result.presentCount,
      totalMembers: Math.max(prev.totalMembers || 0, result.totalCount),
      rollCallAttendance: result.attendance,
      rollCallCompleted: true,
    }));
    addMinuteEntry({
      text: `Roll call: ${result.presentCount} present, ${result.absentCount} absent.`,
      type: 'roll_call',
      status: 'completed',
      metadata: { attendance: result.attendance },
    });
    setShowRollCallModal(false);
  };


  const pullConsentLineToFloor = (lineId) => {
    const item = meetingSetup.consentAgendaItems?.find((i) => i.id === lineId);
    if (!item || item.pulledToFloor) return;
    const batchId = meetingSetup.consentCalendarBatchId || `cb-${Date.now()}`;
    const lineText = item.text || '';
    addMinuteEntry({
      id: `consent-pull-${Date.now()}`,
      type: 'consent_agenda_pull',
      text: `Removed from the consent calendar to the floor for separate consideration: ${lineText}`,
      timestamp: new Date().toLocaleTimeString(),
      metadata: { consentBatchId: batchId, consentLineId: lineId },
      result: null,
    });
    setMeetingSetup((prev) => ({
      ...prev,
      consentCalendarBatchId: batchId,
      consentAgendaItems: (prev.consentAgendaItems || []).map((i) =>
        i.id === lineId ? { ...i, pulledToFloor: true } : i
      ),
      consentPulledToFloorQueue: [
        ...(prev.consentPulledToFloorQueue || []),
        { consentLineId: lineId, text: lineText },
      ],
    }));
  };

  const adoptConsentAgenda = () => {
    const currentItem = getCurrentAgendaItem();
    if (!isConsentCalendarAgendaRow(currentItem) || currentItem.completed) return;

    const batchId = meetingSetup.consentCalendarBatchId || `cb-${Date.now()}`;
    const remaining = getActiveConsentItems(meetingSetup.consentAgendaItems);
    const queue = [...(meetingSetup.consentPulledToFloorQueue || [])];
    const remainingTexts = remaining.map((i) => i.text).filter(Boolean);
    const pulledTexts = queue.map((q) => q.text).filter(Boolean);

    const summaryLines = [
      'The following items on the consent calendar were adopted by general consent:',
      ...(remainingTexts.length > 0
        ? remainingTexts.map((t, i) => `  ${i + 1}. ${t}`)
        : ['  (none — all items were removed to the floor.)']),
    ];
    if (pulledTexts.length > 0) {
      summaryLines.push(
        'Items removed to the floor for separate consideration (to be taken up as main motions):',
        ...pulledTexts.map((t, i) => `  ${i + 1}. ${t}`)
      );
    }
    const summaryText = summaryLines.join('\n');

    const pullEntries = meetingMinutes.filter(
      (e) => e.type === 'consent_agenda_pull' && e.metadata?.consentBatchId === batchId
    );
    const maxPullChrono =
      pullEntries.length > 0
        ? Math.max(...pullEntries.map((e) => Number(e.chronologicalOrder) || 0))
        : null;
    const adoptionBase =
      maxPullChrono != null && maxPullChrono > 0 ? maxPullChrono + 1 : Date.now();

    addMinuteEntry({
      id: `consent-summary-${Date.now()}`,
      type: 'consent_batch_summary',
      text: summaryText,
      result: 'passed_by_uc',
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: adoptionBase,
      metadata: {
        consentBatchId: batchId,
        adoptedCount: remainingTexts.length,
        removedCount: pulledTexts.length,
      },
    });

    handleAddAgendaEntry({
      id: Date.now(),
      text: 'Consent Calendar completed',
      timestamp: new Date().toLocaleTimeString(),
      type: 'agenda_completion',
      chronologicalOrder: adoptionBase + 1,
    });

    const consentIdx = currentAgendaIndex;
    const newRows = queue.map((q, idx) => ({
      id: `mfc-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
      type: MAIN_FROM_CONSENT_AGENDA_TYPE,
      title: `Main motion (from consent): ${(q.text || 'Untitled').slice(0, 72)}${(q.text || '').length > 72 ? '…' : ''}`,
      mainMotionText: q.text || '',
      completed: false,
    }));

    const newOrder = meetingSetup.orderOfBusiness.map((row, i) =>
      i === consentIdx ? { ...row, completed: true, completedAt: new Date().toLocaleTimeString() } : row
    );
    newOrder.splice(consentIdx + 1, 0, ...newRows);

    setMeetingSetup({
      ...meetingSetup,
      orderOfBusiness: newOrder,
      consentCalendarBatchId: batchId,
      consentPulledToFloorQueue: [],
      consentRemovalSuggestions: [],
    });
    setCurrentAgendaIndex(consentIdx + 1);
  };

  const submitMainMotionFromConsentAgenda = (text, member) => {
    if (!member || !String(member).trim()) {
      alert('Please enter the name of the member moving the motion.');
      return;
    }
    const motion = {
      id: Date.now(),
      type: 'MAIN',
      text: text || 'Main motion',
      member: String(member).trim(),
      seconded: false,
      status: 'pending_second',
      timestamp: new Date().toLocaleTimeString(),
      parentMotionId: null,
    };
    setMotions((prev) => [...prev, motion]);
    setCurrentMotion(motion);
    setMeetingSetup((prev) => ({
      ...prev,
      orderOfBusiness: prev.orderOfBusiness.map((row, i) =>
        i === currentAgendaIndex && row.type === MAIN_FROM_CONSENT_AGENDA_TYPE
          ? { ...row, introducedMotionId: motion.id }
          : row
      ),
    }));
  };

  const dismissConsentSuggestion = (suggestionId) => {
    setMeetingSetup((prev) => ({
      ...prev,
      consentRemovalSuggestions: (prev.consentRemovalSuggestions || []).filter((s) => s.id !== suggestionId),
    }));
  };

  // Legacy function for backward compatibility (will be replaced)
  const handleAddAgendaEntry = (entry) => {
    addMinuteEntry(entry);
  };

  // Meeting start/end time tracking — first press of header meeting timer "Start" (Play)
  // totalMembers/presentMembers come from manual entry during meeting setup, or from
  // roll call (see rollCallAttendance / handleRollCallConfirm) — not from this handler.
  // Idempotent regardless of call count/source: dedupes on the live meetingMinutes array
  // (via the functional updater) rather than trusting a meetingStartTime check made earlier
  // in the same tick, since that can be stale across a race or a driver handoff.
  const handleCallToOrder = () => {
    const startTime = new Date();
    setMeetingMinutes((prev) => {
      if ((prev || []).some((entry) => entry.type === 'call_to_order')) {
        return prev;
      }
      return [
        ...prev,
        {
          id: Date.now(),
          text: `Meeting called to order at ${startTime.toLocaleTimeString()}`,
          timestamp: startTime.toLocaleTimeString(),
          chronologicalOrder: Date.now(),
          type: 'call_to_order',
          parentMotionId: null,
          motionData: null,
          electionData: null,
          metadata: { fullDateTime: startTime.toISOString() },
          status: 'completed',
          result: null,
          activityOnly: false,
          lastUpdated: Date.now(),
        },
      ];
    });
    // Functional form: never clobber an already-recorded start time with a later re-invocation.
    setMeetingStartTime((prev) => prev || startTime);
  };

  const handleToggleMeetingTimer = () => {
    if (!isTimerRunning && !meetingStartTime) {
      const agendaWidgetGuidesOrder =
        showAgendaWidget && (meetingSetup.orderOfBusiness?.length || 0) > 0;
      if (!agendaWidgetGuidesOrder) {
        handleCallToOrder();
      }
    }
    setIsTimerRunning((r) => !r);
  };

  const appendAdjournmentMinute = useCallback(
    (minutesList) => {
      const entry = createAdjournmentMinuteEntry({ meetingStartTime });
      const next = [...(Array.isArray(minutesList) ? minutesList : []), entry];
      setMeetingMinutes(next);
      setMeetingEndTime(entry.metadata.fullDateTime);
      setIsTimerRunning(false);
      setMeetingSetup((prev) => ({
        ...prev,
        meetingEndTime: entry.metadata.fullDateTime,
      }));
      return { minutes: next, endTime: entry.metadata.fullDateTime };
    },
    [meetingStartTime]
  );

  /**
   * Official product close: archive Meeting Log, end live officer session, leave meeting UI.
   */
  const finalizeMeeting = useCallback(
    async ({
      recordAdjournment = false,
      navigateHome = true,
      minutesOverride = null,
      endTimeOverride = null,
      confirmArchiveFailure = true,
    } = {}) => {
      const orgId = orgIdFromRoute || activeOrganizationId;
      const sessionId = officerSessionId;
      let minutes = Array.isArray(minutesOverride) ? [...minutesOverride] : [...meetingMinutes];
      let endSnapshot = endTimeOverride || meetingEndTime || new Date().toISOString();

      if (recordAdjournment && !minutesIncludeAdjournment(minutes)) {
        const { minutes: withAdj, endTime } = appendAdjournmentMinute(minutes);
        minutes = withAdj;
        endSnapshot = endTime;
      } else if (!meetingEndTime && !endTimeOverride) {
        endSnapshot = new Date().toISOString();
        setMeetingEndTime(endSnapshot);
        setIsTimerRunning(false);
      }

      const setupSnapshot = {
        ...meetingSetup,
        meetingStartTime: meetingStartTime || meetingSetup.meetingStartTime,
        meetingEndTime: endSnapshot,
      };

      const { archiveError, endedSession } = await archiveAndEndLiveSession({
        organizationId: orgId,
        meetingSessionId: sessionId,
        meetingMinutes: minutes,
        meetingSetup: setupSnapshot,
        meetingStartTime: meetingStartTime || meetingSetup.meetingStartTime,
        meetingEndTime: endSnapshot,
        meetingTimer,
        endSession: Boolean(sessionId),
      });

      if (archiveError && confirmArchiveFailure) {
        const msg = formatSupabaseError(
          archiveError,
          'Could not save minutes to the Meeting Log.'
        );
        if (navigateHome) {
          if (
            !window.confirm(`${msg}\n\nEnd the session anyway? Unsaved minutes may be lost.`)
          ) {
            return { cancelled: true, archiveError };
          }
        } else {
          window.alert(msg);
          return { cancelled: true, archiveError };
        }
        if (sessionId && hasSupabaseConfig() && !endedSession) {
          try {
            await endMeetingSession(sessionId);
          } catch (err) {
            console.warn('end_meeting_session failed', err?.message || err);
          }
        }
      }

      try {
        clearMeetingSessionFlag();
      } catch {
        /* ignore */
      }
      setOfficerSessionId(null);
      officerSessionBootRef.current = '';
      setDriverSeat('local');
      setChairSignal(null);
      setIsTimerRunning(false);

      if (navigateHome) {
        if (orgId) {
          navigate(`/orgs/${orgId}`, { replace: true });
        } else {
          navigate('/orgs', { replace: true });
        }
      }

      return { cancelled: false, archiveError };
    },
    [
      orgIdFromRoute,
      activeOrganizationId,
      officerSessionId,
      meetingMinutes,
      meetingEndTime,
      meetingSetup,
      meetingStartTime,
      meetingTimer,
      appendAdjournmentMinute,
      navigate,
    ]
  );

  const promptFinalizeAfterAdjourn = useCallback(
    (minutesAfterAdjourn, endTimeIso) => {
      if (
        !window.confirm(
          'Meeting adjourned. End the live session and save minutes to the Meeting Log?'
        )
      ) {
        return;
      }
      void finalizeMeeting({
        recordAdjournment: false,
        navigateHome: true,
        minutesOverride: minutesAfterAdjourn,
        endTimeOverride: endTimeIso || new Date().toISOString(),
      });
    },
    [finalizeMeeting]
  );

  const endElections = () => {
    if (window.confirm('End all elections and return to regular meeting? This will clear any ongoing elections.')) {
      setInElectionMode(false);
      setCurrentElection(null);
      setCandidateSpeechQueue([]);
      setCurrentCandidate(null);
      setCurrentSpeaker(null);
      setSpeakerTime(0);
    }
  };


  // Reset meeting to clean state (for testing)
  const resetMeeting = () => {
    if (window.confirm('Are you sure you want to reset the entire meeting? This will clear all data including motions, minutes, and elections.')) {
      // Reset all state to initial values
      setMeetingTimer(0);
      setIsTimerRunning(false);
      setCurrentMotion(null);
      setMotions([]);
      setMotionStack([]);
      setCommittees([]);
      setElections([]);
      setMeetingMinutes([]);
      setCurrentAgendaIndex(0);
      setSpeakerQueue([]);
      setCurrentSpeaker(null);
      setSpeakerTime(0);
      setCurrentElection(null);
      setCandidateSpeechQueue([]);
      setCurrentCandidate(null);
      setInElectionMode(false);
      setElectionScreen(1);
      setElectionNominees([]);
      setElectionBallotVotes({});
      setElectionWriteInCandidates([]);
      setElectionInvalidBallots(0);
      setReferralMotion(null);
      setShowReferralDialog(false);
      setSelectedCommitteeForReferral(null);
      setReferralInstructions('');

      // Reset to meeting view
      setCurrentView('meeting');

      // Sync the reset state
      syncMeetingState();
    }
  };

  /** Clears in-session meeting data and opens the setup wizard (keeps committees & settings). */
  const startNewMeetingSession = () => {
    const hasMinutes = Array.isArray(meetingMinutes) && meetingMinutes.length > 0;
    const confirmMsg = hasMinutes
      ? 'Start a new meeting? Current minutes will be saved to the Meeting Log (session finalize), then cleared. You can configure the next meeting in the setup wizard.'
      : 'Start a new meeting? Motions, minutes, elections, and timers for this session will be cleared. You can configure the meeting again in the setup wizard.';
    if (!window.confirm(confirmMsg)) {
      return;
    }

    const orgId = orgIdFromRoute || activeOrganizationId;
    const minutesSnapshot = meetingMinutes;
    const setupSnapshot = meetingSetup;
    const startSnapshot = meetingStartTime;
    const endSnapshot = meetingEndTime || new Date().toISOString();
    const timerSnapshot = meetingTimer;
    const sessionSnapshot = officerSessionId;

    const wipeAndReset = () => {
      try {
        clearMeetingSessionFlag();
      } catch {
        /* ignore */
      }

      setMeetingSetup((prev) => {
        const fresh = createDefaultMeetingSetup();
        const prevOrg = prev.organizationSettings;
        const org = prevOrg
          ? organizationSettingsFromBylaws({
              allowConsentAgenda: !!prevOrg.allowConsentAgenda,
              enableRollCallRoster: !!prevOrg.enableRollCallRoster,
              defaultOrderOfBusiness:
                prevOrg.defaultOrderOfBusiness?.length > 0
                  ? cloneAgendaTemplateFromOrder(prevOrg.defaultOrderOfBusiness)
                  : fresh.organizationSettings.defaultOrderOfBusiness,
              defaultConsentLines: cloneConsentLinesTemplate(
                prevOrg.defaultConsentLines?.length
                  ? prevOrg.defaultConsentLines
                  : fresh.organizationSettings.defaultConsentLines || []
              ),
              defaultUnanimousConsentRequests:
                prevOrg.defaultUnanimousConsentRequests?.length
                  ? prevOrg.defaultUnanimousConsentRequests
                  : fresh.organizationSettings.defaultUnanimousConsentRequests,
              defaultQuorumType: prevOrg.defaultQuorumType || 'percentage',
              defaultQuorumValue:
                typeof prevOrg.defaultQuorumValue === 'number'
                  ? prevOrg.defaultQuorumValue
                  : 50,
              defaultParliamentaryMode: prevOrg.defaultParliamentaryMode || 'standard',
            })
          : fresh.organizationSettings;
        const { orderOfBusiness, consentAgendaItems } = meetingAgendaFromOrganizationSettings(org);
        setParliamentaryMode(org.defaultParliamentaryMode || 'standard');
        rollCallPromptedRef.current = null;
        return {
          ...fresh,
          rollCallCompleted: false,
          rollCallAttendance: null,
          organizationName: prev.organizationName || fresh.organizationName,
          organizationSettings: org,
          orderOfBusiness,
          consentAgendaItems,
          consentPulledToFloorQueue: [],
          consentRemovalSuggestions: [],
          consentCalendarBatchId: undefined,
          quorumType: org.defaultQuorumType || 'percentage',
          quorumRequirement:
            typeof org.defaultQuorumValue === 'number' ? org.defaultQuorumValue : 50,
        };
      });
      setMeetingTimer(0);
      setIsTimerRunning(false);
      setMeetingStartTime(null);
      setMeetingEndTime(null);
      setCurrentMotion(null);
      setMotions([]);
      setMotionStack([]);
      setMeetingMinutes([]);
      setCurrentAgendaIndex(0);
      setTabledMotions([]);
      setPostponedMotions([]);
      setReferredMotions([]);
      setPendingUCRequest(null);
      setSpeakerQueue([]);
      setCurrentSpeaker(null);
      setSpeakerTime(0);
      setElections([]);
      setCurrentElection(null);
      setCandidateSpeechQueue([]);
      setCurrentCandidate(null);
      setInElectionMode(false);
      setElectionScreen(1);
      setElectionNominees([]);
      setElectionBallotVotes({});
      setElectionWriteInCandidates([]);
      setElectionInvalidBallots(0);
      setReferralMotion(null);
      setShowReferralDialog(false);
      setSelectedCommitteeForReferral(null);
      setReferralInstructions('');
      setCurrentView('meeting');
      setShowSettings(false);
      setShowSetupWizard(true);
      setDriverSeat('local');
      setChairSignal(null);
      if (officerSessionId) {
        setOfficerSessionId(null);
        officerSessionBootRef.current = '';
        const next = new URLSearchParams(searchParams);
        next.delete('session');
        navigate({ search: next.toString() }, { replace: true });
      }
      syncMeetingState();
    };

    void (async () => {
      if (hasMinutes && orgId && hasSupabaseConfig()) {
        const { archiveError, endedSession } = await archiveAndEndLiveSession({
          organizationId: orgId,
          meetingSessionId: sessionSnapshot,
          meetingMinutes: minutesSnapshot,
          meetingSetup: setupSnapshot,
          meetingStartTime: startSnapshot,
          meetingEndTime: endSnapshot,
          meetingTimer: timerSnapshot,
          endSession: Boolean(sessionSnapshot),
        });
        if (archiveError) {
          const msg = formatSupabaseError(
            archiveError,
            'Could not save minutes to the Meeting Log.'
          );
          if (
            !window.confirm(
              `${msg}\n\nStart a new meeting anyway? Unsaved minutes will be lost.`
            )
          ) {
            return;
          }
          if (sessionSnapshot && hasSupabaseConfig() && !endedSession) {
            try {
              await endMeetingSession(sessionSnapshot);
            } catch {
              /* ignore */
            }
          }
        }
      } else if (sessionSnapshot && hasSupabaseConfig()) {
        try {
          await endMeetingSession(sessionSnapshot);
        } catch {
          /* ignore */
        }
      }
      wipeAndReset();
    })();
  };

  const handleEndLiveMeeting = () => {
    const hasAdjourn = minutesIncludeAdjournment(meetingMinutes);
    let recordAdjournment = false;
    if (!hasAdjourn) {
      const record = window.confirm(
        'Record adjournment in the minutes, then end the session?\n\nOK = add adjournment line and save to Meeting Log.\nCancel = end/archive without adding adjournment (technical teardown).'
      );
      // window.confirm: OK=true means record; but we also need a second confirm for ending
      // Plan: Default Yes for record. If they Cancel, still ask to end without adjournment.
      if (record) {
        recordAdjournment = true;
      } else if (
        !window.confirm(
          'End the live officer session without recording adjournment? Minutes will still be saved to the Meeting Log when available.'
        )
      ) {
        return;
      }
    } else if (
      !window.confirm(
        'End the live officer session for everyone? Minutes will be saved to the Meeting Log when available. You will return to the organization home.'
      )
    ) {
      return;
    }

    void finalizeMeeting({
      recordAdjournment,
      navigateHome: true,
      confirmArchiveFailure: true,
    });
  };

  const completeMeetingSetupWizard = () => {
    markMeetingSessionStarted();
    setShowSetupWizard(false);
    if (meetingSetup.enableQrDeviceVoting && hasSupabaseConfig()) {
      void ensureVoteRoom();
    }
    if (hasSupabaseConfig()) {
      void ensureOfficerSession();
    }
  };

  /** Tour-only: close wizard without creating a live officer session (avoids Join panel). */
  const dismissMeetingSetupWizardForTour = useCallback(() => {
    markMeetingSessionStarted();
    setShowSetupWizard(false);
  }, []);

  // Guided tour: sample motion + speakers so console highlights are meaningful
  useEffect(() => {
    const findAgendaIndex = (order, match) => {
      if (!match || !Array.isArray(order)) return 0;
      const needle = String(match).toLowerCase();
      const idx = order.findIndex((item) => String(item?.title || '').toLowerCase().includes(needle));
      return idx >= 0 ? idx : 0;
    };

    const clearTourMotion = () => {
      setCurrentMotion((prev) => (prev?.id === TOUR_DEMO_MOTION_ID ? null : prev));
      setMotions((prev) => (prev || []).filter((m) => m.id !== TOUR_DEMO_MOTION_ID));
      setMotionStack((prev) => (prev || []).filter((m) => m.id !== TOUR_DEMO_MOTION_ID));
      setCurrentSpeaker(null);
      setSpeakerQueue([]);
      setSpeakerTime(0);
      setIsTimerRunning(false);
    };

    const seedMeetingDemo = () => {
      setShowAgendaWidget(true);
      setMeetingSetup((prev) => {
        const order = prev.orderOfBusiness?.length
          ? prev.orderOfBusiness
          : createDefaultMeetingSetup().orderOfBusiness;
        const idx = findAgendaIndex(order, 'New Business');
        const nextOrder = order.map((item, i) =>
          i < idx ? { ...item, completed: true } : i === idx ? { ...item, completed: false } : item
        );
        setCurrentAgendaIndex(idx);
        return { ...prev, orderOfBusiness: nextOrder };
      });
      const motion = buildTourDemoMotion();
      setCurrentMotion(motion);
      setMotions((prev) => {
        const without = (prev || []).filter((m) => m.id !== TOUR_DEMO_MOTION_ID);
        return [...without, motion];
      });
      setMotionStack([]);
      const speakers = buildTourDemoSpeakers();
      setCurrentSpeaker(speakers.currentSpeaker);
      setSpeakerTime(speakers.speakerTime);
      setSpeakerQueue(speakers.speakerQueue);
      setIsTimerRunning(false);
      setMeetingMinutes((prev) => {
        const withoutTour = (prev || []).filter((e) => !String(e.id).startsWith('pp-tour-min-'));
        return [...withoutTour, ...buildTourDemoMinutes(motion)];
      });
    };

    const setTourAgenda = (event) => {
      const match = event?.detail?.match || 'Call to Order';
      const clearMotion = event?.detail?.clearMotion !== false;
      const markPriorComplete = event?.detail?.markPriorComplete !== false;
      setShowAgendaWidget(true);
      if (clearMotion) clearTourMotion();
      setMeetingSetup((prev) => {
        const order = prev.orderOfBusiness?.length
          ? prev.orderOfBusiness
          : createDefaultMeetingSetup().orderOfBusiness;
        const idx = findAgendaIndex(order, match);
        const nextOrder = markPriorComplete
          ? order.map((item, i) =>
              i < idx ? { ...item, completed: true } : i === idx ? { ...item, completed: false } : item
            )
          : order;
        setCurrentAgendaIndex(idx);
        return { ...prev, orderOfBusiness: nextOrder };
      });
    };

    // A prior Elections tour run leaves currentElection/inElectionMode set — several
    // components (e.g. ObserverView) branch on currentElection alone, regardless of which
    // tour or view is actually active. Any non-election tour destination must clear this
    // first or it'll keep rendering election content.
    const clearElectionDemoForTour = () => {
      setCurrentElection(null);
      setInElectionMode(false);
      setElectionScreen(1);
      setElectionNominees([]);
      setElectionBallotVotes({});
      setElectionWriteInCandidates([]);
      setElectionInvalidBallots(0);
      setCurrentCandidate(null);
      setCandidateSpeechQueue([]);
    };

    const openObserverForTour = () => {
      clearElectionDemoForTour();
      seedMeetingDemo();
      setMeetingSetup((prev) => ({ ...prev, enableQrDeviceVoting: true }));
      setVoteJoinCode((prev) => prev || TOUR_DEMO_JOIN_CODE);
      setShowSetupWizard(false);
      markMeetingSessionStarted();
      document.body.classList.remove('observer-window');
      setCurrentView('observer');
    };

    const openMeetingForTour = () => {
      clearElectionDemoForTour();
      document.body.classList.remove('observer-window');
      document.body.classList.remove('chair-prompt-window');
      setCurrentView('meeting');
      setChairNavView('meeting');
    };

    const openElectionsForTour = () => {
      document.body.classList.remove('observer-window');
      document.body.classList.remove('chair-prompt-window');
      setShowSetupWizard(false);
      setCurrentView('elections');
      setChairNavView('elections');
    };

    const openCommitteesForTour = () => {
      clearElectionDemoForTour();
      document.body.classList.remove('observer-window');
      document.body.classList.remove('chair-prompt-window');
      setShowSetupWizard(false);
      setCurrentView('committees');
      setChairNavView('committees');
    };

    const openMeetingWizardForTour = () => {
      openMeetingForTour();
      setShowSetupWizard(true);
    };

    const clearMeetingDemoForTour = () => {
      clearTourMotion();
      setMeetingMinutes((prev) => (prev || []).filter((e) => !String(e.id).startsWith('pp-tour-min-')));
    };

    const setAssistedSessionForTour = () => {
      setMeetingSetup((prev) => ({ ...prev, sessionMode: 'assisted' }));
      setDriverSeat('local');
    };

    const openChairDashboardForTour = () => {
      openMeetingForTour();
      setMeetingSetup((prev) => ({ ...prev, sessionMode: 'assisted' }));
      setDriverSeat('local');
      setShowSetupWizard(false);
      markMeetingSessionStarted();
      seedMeetingDemo();
      setCurrentView('chair-prompt');
    };

    window.addEventListener('pp-tour-seed-meeting-demo', seedMeetingDemo);
    window.addEventListener('pp-tour-clear-meeting-demo', clearMeetingDemoForTour);
    window.addEventListener('pp-tour-set-agenda', setTourAgenda);
    window.addEventListener('pp-tour-open-observer', openObserverForTour);
    window.addEventListener('pp-tour-open-meeting-view', openMeetingForTour);
    window.addEventListener('pp-tour-open-elections-view', openElectionsForTour);
    window.addEventListener('pp-tour-open-committees-view', openCommitteesForTour);
    window.addEventListener('pp-tour-open-meeting-wizard', openMeetingWizardForTour);
    window.addEventListener('pp-tour-set-assisted-session', setAssistedSessionForTour);
    window.addEventListener('pp-tour-open-chair-dashboard', openChairDashboardForTour);
    return () => {
      window.removeEventListener('pp-tour-seed-meeting-demo', seedMeetingDemo);
      window.removeEventListener('pp-tour-clear-meeting-demo', clearMeetingDemoForTour);
      window.removeEventListener('pp-tour-set-agenda', setTourAgenda);
      window.removeEventListener('pp-tour-open-observer', openObserverForTour);
      window.removeEventListener('pp-tour-open-meeting-view', openMeetingForTour);
      window.removeEventListener('pp-tour-open-elections-view', openElectionsForTour);
      window.removeEventListener('pp-tour-open-committees-view', openCommitteesForTour);
      window.removeEventListener('pp-tour-open-meeting-wizard', openMeetingWizardForTour);
      window.removeEventListener('pp-tour-set-assisted-session', setAssistedSessionForTour);
      window.removeEventListener('pp-tour-open-chair-dashboard', openChairDashboardForTour);
    };
  }, []);

  // Export minutes
  const exportMinutes = (format = 'txt') => {
    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    // Enhanced parliamentary document header
    let minutesContent = ``;

    // Formal parliamentary header (use ASCII characters for better compatibility)
    minutesContent += `===============================================================\n`;
    minutesContent += `                        MINUTES OF MEETING\n`;
    if (meetingSetup.organizationName) {
      minutesContent += `                    ${meetingSetup.organizationName.toUpperCase()}\n`;
    }
    minutesContent += `===============================================================\n\n`;

    // Meeting metadata section
    minutesContent += `MEETING INFORMATION:\n`;
    minutesContent += `-------------------------------------------------------------\n`;

    if (meetingSetup.organizationName) {
      minutesContent += `Organization:        ${meetingSetup.organizationName}\n`;
    }

    // Enhanced date and time formatting
    if (meetingSetup.meetingStartTime) {
      const meetingDate = new Date(meetingSetup.meetingStartTime).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const startTime = new Date(meetingSetup.meetingStartTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      minutesContent += `Date:                ${meetingDate}\n`;
      minutesContent += `Called to Order:     ${startTime}\n`;
    }

    if (meetingSetup.meetingEndTime) {
      const endTime = new Date(meetingSetup.meetingEndTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      minutesContent += `Adjourned:           ${endTime}\n`;
    }

    minutesContent += `Duration:            ${formatTime(meetingTimer)}\n`;

    // Quorum information
    if (meetingSetup.organizationName) {
      const quorumStatus = getQuorumStatus(meetingSetup);
      minutesContent += `Quorum Status:       ${quorumStatus.text} (${quorumStatus.details})\n`;
    }

    minutesContent += `\n`;

    // Enhanced parliamentary proceedings section
    if (meetingMinutes.length > 0) {
      minutesContent += `PROCEEDINGS:\n`;
      minutesContent += `-------------------------------------------------------------\n\n`;

      // Sort by chronological order (ascending for export)
      const sortedMinutes = [...meetingMinutes].sort((a, b) =>
        (a.chronologicalOrder || 0) - (b.chronologicalOrder || 0)
      );

      const officialEntries = sortedMinutes.filter(
        (e) =>
          !e.activityOnly &&
          e.type !== 'objection' &&
          e.type !== 'uc_request'
      );

      officialEntries.forEach(entry => {
        const textToUse = getMinuteEntryDisplayText(entry, motionTypes);
        let entryText = `${entry.timestamp} - ${textToUse}`;

        // Add status information (skip adopt/defeat suffix when the line already states chair rulings, etc.)
        if (
          entry.status === 'completed' &&
          entry.motionData &&
          !minuteExportSuppressesAdoptedDefeatedSuffix(entry, motionTypes)
        ) {
          const status = entry.motionData.result || entry.motionData.status;
          if (status === 'voted_passed' || status === 'passed') {
            entryText += ' - ADOPTED';
          } else if (status === 'voted_failed' || status === 'failed') {
            entryText += ' - DEFEATED';
          } else if (status === 'passed_by_uc') {
            entryText += ' - ADOPTED BY UNANIMOUS CONSENT';
          } else if (status === 'withdrawn') {
            entryText += ' - WITHDRAWN';
          } else if (status === 'tabled') {
            entryText += ' - LAID ON THE TABLE';
          } else if (status === 'postponed') {
            entryText += ' - POSTPONED';
          }
        }

        minutesContent += entryText + '\n';
      });

      minutesContent += `\n`;
    } else {
      minutesContent += `No formal business was conducted during this meeting.\n\n`;
    }

    // Enhanced elections section with formal parliamentary formatting
    const completedElections = elections.filter(e => e.status === 'completed');
    if (completedElections.length > 0) {
      minutesContent += `\nELECTIONS CONDUCTED:\n`;
      minutesContent += `-------------------------------------------------------------\n\n`;
      completedElections.forEach((election, index) => {
        minutesContent += `${index + 1}. Election for ${election.office}\n`;

        // Winner information
        if (election.winner) {
          minutesContent += `   Winner: ${election.winner}\n`;
        }

        // Detailed vote breakdown
        if (election.results && election.results.length > 0) {
          minutesContent += `   Final Results:\n`;
          election.results.forEach((result, idx) => {
            minutesContent += `     ${idx + 1}. ${result.candidate}: ${result.votes} votes (${result.percentage}%)\n`;
          });
        }

        // Ballot summary (use per-election data)
        // Note: election.results now includes both regular nominees and write-ins, so we don't double-count
        const totalValidVotes = election.results ? election.results.reduce((sum, result) => sum + result.votes, 0) : 0;
        const totalBallots = totalValidVotes + (election.electionInvalidBallots || 0);
        const writeInVotes = election.electionWriteInCandidates ? election.electionWriteInCandidates.reduce((sum, writeIn) => sum + (writeIn.votes || 0), 0) : 0;

        if (totalBallots > 0) {
          minutesContent += `   Ballot Summary:\n`;
          minutesContent += `     Total Ballots Cast: ${totalBallots}\n`;
          minutesContent += `     Valid Ballots: ${totalValidVotes}\n`;
          if (election.electionInvalidBallots > 0) {
            minutesContent += `     Invalid/Spoiled Ballots: ${election.electionInvalidBallots}\n`;
          }
          if (writeInVotes > 0) {
            minutesContent += `     Total Write-in Votes: ${writeInVotes}\n`;
          }
        }

        minutesContent += `\n`;
      });
    }

    // Formal parliamentary footer
    minutesContent += `\n\n`;
    minutesContent += `===============================================================\n`;
    minutesContent += `                        ATTESTATION\n`;
    minutesContent += `===============================================================\n\n`;

    minutesContent += `These minutes were compiled from proceedings recorded during the\n`;
    minutesContent += `above-referenced meeting and accurately reflect the business\n`;
    minutesContent += `conducted and decisions made.\n\n`;

    minutesContent += `Minutes prepared: ${new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })} at ${new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })}\n\n`;

    minutesContent += `Secretary: ______________________________ Date: __________\n\n`;
    minutesContent += `Chair: __________________________________ Date: __________\n\n`;

    minutesContent += `-------------------------------------------------------------\n`;
    minutesContent += `Generated by Pocket Parliamentarian\n`;
    minutesContent += `Parliamentary Meeting Management System\n`;
    minutesContent += `-------------------------------------------------------------\n`;

    const fileName = `Meeting_Minutes_${meetingSetup.organizationName || 'Organization'}_${currentDate.replace(/\//g, '-')}`;
    downloadMinutesExport(minutesContent, fileName, format);
  };

  const navigationItems = [
    { id: 'meeting', label: 'Meeting', icon: Gavel, color: 'text-blue-600' },
    { id: 'committees', label: 'Committees', icon: Users, color: 'text-green-600' },
    { id: 'elections', label: 'Elections', icon: Vote, color: 'text-amber-600' },
  ];

  const themeClass = 'min-h-screen bg-pp-paper font-sans text-pp-ink antialiased';

  return (
    <div className={themeClass}>

      {!document.body.classList.contains('chair-prompt-window') &&
        currentView !== 'observer' &&
        currentView !== 'chair-prompt' && (
      <div className="meeting-header">
        <MeetingHeader
          globalTheme={globalTheme}
          meetingSetup={meetingSetup}
          meetingTimer={meetingTimer}
          isTimerRunning={isTimerRunning}
          onToggleTimer={handleToggleMeetingTimer}
          onExportMinutes={exportMinutes}
          onOpenSettings={() => setShowSettings(true)}
          onOpenTutorial={() => openCatalog()}
          onOpenQuorumAdjust={() => setShowQuorumAdjust(true)}
          currentView={currentView}
          onViewChange={handleViewChange}
          orgId={orgIdFromRoute || activeOrganizationId}
          canMeeting={skipAuth || canMeeting}
          canCommittees={skipAuth || canCommittees}
          canElections={skipAuth || canElections}
          voteJoinCode={meetingSetup.enableQrDeviceVoting ? voteJoinCode : null}
          voteRoomError={voteRoomError}
          voteRoomBusy={voteRoomBusy}
          onEnsureVoteRoom={
            meetingSetup.enableQrDeviceVoting && hasSupabaseConfig() ? ensureVoteRoom : null
          }
          onLaunchChairPrompt={launchChairPromptWindow}
          driverSeat={driverSeat}
          onReturnControl={handleChairReturnControl}
          onReturnToChairPrompt={handleReturnToChairPrompt}
          officerSessionId={officerSessionId}
          officerPromptUrl={
            officerSessionId && (orgIdFromRoute || activeOrganizationId)
              ? officerSessionUrl({
                  orgId: orgIdFromRoute || activeOrganizationId,
                  sessionId: officerSessionId,
                  seat: 'prompt',
                })
              : null
          }
          officerObserverUrl={
            officerSessionId && (orgIdFromRoute || activeOrganizationId)
              ? officerSessionUrl({
                  orgId: orgIdFromRoute || activeOrganizationId,
                  sessionId: officerSessionId,
                  seat: 'observer',
                })
              : null
          }
          onEndLiveMeeting={officerSessionId ? handleEndLiveMeeting : null}
        />
      </div>
      )}

      {/* Content Area */}
      <div className={`mx-auto max-w-[1600px] px-4 pb-24 sm:px-6 lg:px-8 ${document.body.classList.contains('chair-prompt-window') ? 'max-w-none px-0 pb-0' : ''}`}>
        {chairSignal &&
          currentView !== 'observer' &&
          currentView !== 'chair-prompt' &&
          !document.body.classList.contains('chair-prompt-window') &&
          !document.body.classList.contains('observer-window') && (
          <div
            className={`mb-4 mt-4 flex items-start justify-between gap-3 rounded-xl border px-4 py-3 shadow-pp ${
              chairSignal.type === 'hold'
                ? 'border-amber-300 bg-amber-50 text-amber-950'
                : chairSignal.type === 'take_control'
                  ? 'border-pp-accent/40 bg-pp-accentSoft text-pp-ink'
                  : chairSignal.type === 'return_control'
                    ? 'border-pp-sage/40 bg-pp-sageSoft text-pp-sage'
                    : 'border-pp-sage/40 bg-pp-sageSoft text-pp-sage'
            }`}
            data-tour="chair-signal-banner"
            role="status"
          >
            <div>
              <p className="text-sm font-semibold">
                {chairSignal.type === 'hold' && 'Chair asked to hold'}
                {chairSignal.type === 'ready' && 'Chair is ready'}
                {chairSignal.type === 'take_control' && 'Chair took control'}
                {chairSignal.type === 'return_control' && 'Control returned to secretary'}
              </p>
              <p className="mt-0.5 text-xs opacity-90">
                {chairSignal.type === 'hold' &&
                  'Pause advancing until the chair signals Ready. Motions still run on this Driver window.'}
                {chairSignal.type === 'ready' &&
                  'The chair is ready for the next parliamentary step.'}
                {chairSignal.type === 'take_control' &&
                  'Run Motions on this Driver console (or hand the laptop to the chair). Use Return control when finished.'}
                {chairSignal.type === 'return_control' &&
                  'You are driving again — Motions and Active business are yours.'}
                {chairSignal.at ? ` · ${chairSignal.at}` : ''}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              {chairSignal.type === 'take_control' && driverSeat === 'remote' && (
                <button
                  type="button"
                  className="rounded-lg bg-pp-sage px-2.5 py-1 text-xs font-semibold text-white"
                  onClick={() => {
                    handleChairReturnControl();
                    setChairSignal(null);
                  }}
                >
                  Return control
                </button>
              )}
              <button
                type="button"
                className="rounded-lg px-2 py-1 text-xs font-medium underline"
                onClick={() => setChairSignal(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {chairMessageToDriver &&
          currentView !== 'observer' &&
          currentView !== 'chair-prompt' &&
          !document.body.classList.contains('chair-prompt-window') &&
          !document.body.classList.contains('observer-window') && (
            <div
              className="mb-4 mt-4 flex items-start justify-between gap-3 rounded-xl border border-pp-accent/40 bg-pp-accentSoft px-4 py-3 text-pp-ink shadow-pp"
              data-tour="chair-message-banner"
              role="status"
            >
              <div>
                <p className="text-sm font-semibold">Message from the chair</p>
                <p className="mt-0.5 text-sm">{chairMessageToDriver.body}</p>
                {chairMessageToDriver.at ? (
                  <p className="mt-1 text-xs opacity-80">{chairMessageToDriver.at}</p>
                ) : null}
              </div>
              <button
                type="button"
                className="shrink-0 rounded-lg px-2 py-1 text-xs font-medium underline"
                onClick={() => setChairMessageToDriver(null)}
              >
                Dismiss
              </button>
            </div>
          )}

        {meetingSetup?.sessionMode === 'assisted' &&
          officerSessionId &&
          currentView !== 'observer' &&
          currentView !== 'chair-prompt' &&
          !document.body.classList.contains('chair-prompt-window') &&
          !document.body.classList.contains('observer-window') && (
            <div
              className="mb-4 mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-pp-line bg-pp-surface px-3 py-2 shadow-pp"
              data-tour="driver-message-composer"
            >
              <label className="sr-only" htmlFor="driver-msg-to-chair">
                Message to chair
              </label>
              <input
                id="driver-msg-to-chair"
                type="text"
                value={driverMessageDraft}
                onChange={(e) => setDriverMessageDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    sendDriverMessageToChair();
                  }
                }}
                placeholder="Message the chair…"
                maxLength={280}
                className="min-w-[12rem] flex-1 rounded-lg border border-pp-line bg-pp-paper px-3 py-2 text-sm text-pp-ink outline-none focus:ring-2 focus:ring-pp-accent/30"
              />
              <button
                type="button"
                disabled={driverMessageBusy || !String(driverMessageDraft || '').trim()}
                onClick={sendDriverMessageToChair}
                className="rounded-lg bg-pp-accent px-3 py-2 text-sm font-semibold text-white hover:bg-pp-accentHover disabled:opacity-50"
              >
                {driverMessageBusy ? 'Sending…' : 'Send to chair'}
              </button>
            </div>
          )}

        {currentView === 'meeting' && (skipAuth || canMeeting) && !document.body.classList.contains('observer-window') && !document.body.classList.contains('chair-prompt-window') && (
          <MeetingInterface 
            theme={globalTheme}
            meetingSetup={meetingSetup}
            setMeetingSetup={setMeetingSetup}
            motions={motions}
            setMotions={setMotions}
            currentMotion={currentMotion}
            setCurrentMotion={setCurrentMotion}
            motionStack={motionStack}
            setMotionStack={setMotionStack}
            meetingMinutes={meetingMinutes}
            setMeetingMinutes={setMeetingMinutes}
            addMinuteEntry={addMinuteEntry}
            updateMinuteEntry={updateMinuteEntry}
            onAddAgendaEntry={handleAddAgendaEntry}
            onExportMinutes={exportMinutes}
            committees={committees}
            onReferMotion={handleReferMotion}
            speakerQueue={speakerQueue}
            setSpeakerQueue={setSpeakerQueue}
            currentSpeaker={currentSpeaker}
            setCurrentSpeaker={setCurrentSpeaker}
            speakerTime={speakerTime}
            setSpeakerTime={setSpeakerTime}
            speakerTimeLimit={globalSpeakerTimeLimit}
            setSpeakerTimeLimit={setGlobalSpeakerTimeLimit}
            showSetupWizard={showSetupWizard}
            setShowSetupWizard={setShowSetupWizard}
            onCompleteMeetingSetup={completeMeetingSetupWizard}
            onTourSafeDismissMeetingSetup={dismissMeetingSetupWizardForTour}
            orgId={orgIdFromRoute || activeOrganizationId}
            parliamentaryMode={parliamentaryMode}
            motionSortMode={motionSortMode}
            referralMotion={referralMotion}
            setReferralMotion={setReferralMotion}
            showReferralDialog={showReferralDialog}
            setShowReferralDialog={setShowReferralDialog}
            unanimousConsentRequests={unanimousConsentRequests}
            motionTypes={motionTypes}
            showAgendaWidget={showAgendaWidget}
            setShowAgendaWidget={setShowAgendaWidget}
            currentAgendaIndex={currentAgendaIndex}
            setCurrentAgendaIndex={setCurrentAgendaIndex}
            getAgendaDisplayText={getAgendaDisplayText}
            advanceAgenda={advanceAgenda}
            getCurrentAgendaItem={getCurrentAgendaItem}
            handleUnanimousConsent={handleUnanimousConsent}
            handleUCGrant={handleUCGrant}
            handleUCObjection={handleUCObjection}
            pendingUCRequest={pendingUCRequest}
            tabledMotions={tabledMotions}
            setTabledMotions={setTabledMotions}
            postponedMotions={postponedMotions}
            setPostponedMotions={setPostponedMotions}
            referredMotions={referredMotions}
            setReferredMotions={setReferredMotions}
            chairScript={chairScript}
            chairScriptRefining={chairScriptRefining}
            chairScriptSource={chairScriptSource}
            pullConsentLineToFloor={pullConsentLineToFloor}
            adoptConsentAgenda={adoptConsentAgenda}
            submitMainMotionFromConsentAgenda={submitMainMotionFromConsentAgenda}
            dismissConsentSuggestion={dismissConsentSuggestion}
            meetingStartTime={meetingStartTime}
            requestAmendTextConfirm={requestAmendTextConfirm}
            remoteVoteSession={remoteVoteSession}
            remoteVoteTally={remoteVoteTally}
            remoteVoteBusy={remoteVoteBusy}
            remoteVoteError={remoteVoteError}
            setMeetingEndTime={setMeetingEndTime}
            setIsTimerRunning={setIsTimerRunning}
            promptFinalizeAfterAdjourn={promptFinalizeAfterAdjourn}
            onOpenRemoteVote={
              meetingSetup.enableQrDeviceVoting && voteRoomId && hasSupabaseConfig()
                ? handleOpenRemoteMotionVote
                : null
            }
            onCloseRemoteVote={
              meetingSetup.enableQrDeviceVoting && voteRoomId && hasSupabaseConfig()
                ? handleCloseRemoteVote
                : null
            }
          />
        )}

        {currentView === 'committees' && (skipAuth || canCommittees) && !document.body.classList.contains('observer-window') && !document.body.classList.contains('chair-prompt-window') && (
          <Committees
            theme={globalTheme}
            committees={committees}
            setCommittees={hasSupabaseConfig() ? setCommitteesPersisted : setCommittees}
            onReloadCommittees={
              hasSupabaseConfig()
                ? async () => {
                    const orgId = orgIdFromRoute || activeOrganizationId;
                    if (!orgId) return;
                    const cloud = await loadCommitteesCloud(orgId);
                    setCommittees(cloud);
                  }
                : null
            }
            organizationId={orgIdFromRoute || activeOrganizationId}
            currentUserId={session?.user?.id || null}
            canManageCommittees={
              skipAuth ||
              activeMembership?.role === 'Admin' ||
              activeMembership?.role === 'Chair' ||
              activeMembership?.role === 'Secretary'
            }
            motions={motions}
            onAddAgendaEntry={handleAddAgendaEntry}
            meetingMinutes={meetingMinutes}
            setMeetingMinutes={setMeetingMinutes}
            organizationName={meetingSetup.organizationName}
          />
        )}
        
        {currentView === 'elections' && (skipAuth || canElections) && !document.body.classList.contains('observer-window') && !document.body.classList.contains('chair-prompt-window') && (
          <ElectionsInterface
            theme={globalTheme}
            meetingSetup={meetingSetup}
            elections={elections}
            setElections={setElections}
            currentElection={currentElection}
            setCurrentElection={setCurrentElection}
            candidateSpeechQueue={candidateSpeechQueue}
            setCandidateSpeechQueue={setCandidateSpeechQueue}
            currentCandidate={currentCandidate}
            setCurrentCandidate={setCurrentCandidate}
            speakerTimeLimit={candidateSpeechTimeLimit}
            setCandidateSpeechTimeLimit={setCandidateSpeechTimeLimit}
            inElectionMode={inElectionMode}
            setInElectionMode={setInElectionMode}
            currentSpeaker={currentSpeaker}
            setCurrentSpeaker={setCurrentSpeaker}
            speakerTime={speakerTime}
            setSpeakerTime={setSpeakerTime}
            isTimerRunning={isTimerRunning}
            setIsTimerRunning={setIsTimerRunning}
            onEndElections={endElections}
            onAddAgendaEntry={handleAddAgendaEntry}
            electionScreen={electionScreen}
            setElectionScreen={setElectionScreen}
            electionNominees={electionNominees}
            setElectionNominees={setElectionNominees}
            electionBallotVotes={electionBallotVotes}
            setElectionBallotVotes={setElectionBallotVotes}
            electionWriteInCandidates={electionWriteInCandidates}
            setElectionWriteInCandidates={setElectionWriteInCandidates}
            electionInvalidBallots={electionInvalidBallots}
            setElectionInvalidBallots={setElectionInvalidBallots}
            onViewChange={handleViewChange}
            remoteVoteSession={remoteVoteSession}
            remoteVoteTally={remoteVoteTally}
            remoteVoteBusy={remoteVoteBusy}
            remoteVoteError={remoteVoteError}
            onOpenRemoteElectionBallot={
              meetingSetup.enableQrDeviceVoting && voteRoomId && hasSupabaseConfig()
                ? handleOpenRemoteElectionBallot
                : null
            }
            onCloseRemoteVote={
              meetingSetup.enableQrDeviceVoting && voteRoomId && hasSupabaseConfig()
                ? handleCloseRemoteVote
                : null
            }
            onUseRemoteElectionTally={
              meetingSetup.enableQrDeviceVoting && voteRoomId && hasSupabaseConfig()
                ? handleUseRemoteElectionTally
                : null
            }
          />
        )}
        
        {(currentView === 'observer' || document.body.classList.contains('observer-window')) && (
          <div className="observer-content">
            <ObserverView
              theme={globalTheme}
              meetingSetup={meetingSetup}
              currentMotion={currentMotion}
              motions={motions}
              agendaEntries={meetingMinutes}
              currentAgendaIndex={currentAgendaIndex}
              motionTypes={motionTypes}
              speakerQueue={speakerQueue}
              speakerTimer={speakerTime}
              isTimerRunning={Boolean(currentSpeaker)}
              meetingTimer={meetingTimer}
              motionStack={motionStack}
              currentSpeaker={currentSpeaker}
              currentElection={currentElection}
              candidateSpeechQueue={candidateSpeechQueue}
              currentCandidate={currentCandidate}
              inElectionMode={inElectionMode}
              currentView={chairNavView}
              electionScreen={electionScreen}
              electionNominees={electionNominees}
              electionBallotVotes={electionBallotVotes}
              electionWriteInCandidates={electionWriteInCandidates}
              electionInvalidBallots={electionInvalidBallots}
              unanimousConsentRequests={unanimousConsentRequests}
              pendingUCRequest={pendingUCRequest}
              showMotionCharacteristics={showObserverMotionCharacteristics}
              voteJoinCode={meetingSetup.enableQrDeviceVoting ? voteJoinCode : null}
              layoutConfig={observerLayoutConfig}
              onLayoutChange={updateObserverLayout}
              onToggleEditMode={toggleObserverEditMode}
              onToggleCard={toggleObserverCard}
              onResetLayout={resetObserverLayout}
              canEditLayout={skipAuth || canAdmin}
              onConsentSuggestRemoval={(lineId, text) => {
                setMeetingSetup((prev) => ({
                  ...prev,
                  consentRemovalSuggestions: [
                    ...(prev.consentRemovalSuggestions || []),
                    {
                      id: `obs-inline-${Date.now()}`,
                      lineId,
                      text: text || '',
                      suggestedAt: new Date().toLocaleTimeString(),
                    },
                  ],
                }));
              }}
            />
          </div>
        )}

        {(currentView === 'chair-prompt' || document.body.classList.contains('chair-prompt-window')) && (
          <ChairPromptView
            theme={globalTheme}
            meetingSetup={meetingSetup}
            chairScript={chairScript}
            chairScriptRefining={chairScriptRefining}
            chairScriptSource={chairScriptSource}
            currentMotion={currentMotion}
            motionTypes={motionTypes}
            currentAgendaIndex={currentAgendaIndex}
            currentSpeaker={currentSpeaker}
            speakerQueue={speakerQueue}
            speakerTime={speakerTime}
            speakerTimeLimit={
              inElectionMode
                ? (currentElection?.speechTimeLimit || candidateSpeechTimeLimit || 3) * 60
                : globalSpeakerTimeLimit
            }
            meetingTimer={meetingTimer}
            pendingUCRequest={pendingUCRequest}
            inElectionMode={inElectionMode}
            electionScreen={electionScreen}
            currentElection={currentElection}
            electionNominees={electionNominees}
            currentCandidate={currentCandidate}
            candidateSpeechQueue={candidateSpeechQueue}
            onSignalReady={() => sendChairSignalFromPrompt('ready')}
            onSignalHold={() => sendChairSignalFromPrompt('hold')}
            onTakeControl={handleChairTakeControl}
            onReturnControl={handleChairReturnControl}
            onAddChairNote={submitChairNoteFromDashboard}
            onSendChairMessage={sendChairMessageToDriver}
            driverSeat={driverSeat}
            lastChairSignal={chairSignal}
            driverMessage={driverMessageToChair}
            onDismissDriverMessage={() => setDriverMessageToChair(null)}
            electionBallotVotes={electionBallotVotes}
            electionWriteInCandidates={electionWriteInCandidates}
            isPopup={Boolean(typeof window !== 'undefined' && window.opener && !window.opener.closed)}
            orgId={orgIdFromRoute || activeOrganizationId}
          />
        )}
      </div>

      {/* Status Bar */}
      {!document.body.classList.contains('chair-prompt-window') && (
      <div className="status-bar fixed bottom-0 left-0 right-0 border-t border-white/10 bg-pp-ink text-pp-paper/85 shadow-[0_-4px_24px_rgba(0,0,0,0.12)]">
        <div className="mx-auto max-w-[1600px] px-4 py-2.5 sm:px-6">
          <div className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex items-center gap-4">
              <span className="text-pp-paper/80">
                {meetingSetup.presentMembers > 0 && (
                  <>
                    Present: {meetingSetup.presentMembers}/{meetingSetup.totalMembers} • 
                    Quorum: {getQuorumStatus(meetingSetup).present ? '✓ Present' : '⚠ Not Present'}
                  </>
                )}
              </span>
            </div>
            <div className="flex min-w-0 items-center gap-4">
              <span className="truncate text-pp-paper/70">
                Motions: {motions.length} • Committees: {committees.length} • Elections: {elections.length}
                {currentMotion && ` • Current: ${currentMotion.text.substring(0, 40)}${currentMotion.text.length > 40 ? '…' : ''}`}
              </span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Amend text confirm modal */}
      {amendTextConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-pp-ink">Main motion as amended</h2>
            <p className="mt-1 text-sm text-pp-muted">
              {amendTextConfirm.loading
                ? 'Working out the updated wording…'
                : amendTextConfirm.summary ||
                  'Confirm the operative text of the main motion after this amendment.'}
            </p>
            {amendTextConfirm.amendmentMotion?.text && (
              <p className="mt-3 rounded-lg border border-pp-line bg-pp-paper px-3 py-2 text-xs text-pp-muted">
                <span className="font-semibold text-pp-ink">Amendment: </span>
                {amendTextConfirm.amendmentMotion.text}
              </p>
            )}
            <label className="mt-4 block text-xs font-medium text-pp-muted">
              Operative main motion text
              <textarea
                className="mt-1 w-full rounded-lg border border-pp-line px-3 py-2 text-sm text-pp-ink"
                rows={5}
                disabled={amendTextConfirm.loading}
                value={amendConfirmDraft}
                onChange={(e) => setAmendConfirmDraft(e.target.value)}
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                disabled={amendTextConfirm.loading || !String(amendConfirmDraft || '').trim()}
                onClick={confirmAmendText}
                className="rounded-xl bg-pp-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {amendTextConfirm.loading ? 'Please wait…' : 'Confirm wording'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">Settings</h2>
                <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Meeting</h3>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowSettings(false);
                        setShowQuorumAdjust(true);
                      }}
                      className="w-full text-left px-3 py-2 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Adjust attendance / quorum…
                    </button>
                    {isRollCallEnabledFor(meetingSetup) && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowSettings(false);
                          setShowRollCallModal(true);
                        }}
                        className="w-full text-left px-3 py-2 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Open roll call roster…
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={startNewMeetingSession}
                      className="w-full text-left px-3 py-2 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Start new meeting…
                    </button>
                    <p className="text-xs text-gray-500">
                      Organization defaults (agenda template, UC presets, parliamentary mode) live in
                      Organization settings.
                    </p>
                  </div>
                </div>

                <div data-tour="settings-session-mode">
                  <h3 className="text-lg font-semibold mb-3">Who is driving?</h3>
                  <p className="mb-3 text-xs text-gray-500">
                    Solo chair keeps the full console. Secretary-assisted: this device is the Driver;
                    open Chair Dashboard for the chair.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label
                      className={`cursor-pointer rounded-xl border p-3 ${
                        (meetingSetup.sessionMode || 'solo') === 'solo'
                          ? 'border-pp-accent bg-pp-accentSoft/40 ring-1 ring-pp-accent/30'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="settingsSessionMode"
                        className="sr-only"
                        checked={(meetingSetup.sessionMode || 'solo') === 'solo'}
                        onChange={() => setMeetingSetup({ ...meetingSetup, sessionMode: 'solo' })}
                      />
                      <span className="block text-sm font-semibold text-gray-900">I&apos;m chairing alone</span>
                      <span className="mt-1 block text-xs text-gray-600">
                        Full console on this device.
                      </span>
                    </label>
                    <label
                      className={`cursor-pointer rounded-xl border p-3 ${
                        meetingSetup.sessionMode === 'assisted'
                          ? 'border-pp-accent bg-pp-accentSoft/40 ring-1 ring-pp-accent/30'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="settingsSessionMode"
                        className="sr-only"
                        checked={meetingSetup.sessionMode === 'assisted'}
                        onChange={() => setMeetingSetup({ ...meetingSetup, sessionMode: 'assisted' })}
                      />
                      <span className="block text-sm font-semibold text-gray-900">Secretary will drive</span>
                      <span className="mt-1 block text-xs text-gray-600">
                        This device is the Driver console.
                      </span>
                    </label>
                  </div>
                </div>

                {hasSupabaseConfig() && (
                  <div data-tour="settings-qr-voting">
                    <h3 className="text-lg font-semibold mb-3">Phone / QR voting</h3>
                    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={Boolean(meetingSetup.enableQrDeviceVoting)}
                        onChange={(e) =>
                          setMeetingSetup({
                            ...meetingSetup,
                            enableQrDeviceVoting: e.target.checked,
                          })
                        }
                      />
                      <span>
                        <span className="text-sm font-medium text-gray-800">
                          Enable QR device voting
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-600">
                          Shows a join code and QR on the Observer screen so phones can cast Aye/No/Abstain
                          or election ballots when you open a remote vote. Chair still confirms tallies.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold mb-3">Appearance</h3>
                  <div className="space-y-3">

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">AI Chair Assistant</label>
                      <p className="text-sm text-gray-600">
                        The chair script is now powered by AI that automatically adapts to meeting context and parliamentary procedures.
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Provides contextually appropriate guidance based on current meeting state
                      </p>
                    </div>


                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="show-agenda-widget"
                        checked={showAgendaWidget}
                        onChange={(e) => setShowAgendaWidget(e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="show-agenda-widget" className="text-sm font-medium text-gray-700">
                        Show agenda widget
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Motion Display</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sort motions by</label>
                      <select
                        value={motionSortMode}
                        onChange={(e) => setMotionSortMode(e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="smart">Smart (context)</option>
                        <option value="by_class">Motion Class</option>
                        <option value="alphabetical">Alphabetical</option>
                        <option value="most_used">Most Used</option>
                        <option value="by_precedence">Precedence Order</option>
                        <option value="in_order_only">In order only</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="show-observer-motion-characteristics"
                        checked={showObserverMotionCharacteristics}
                        onChange={(e) => setShowObserverMotionCharacteristics(e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="show-observer-motion-characteristics" className="text-sm font-medium text-gray-700">
                        Show motion characteristics in observer view
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testing Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-red-600">Testing (Dev Only)</h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700 mb-3">
                    The following actions are for testing purposes only and will not be available in the final version.
                  </p>
                  <button
                    onClick={() => {
                      setShowSettings(false);
                      resetMeeting();
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
                  >
                    Reset Entire Meeting
                  </button>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Save Settings
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quorum Adjustment Modal */}
      {showQuorumAdjust && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">Adjust Attendance</h2>
                <button onClick={() => setShowQuorumAdjust(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Present Members</label>
                  <input
                    type="number"
                    value={meetingSetup.presentMembers}
                    onChange={(e) => setMeetingSetup({ ...meetingSetup, presentMembers: parseInt(e.target.value) || 0 })}
                    className="w-full p-2 border rounded"
                    min="0"
                    max={meetingSetup.totalMembers}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Current Status: {getQuorumStatus(meetingSetup).text}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setShowQuorumAdjust(false)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Update
                </button>
                <button
                  onClick={() => setShowQuorumAdjust(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <RollCallModal
        open={showRollCallModal}
        roster={orgRoster}
        initialPresentIds={
          Array.isArray(meetingSetup.rollCallAttendance)
            ? meetingSetup.rollCallAttendance.filter((a) => a.present).map((a) => a.rosterId)
            : null
        }
        onCancel={() => setShowRollCallModal(false)}
        onConfirm={handleRollCallConfirm}
      />

      {/* Committee Referral Dialog */}
      {showReferralDialog && referralMotion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">Refer to Committee</h2>
                <button 
                  onClick={() => {
                    setShowReferralDialog(false);
                    setReferralMotion(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Select which committee to refer the motion to:
                  </p>
                  <p className="text-sm font-medium text-gray-800 mb-4 p-2 bg-gray-50 rounded">
                    "{referralMotion.text}"
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Committee</label>
                  <select
                    value={creatingAdHocCommittee ? '__ad_hoc__' : selectedCommitteeForReferral?.id || ''}
                    onChange={(e) => {
                      if (e.target.value === '__ad_hoc__') {
                        setCreatingAdHocCommittee(true);
                        setSelectedCommitteeForReferral(null);
                        return;
                      }
                      setCreatingAdHocCommittee(false);
                      setAdHocCommitteeName('');
                      // Committee ids are UUIDs (Supabase) — compare as strings, not parseInt.
                      const committee = committees.find(c => String(c.id) === e.target.value);
                      setSelectedCommitteeForReferral(committee);
                    }}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select a committee...</option>
                    {committees.map(committee => (
                      <option key={committee.id} value={committee.id}>
                        {committee.name}
                      </option>
                    ))}
                    <option value="__ad_hoc__">+ Create ad hoc committee…</option>
                  </select>
                  {creatingAdHocCommittee && (
                    <input
                      type="text"
                      autoFocus
                      value={adHocCommitteeName}
                      onChange={(e) => setAdHocCommitteeName(e.target.value)}
                      placeholder="New committee name"
                      className="mt-2 w-full p-2 border rounded text-sm"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instructions (optional)</label>
                  <textarea
                    value={referralInstructions}
                    onChange={(e) => setReferralInstructions(e.target.value)}
                    className="w-full p-2 border rounded h-20 text-sm"
                    placeholder="Any specific instructions for the committee..."
                  />
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => void submitReferral()}
                  disabled={
                    adHocCommitteeBusy ||
                    (!selectedCommitteeForReferral && !(creatingAdHocCommittee && adHocCommitteeName.trim()))
                  }
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {adHocCommitteeBusy ? 'Creating committee…' : 'Refer to Committee'}
                </button>
                <button
                  onClick={() => {
                    setShowReferralDialog(false);
                    setReferralMotion(null);
                    setSelectedCommitteeForReferral(null);
                    setReferralInstructions('');
                    setCreatingAdHocCommittee(false);
                    setAdHocCommitteeName('');
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Meeting Interface Component
const MeetingInterface = ({
  theme, meetingSetup, setMeetingSetup, motions, setMotions, currentMotion, setCurrentMotion,
  motionStack, setMotionStack, meetingMinutes, setMeetingMinutes, addMinuteEntry, updateMinuteEntry, onAddAgendaEntry, onExportMinutes, committees, onReferMotion,
  speakerQueue, setSpeakerQueue, currentSpeaker, setCurrentSpeaker, speakerTime, setSpeakerTime,
  speakerTimeLimit, setSpeakerTimeLimit, showSetupWizard, setShowSetupWizard, onCompleteMeetingSetup, onTourSafeDismissMeetingSetup, orgId = null, parliamentaryMode, motionSortMode,
  referralMotion, setReferralMotion, showReferralDialog, setShowReferralDialog,
  unanimousConsentRequests, motionTypes, showAgendaWidget, setShowAgendaWidget,
  currentAgendaIndex, setCurrentAgendaIndex, getAgendaDisplayText, advanceAgenda, getCurrentAgendaItem,
  handleUnanimousConsent, handleUCGrant, handleUCObjection, pendingUCRequest,
  tabledMotions, setTabledMotions, postponedMotions, setPostponedMotions, referredMotions, setReferredMotions,
  chairScript,
  chairScriptRefining = false,
  chairScriptSource = 'template',
  pullConsentLineToFloor,
  adoptConsentAgenda,
  submitMainMotionFromConsentAgenda,
  dismissConsentSuggestion,
  meetingStartTime,
  requestAmendTextConfirm,
  remoteVoteSession = null,
  remoteVoteTally = null,
  remoteVoteBusy = false,
  remoteVoteError = null,
  onOpenRemoteVote = null,
  onCloseRemoteVote = null,
  setMeetingEndTime = null,
  setIsTimerRunning = null,
  promptFinalizeAfterAdjourn = null,
}) => {
  const [meetingConsentLinesOpen, setMeetingConsentLinesOpen] = useState(true);
  const [selectedMotionType, setSelectedMotionType] = useState(null);
  const [motionText, setMotionText] = useState('');
  const [memberName, setMemberName] = useState('');
  const [selectedCommitteeForReferral, setSelectedCommitteeForReferral] = useState(null);
  const [referralInstructions, setReferralInstructions] = useState('');
  const [motionUsageCount, setMotionUsageCount] = useState({});
  const [showChairNoteModal, setShowChairNoteModal] = useState(false);
  const [chairNoteDraft, setChairNoteDraft] = useState('');
  const pausedSpeakerRef = useRef(null);

  // Guided tour: close setup wizard so console controls are visible
  useEffect(() => {
    const dismissWizard = (event) => {
      if (event?.detail?.tourSafe) {
        if (typeof onTourSafeDismissMeetingSetup === 'function') {
          onTourSafeDismissMeetingSetup();
        } else {
          setShowSetupWizard?.(false);
        }
        return;
      }
      if (typeof onCompleteMeetingSetup === 'function') {
        onCompleteMeetingSetup();
      } else {
        setShowSetupWizard?.(false);
      }
    };
    window.addEventListener('pp-tour-dismiss-meeting-wizard', dismissWizard);
    return () => window.removeEventListener('pp-tour-dismiss-meeting-wizard', dismissWizard);
  }, [onCompleteMeetingSetup, onTourSafeDismissMeetingSetup, setShowSetupWizard]);

  const chairNoteSuggestions = React.useMemo(() => {
    const currentAgendaItem =
      currentAgendaIndex >= (meetingSetup.orderOfBusiness?.length ?? 0)
        ? null
        : meetingSetup.orderOfBusiness[currentAgendaIndex];
    const quorumStatus = getQuorumStatus(meetingSetup);
    return buildChairNoteSuggestions(
      {
        currentMotion,
        motionStack,
        motions,
        currentSpeaker,
        recognizedMember: null,
        recentActions: [],
        speakerQueue,
        meetingSetup,
        pendingUCRequest,
        currentAgendaItem,
        quorumStatus,
        tabledMotions,
        postponedMotions,
        isQuorumPresent: quorumStatus.present,
        meetingStartTime,
      },
      motionTypes
    );
  }, [
    currentMotion,
    motionStack,
    motions,
    currentSpeaker,
    speakerQueue,
    meetingSetup,
    pendingUCRequest,
    currentAgendaIndex,
    tabledMotions,
    postponedMotions,
    meetingStartTime,
    motionTypes,
  ]);

  const submitChairNote = useCallback(() => {
    const text = chairNoteDraft.trim();
    if (!text) return;
    addMinuteEntry({
      id: `chair-note-${Date.now()}`,
      type: 'chair_note',
      text,
      status: 'completed',
      result: null,
    });
    setChairNoteDraft('');
    setShowChairNoteModal(false);
  }, [chairNoteDraft, addMinuteEntry]);

  const cardClass =
    theme === 'dark'
      ? 'bg-gray-800 text-white border border-gray-600'
      : 'bg-pp-surface text-pp-ink border border-pp-line/80';
  const inputClass =
    theme === 'dark' ? 'bg-gray-700 text-white border-gray-600' : 'border-pp-line bg-pp-surface text-pp-ink';

  const transitionMotionStatus = (motion, event, tallyResult = null) => {
    if (!motion) return { ok: false, reason: 'No motion to transition' };

    const next = getNextLegacyStatus(motion.status, event, motion.type);
    if (!next.ok) return next;

    // Special-case: legacy "tally" is encoded as voted_passed/voted_failed
    if (next.nextStatus === '__TALLY__') {
      if (tallyResult !== 'passed' && tallyResult !== 'failed') {
        return { ok: false, reason: 'Tally requires result: passed|failed' };
      }
      return {
        ok: true,
        updatedMotion: {
          ...motion,
          status: tallyResult === 'passed' ? 'voted_passed' : 'voted_failed',
          voteTime: new Date().toLocaleTimeString()
        }
      };
    }

    return { ok: true, updatedMotion: { ...motion, status: next.nextStatus } };
  };

  // Handler for taking motion from table
  const handleTakeFromTable = (tabledMotionId) => {
    const currentMeetingState = {
      tabledMotions,
      meetingMinutes
    };

    const updatedState = takeFromTable(tabledMotionId, currentMeetingState);

    if (updatedState !== currentMeetingState) {
      setCurrentMotion(updatedState.currentMotion);
      setMotionStack(updatedState.motionStack);
      setTabledMotions(updatedState.tabledMotions);
      setMeetingMinutes(updatedState.meetingMinutes);

      // Add the restored motion to the motions array so it can be updated during progression
      if (updatedState.currentMotion) {
        setMotions(prev => [...prev, updatedState.currentMotion]);
      }
    }
  };

  const submitMotion = () => {
    if (!selectedMotionType || !memberName) {
      alert('Please enter member name.');
      return;
    }

    if (!isMotionInOrder(selectedMotionType, currentMotion, motionStack)) {
      const applied = canApplyMotion(selectedMotionType, currentMotion, motionStack);
      alert(applied.reason || 'That motion is not in order under the current parliamentary state.');
      return;
    }

    const motionType = motionTypes[selectedMotionType];
    const isSubsidiaryMotion = motionType.class === 'Subsidiary';
    const isIncidentalMotion = motionType.class === 'Incidental';
    const parentMotionId =
      (isSubsidiaryMotion || isIncidentalMotion) && currentMotion ? currentMotion.id : null;
    const skipsSecond = motionType.needsSecond === false;
    // INFO/CHAIR-flow motions normally skip straight past seconding (points of
    // order, inquiries, etc. never need one) — but Appeal is the one CHAIR-flow
    // motion that genuinely does require a real second, so it must not be swept
    // into this shortcut.
    const isImmediateFlow = (motionType.flowType === 'INFO' || motionType.flowType === 'CHAIR') && skipsSecond;

    const motion = {
      id: Date.now(),
      type: selectedMotionType,
      text: motionText || motionTypes[selectedMotionType].name,
      member: memberName,
      seconded: skipsSecond,
      // A point of order is raised and ruled on immediately — there's no
      // assembly debate/vote step to walk through first.
      status: selectedMotionType === 'POINT_ORDER'
        ? 'ready_for_vote'
        : isImmediateFlow ? 'seconded' : (skipsSecond ? 'seconded' : 'pending_second'),
      timestamp: new Date().toLocaleTimeString(),
      parentMotionId: parentMotionId,
      // For amendments and committee referrals, track which motion this is affecting
      ...((selectedMotionType === 'AMEND' || selectedMotionType === 'COMMIT') && currentMotion && { targetMotionId: currentMotion.id })
    };

    setMotions([...motions, motion]);
    setMotionUsageCount(prev => ({
      ...prev,
      [selectedMotionType]: (prev[selectedMotionType] || 0) + 1
    }));

    // Motion will be added to minutes only when disposed of (passed, failed, etc.)

    if (currentMotion) {
      // If making an amendment or interrupting with an incidental, pause the current motion.
      const shouldHold = selectedMotionType === 'AMEND' || isIncidentalMotion;
      const motionToStack = shouldHold ? { ...currentMotion, status: 'on_hold' } : currentMotion;

      setMotionStack([...motionStack, motionToStack]);

      if (shouldHold) {
        setMotions(prev => prev.map(m => (m.id === currentMotion.id ? motionToStack : m)));
      }

      // If this is an interrupt-style incidental, pause the speaker timer.
      if (isIncidentalMotion && currentSpeaker && pausedSpeakerRef.current == null) {
        pausedSpeakerRef.current = { speaker: currentSpeaker, time: speakerTime };
        setCurrentSpeaker(null);
      }
    }

    setCurrentMotion(motion);
    setSelectedMotionType(null);
    setMotionText('');
    setMemberName('');
  };

  const secondMotion = (secondedBy) => {
    if (currentMotion && !currentMotion.seconded) {
      const transitioned = transitionMotionStatus(currentMotion, MotionEvent.SECOND);
      if (!transitioned.ok) {
        console.warn(transitioned.reason);
        return;
      }
      const updatedMotion = { ...transitioned.updatedMotion, seconded: true, secondedBy };
      setCurrentMotion(updatedMotion);
      setMotions(motions.map(m => m.id === currentMotion.id ? updatedMotion : m));

      // Second will be noted in final motion disposition
    }
  };

  const handleNoSecond = () => {
    if (currentMotion && currentMotion.status === 'pending_second') {
      const killedId = currentMotion.id;
      const killedType = currentMotion.type;
      // Remove the motion from the motions array (kills it completely)
      setMotions(motions.filter(m => m.id !== killedId));

      // Remove any minute entries related to this motion
      setMeetingMinutes(prev => prev.filter(entry =>
        entry.motionData?.id !== killedId
      ));

      if (killedType === 'MAIN') {
        setMeetingSetup((prev) => ({
          ...prev,
          orderOfBusiness: prev.orderOfBusiness.map((row) =>
            row.introducedMotionId === killedId ? { ...row, introducedMotionId: null } : row
          ),
        }));
      }

      // Clear current motion
      setCurrentMotion(null);

      // Restore previous motion from stack if there was one
      if (motionStack.length > 0) {
        const previousMotion = motionStack[motionStack.length - 1];
        setCurrentMotion(previousMotion);
        setMotionStack(motionStack.slice(0, -1));
      }
    }
  };

  const stateMotion = () => {
    if (currentMotion && currentMotion.status === 'seconded') {
      const motionType = motionTypes[currentMotion.type];

      const transitioned = transitionMotionStatus(currentMotion, MotionEvent.STATE);
      if (!transitioned.ok) {
        console.warn(transitioned.reason);
        return;
      }
      const updatedMotion = transitioned.updatedMotion;
      setCurrentMotion(updatedMotion);
      setMotions(motions.map(m => m.id === currentMotion.id ? updatedMotion : m));

      // Only create minute entries for non-amendment motions
      // Amendments will be recorded when they're completed and affect the main motion
      if (currentMotion.type !== 'AMEND') {
        const secondedByText = currentMotion.secondedBy ? ` (seconded by ${currentMotion.secondedBy})` : '';

        addMinuteEntry({
          id: `motion_${currentMotion.id}`, // Fixed ID for later updates
          text: formatStatedMotionMinuteLine(updatedMotion, motionTypes),
          type: updatedMotion.status === 'ready_for_vote' ? 'motion_ready_for_vote' : 'motion_stated',
          status: 'pending',
          motionData: updatedMotion,
          parentMotionId: currentMotion.parentMotionId
        });
      }
    }
  };

  const voteOnMotion = (result) => {
    if (!currentMotion) return;

    // Auto-close remote motion vote when chair records a result
    if (remoteVoteSession?.status === 'open' && remoteVoteSession?.kind === 'motion') {
      void onCloseRemoteVote?.();
    }

    // Special handling for committee referral motions
    if (currentMotion.type === 'COMMIT' && result === 'passed') {
      // Show referral dialog for committee selection
      setReferralMotion(currentMotion);
      setShowReferralDialog(true);
      return;
    }

    // Voice-vote tie: no count was taken, but the chair could tell it was even.
    // The chair's own vote (or decline) directly decides the result.
    if (typeof result === 'object' && result !== null && result.voiceTie) {
      const chairVote = result.chairTieVote;
      const finalResult = chairVote === 'yes' ? 'passed' : 'failed';

      if (currentMotion.type === 'COMMIT' && finalResult === 'passed') {
        setReferralMotion(currentMotion);
        setShowReferralDialog(true);
        return;
      }

      const transitioned = transitionMotionStatus(
        {
          ...currentMotion,
          vote: {
            method: 'voice_tie',
            chairBrokeTie: chairVote !== 'decline',
            chairTieVote: chairVote,
            talliedAt: new Date().toLocaleTimeString(),
          },
        },
        MotionEvent.TALLY,
        finalResult
      );
      if (!transitioned.ok) {
        console.warn(transitioned.reason);
        return;
      }
      const updatedMotion = transitioned.updatedMotion;
      setMotions(motions.map((m) => (m.id === currentMotion.id ? updatedMotion : m)));
      setCurrentMotion(updatedMotion);
      return;
    }

    // New tally payload: { yes, no, abstain, chairBrokeTie?, chairTieVote? }
    if (typeof result === 'object' && result !== null) {
      const yes = Number(result.yes || 0);
      const no = Number(result.no || 0);
      const abstain = Number(result.abstain || 0);

      const totalMembership = meetingSetup?.totalMembers || null;
      const analysis = analyzeVoteResult(currentMotion.type, yes, no, totalMembership);

      const transitioned = transitionMotionStatus(
        {
          ...currentMotion,
          vote: {
            method: 'tally',
            yes,
            no,
            abstain,
            votesCast: yes + no,
            totalBallots: yes + no + abstain,
            threshold: analysis.threshold,
            requiredVotes: analysis.requiredVotes,
            passes: analysis.passes,
            percentage: analysis.percentage,
            description: analysis.description,
            talliedAt: new Date().toLocaleTimeString(),
            ...(result.chairBrokeTie
              ? { chairBrokeTie: true, chairTieVote: result.chairTieVote }
              : {})
          }
        },
        MotionEvent.TALLY,
        analysis.passes ? 'passed' : 'failed'
      );

      if (!transitioned.ok) {
        console.warn(transitioned.reason);
        return;
      }

      const updatedMotion = transitioned.updatedMotion;
      setMotions(motions.map(m => m.id === currentMotion.id ? updatedMotion : m));
      setCurrentMotion(updatedMotion);

      if (currentMotion.type === 'COMMIT' && analysis.passes) {
        setReferralMotion(updatedMotion);
        setShowReferralDialog(true);
      }
      return;
    }

    const transitioned = transitionMotionStatus(currentMotion, MotionEvent.TALLY, result);
    if (!transitioned.ok) {
      console.warn(transitioned.reason);
      return;
    }
    const updatedMotion = transitioned.updatedMotion;

    setMotions(motions.map(m => m.id === currentMotion.id ? updatedMotion : m));
    setCurrentMotion(updatedMotion);

    // Vote result will be captured in announceResult
  };

  // RONR: the chair may submit a point of order to a vote of the assembly
  // instead of ruling on it personally.
  const putPointOrderToAssembly = () => {
    if (!currentMotion || currentMotion.type !== 'POINT_ORDER') return;
    setCurrentMotion({ ...currentMotion, chairPutToAssembly: true });
  };

  // Helper function to generate the final motion text for minutes
  const generateFinalMotionText = (motion, motionType, secondedByText, finalResult) => {
    if (motionType?.flowType === 'INFO' && motion.type === 'PARL_INQUIRY') {
      return `A parliamentary inquiry was raised: "${motion.text}". The chair responded.`;
    }
    if (motionType?.flowType === 'CHAIR' && motion.type === 'POINT_ORDER') {
      return `A point of order was raised: "${motion.text}". The chair ruled the point ${
        finalResult === 'passed' ? 'well taken' : 'not well taken'
      }.`;
    }
    if (motionType?.flowType === 'CHAIR' && motion.type === 'APPEAL') {
      return `An appeal was taken from the decision of the chair. The decision was ${
        finalResult === 'passed' ? 'sustained' : 'overruled'
      }.`;
    }
    if (motion.type === 'MAIN') {
      const passedAmendments = (motion.amendments || []).filter((a) => a.result === 'passed');
      const originalText = motion.originalText;
      const finalText = motion.text;
      const hadGermaneAmendment =
        passedAmendments.length > 0 &&
        originalText &&
        finalText &&
        originalText !== finalText;

      if (hadGermaneAmendment) {
        return `${motion.member} moved main motion to read originally: "${originalText}"${secondedByText}. As amended, the question is: "${finalText}". The motion ${finalResult}.`;
      }

      if (passedAmendments.length > 0 && finalText) {
        return `${motion.member} moved main motion${secondedByText}. The question as amended: "${finalText}". The motion ${finalResult}.`;
      }

      let result = `${motion.member} moved ${motionType.name.toLowerCase()}`;
      if (finalText && finalText !== motionType.name) {
        result += `: "${finalText}"`;
      }
      result += `${secondedByText}. Motion ${finalResult}.`;
      return result;
    }

    // For amendments, show proper amendment language
    if (motion.type === 'AMEND') {
      return formatAmendmentDispositionText(motion, `Amendment ${finalResult}.`);
    }

    // For other motions, use the current approach
    return `${motion.member} moved ${motionType.name.toLowerCase()}${motion.text && motion.text !== motionType.name ? `: "${motion.text}"` : ''}${secondedByText}. Motion ${finalResult}.`;
  };

  const announceResult = useCallback(() => {
    if (!currentMotion) return;

    /** Fresh main after amendment announce — avoids stale `motions` closure when restoring */
    let amendmentRestoredMain = null;

    const finalResult = currentMotion.status === 'voted_passed' ? 'passed' : 'failed';
    const transitioned = transitionMotionStatus(currentMotion, MotionEvent.ANNOUNCE);
    if (!transitioned.ok) {
      console.warn(transitioned.reason);
      return;
    }
    const updatedMotion = {
      ...transitioned.updatedMotion,
      result: finalResult
    };

    // When completing a motion, make sure we preserve amendment information
    const finalMotionUpdate = currentMotion.type === 'MAIN' && currentMotion.isAmended
      ? { ...updatedMotion, isAmended: true, amendments: currentMotion.amendments, originalText: currentMotion.originalText }
      : updatedMotion;

    setMotions(motions.map(m => m.id === currentMotion.id ? finalMotionUpdate : m));

    // Update existing minute entry with final result (effects need this snapshot — see below)
    const motionType = motionTypes[currentMotion.type];
    const secondedByText = currentMotion.secondedBy ? ` (seconded by ${currentMotion.secondedBy})` : '';
    const tieBreakSuffix = currentMotion.vote?.chairBrokeTie
      ? ` (Tied vote; the chair voted ${currentMotion.vote.chairTieVote === 'yes' ? 'aye' : 'no'} to break the tie.)`
      : '';

    let minutesForEffects = meetingMinutes;

    if (currentMotion.type === 'AMEND') {
      addMinuteEntry({
        text: formatAmendmentDispositionText(currentMotion, `Amendment ${finalResult}.`) + tieBreakSuffix,
        type: 'amendment_result',
        status: 'completed',
        result: finalResult,
        motionData: finalMotionUpdate,
        parentMotionId: currentMotion.parentMotionId
      });
    } else if (currentMotion.type === 'MAIN') {
      // Keep the stated intro line in place; append a separate disposition line so
      // intervening amendments stay chronologically between make/state and adoption.
      const dispositionText = formatDispositionMinuteLine(
        finalMotionUpdate,
        motionTypes,
        finalResult
      ) + tieBreakSuffix;
      const dispositionEntry = {
        id: `motion_${currentMotion.id}_result`,
        text: dispositionText,
        timestamp: new Date().toLocaleTimeString(),
        chronologicalOrder: Date.now(),
        type: 'motion_completed',
        status: 'completed',
        result: finalResult,
        motionData: finalMotionUpdate,
        parentMotionId: currentMotion.parentMotionId,
        lastUpdated: Date.now(),
      };
      minutesForEffects = [...meetingMinutes, dispositionEntry];
    } else {
      const entryId = `motion_${currentMotion.id}`;
      // Subsidiary / incidental: update stated line in place without reordering.
      minutesForEffects = meetingMinutes.map((entry) =>
        entry.id === entryId
          ? {
              ...entry,
              text: generateFinalMotionText(currentMotion, motionType, secondedByText, finalResult) + tieBreakSuffix,
              type: 'motion_completed',
              status: 'completed',
              result: finalResult,
              motionData: finalMotionUpdate,
              lastUpdated: Date.now(),
            }
          : entry
      );
      const entryFound = minutesForEffects.some((entry) => entry.id === entryId);
      if (!entryFound) {
        console.warn(`Could not find minute entry with ID: ${entryId}. Motion may be restored from table.`);
        minutesForEffects = [
          ...meetingMinutes,
          {
            id: `${entryId}_result`,
            text: generateFinalMotionText(currentMotion, motionType, secondedByText, finalResult) + tieBreakSuffix,
            timestamp: new Date().toLocaleTimeString(),
            chronologicalOrder: Date.now(),
            type: 'motion_completed',
            status: 'completed',
            result: finalResult,
            motionData: finalMotionUpdate,
            parentMotionId: currentMotion.parentMotionId,
            lastUpdated: Date.now(),
          },
        ];
      }
    }

    // Special handling for amendments
    if (currentMotion.type === 'AMEND') {
      if (motionStack.length > 0) {
        // LIFO: whatever this amendment was pending on top of is its real parent — the
        // main motion for a primary amendment, or a still-pending primary amendment for
        // a secondary (amendment-to-the-amendment) one. Not necessarily type 'MAIN'.
        const mainMotionIndex = motionStack.length - 1;
        const mainMotion = motionStack[mainMotionIndex];

        if (finalResult === 'passed') {
          // Dispose amendment; ask chair to confirm operative parent text (AI / deterministic / manual).
          setMotions((prev) =>
            prev.map((m) => (m.id === currentMotion.id ? finalMotionUpdate : m))
          );
          setMotionStack(motionStack.slice(0, -1));
          if (typeof requestAmendTextConfirm === 'function') {
            void requestAmendTextConfirm({
              mainMotion,
              amendmentMotion: finalMotionUpdate,
              result: 'passed',
            });
          } else {
            const updatedMainMotion = applyPassedAmendmentToMain(
              mainMotion,
              currentMotion,
              mainMotion.text
            );
            setMotions((prev) =>
              prev.map((m) => (m.id === mainMotion.id ? updatedMainMotion : m))
            );
            setCurrentMotion(updatedMainMotion);
          }
          return;
        }

        // Amendment failed - parent motion text remains unchanged
        const updatedMainMotion = {
          ...mainMotion,
          status: 'open_debate',
          amendments: [
            ...(mainMotion.amendments || []),
            {
              text: currentMotion.text,
              member: currentMotion.member,
              timestamp: new Date().toLocaleTimeString(),
              result: 'failed',
            },
          ],
        };

        setMotions((prev) =>
          prev.map((m) => (m.id === mainMotion.id ? updatedMainMotion : m))
        );

        const updatedStack = [...motionStack];
        updatedStack[mainMotionIndex] = updatedMainMotion;
        setMotionStack(updatedStack);

        amendmentRestoredMain = updatedMainMotion;
      }
    }

    // Process motion effects for subsidiary motions (Previous Question, Table, etc.)
    // Skip motion effects for main motions - they don't have automatic effects
    if (finalResult === 'passed' && currentMotion.type !== 'MAIN') {
      console.log(`🔨 Motion passed: ${currentMotion.type} - checking for automatic effects`);

      const currentMeetingState = {
        currentMotion,
        motionStack,
        meetingMinutes: minutesForEffects,
        tabledMotions, // eslint-disable-line
        postponedMotions, // eslint-disable-line
        referredMotions // eslint-disable-line
      };

      const updatedState = processMotionResult(currentMotion, finalResult, currentMeetingState);

      if (updatedState !== currentMeetingState) {
        if (updatedState.currentMotion !== currentMotion) {
          setCurrentMotion(updatedState.currentMotion);
        }
        if (updatedState.motionStack !== motionStack) {
          setMotionStack(updatedState.motionStack);
        }
        if (currentMotion.type !== 'AMEND') {
          setMeetingMinutes(updatedState.meetingMinutes);
        } else if (updatedState.meetingMinutes !== minutesForEffects) {
          setMeetingMinutes(updatedState.meetingMinutes);
        }
        if (updatedState.tabledMotions !== tabledMotions) { // eslint-disable-line
          setTabledMotions(updatedState.tabledMotions); // eslint-disable-line
        }
        if (updatedState.postponedMotions !== postponedMotions) { // eslint-disable-line
          setPostponedMotions(updatedState.postponedMotions); // eslint-disable-line
        }
        if (updatedState.referredMotions !== referredMotions) { // eslint-disable-line
          setReferredMotions(updatedState.referredMotions); // eslint-disable-line
        }

        if (updatedState.meetingAdjourned || currentMotion.type === 'ADJ') {
          const endIso = new Date().toISOString();
          if (typeof setMeetingEndTime === 'function') setMeetingEndTime(endIso);
          if (typeof setIsTimerRunning === 'function') setIsTimerRunning(false);
          setMeetingSetup((prev) => ({ ...prev, meetingEndTime: endIso }));
          const minutesAfter = updatedState.meetingMinutes || minutesForEffects;
          window.setTimeout(() => {
            if (typeof promptFinalizeAfterAdjourn === 'function') {
              promptFinalizeAfterAdjourn(minutesAfter, endIso);
            }
          }, 0);
        }

        // If business was disposed (Table, Postpone, Refer), exit early
        if (updatedState.advanceToNextBusiness) {
          console.log('📋 Business disposed of by subsidiary motion - returning to general business');
          return;
        }

        // If Previous Question passed, auto-advance to vote on underlying motion
        if (updatedState.autoAdvanceToVote) {
          console.log('🔨 Previous Question passed - auto-advancing to vote on underlying motion');

          // Find the underlying motion (should be in motion stack or current motion)
          const underlyingMotion = motionStack.find(m => m.type === 'MAIN') ||
                                 (currentMotion?.type === 'MAIN' ? currentMotion : null);

          if (underlyingMotion) {
            // Set the underlying motion to ready for vote and make it current
            const votableMotion = {
              ...underlyingMotion,
              status: 'ready_for_vote',
              debateClosed: true,
              debateClosedBy: 'previous_question',
              debateClosedAt: new Date().toLocaleTimeString()
            };

            // Update the motions array
            setMotions(prev => prev.map(m =>
              m.id === underlyingMotion.id ? votableMotion : m
            ));

            // Set it as current motion and clear the stack
            setCurrentMotion(votableMotion);
            setMotionStack([]);

            console.log('🗳️ Underlying motion advanced to vote:', votableMotion.text);
            return;
          } else {
            console.warn('Previous Question passed but no underlying motion found');
          }
        }
      } else if (currentMotion.type !== 'AMEND') {
        setMeetingMinutes(minutesForEffects);
      }
    } else if (currentMotion.type !== 'AMEND') {
      setMeetingMinutes(minutesForEffects);
    }

    console.log(`📋 announceResult: Motion stack length: ${motionStack.length}, clearing motion...`);

    if (motionStack.length > 0) {
      // Only restore to previous motion if there's actually a previous motion in stack
      const previousMotion = motionStack[motionStack.length - 1]; // -1 because that's the top of the stack
      // If returning to a main motion that was on hold, change status appropriately
      // For amendments, use the in-callback updated main (React state from setMotions is not visible in `motions` yet)
      let restoredMotion;
      if (currentMotion.type === 'AMEND' && amendmentRestoredMain) {
        restoredMotion = amendmentRestoredMain;
      } else if (currentMotion.type === 'AMEND') {
        const updatedMainMotion = motions.find(m => m.id === previousMotion.id);
        restoredMotion = updatedMainMotion
          ? { ...updatedMainMotion, status: 'open_debate' }
          : { ...previousMotion, status: 'open_debate' };
      } else if (previousMotion.status === 'on_hold') {
        restoredMotion = { ...previousMotion, status: 'open_debate' };
      } else {
        restoredMotion = previousMotion;
      }

      console.log(`📋 announceResult: Restoring to previous motion: ${restoredMotion.text}`);
      setCurrentMotion(restoredMotion);
      setMotionStack(motionStack.slice(0, -1));

      // Restore paused speaker after resolving interrupt-style incidentals.
      if (
        pausedSpeakerRef.current &&
        currentMotion &&
        motionTypes[currentMotion.type]?.class === 'Incidental' &&
        !currentSpeaker
      ) {
        setCurrentSpeaker(pausedSpeakerRef.current.speaker);
        setSpeakerTime(pausedSpeakerRef.current.time);
        pausedSpeakerRef.current = null;
      }
    } else {
      console.log(`📋 announceResult: Clearing current motion (no previous motion)`);
      setCurrentMotion(null);
      setMotionStack([]);

      if (
        pausedSpeakerRef.current &&
        currentMotion &&
        motionTypes[currentMotion.type]?.class === 'Incidental' &&
        !currentSpeaker
      ) {
        setCurrentSpeaker(pausedSpeakerRef.current.speaker);
        setSpeakerTime(pausedSpeakerRef.current.time);
        pausedSpeakerRef.current = null;
      }
    }
  }, [currentMotion, motionStack, meetingMinutes, motions, tabledMotions, postponedMotions, referredMotions, requestAmendTextConfirm]); // eslint-disable-line

  // TODO: Motion disposal and retrieval handlers


  const getSortedMotions = () => {
    const motionsList = Object.values(motionTypes);

    switch (motionSortMode) {
      case 'smart':
        return getMotionsBySmartContext(currentMotion, motionStack);

      case 'alphabetical': {
        const sorted = [...motionsList].sort((a, b) => a.name.localeCompare(b.name));
        return { 'All Motions': sorted };
      }

      case 'most_used': {
        const byUsage = [...motionsList].sort((a, b) =>
          (motionUsageCount[b.key] || 0) - (motionUsageCount[a.key] || 0)
        );
        return { 'Most Used': byUsage };
      }

      case 'by_precedence':
        return getMotionsByClass();

      case 'in_order_only': {
        const inOrderOnly = motionsList.filter((m) => isMotionInOrder(m.key, currentMotion, motionStack));
        return { 'In Order Now': inOrderOnly };
      }

      case 'by_class':
      default:
        return getMotionsByClass();
    }
  };

  const motionsByClass = getSortedMotions();

  return (
    <div className="py-3">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-pp-accent">
          Chair console
        </p>
        <span className="hidden text-pp-line sm:inline" aria-hidden>
          ·
        </span>
        <div className="flex items-center gap-1.5">
          <h2 className="text-base font-semibold tracking-tight text-pp-ink sm:text-lg">
            Floor and motions
          </h2>
          <ContextHelpTip
            text={FLOOR_AND_MOTIONS_SECTION_HINT}
            iconClassName="h-4 w-4 shrink-0"
          />
        </div>
      </div>

      <MotionBreadcrumbs
        theme={theme}
        currentMotion={currentMotion}
        motionStack={motionStack}
        motionTypes={motionTypes}
        motions={motions}
      />

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-12 xl:items-start">
        <aside className="flex min-h-0 flex-col xl:col-span-3 xl:h-[min(70vh,40rem)]" data-tour="motion-menu">
          <MotionMenu
            theme={theme}
            motionsByClass={motionsByClass}
            onMotionSelect={setSelectedMotionType}
            unanimousConsentRequests={unanimousConsentRequests}
            onUnanimousConsent={handleUnanimousConsent}
            currentMotion={currentMotion}
            motionStack={motionStack}
          />
        </aside>

        <main className="min-h-0 xl:col-span-5 xl:max-h-[min(70vh,40rem)] xl:overflow-y-auto">
          <MotionManagement
            theme={theme}
            selectedMotionType={selectedMotionType}
            motionTypes={motionTypes}
            memberName={memberName}
            setMemberName={setMemberName}
            motionText={motionText}
            setMotionText={setMotionText}
            onSubmitMotion={submitMotion}
            onCancelMotion={(isNoSecond) => {
              if (isNoSecond) {
                handleNoSecond();
              } else {
                setSelectedMotionType(null);
              }
            }}
            currentMotion={currentMotion}
            motionStack={motionStack}
            onSecondMotion={secondMotion}
            onStateMotion={stateMotion}
            onOpenDebate={() => {
              const transitioned = transitionMotionStatus(currentMotion, MotionEvent.OPEN_DEBATE);
              if (!transitioned.ok) {
                console.warn(transitioned.reason);
                return;
              }
              const updatedMotion = transitioned.updatedMotion;
              setCurrentMotion(updatedMotion);
              setMotions(motions.map(m => m.id === currentMotion.id ? updatedMotion : m));
            }}
            onCloseDebate={() => {
              console.log(`🚫 Close Debate: Motion ID ${currentMotion.id}, current status ${currentMotion.status}`);
              const transitioned = transitionMotionStatus(currentMotion, MotionEvent.CLOSE_DEBATE);
              if (!transitioned.ok) {
                console.warn(transitioned.reason);
                return;
              }
              const updatedMotion = transitioned.updatedMotion;
              setCurrentMotion(updatedMotion);

              const motionFound = motions.find(m => m.id === currentMotion.id);
              console.log(`🚫 Motion found in array: ${!!motionFound}`);

              setMotions(motions.map(m => m.id === currentMotion.id ? updatedMotion : m));
            }}
            onReadyForVote={() => {
              console.log(`🗳️ Ready for Vote: Motion ID ${currentMotion.id}, type ${currentMotion.type}`);
              const transitioned = transitionMotionStatus(currentMotion, MotionEvent.PUT);
              if (!transitioned.ok) {
                console.warn(transitioned.reason);
                return;
              }
              const updatedMotion = transitioned.updatedMotion;
              setCurrentMotion(updatedMotion);

              const motionFound = motions.find(m => m.id === currentMotion.id);
              console.log(`🗳️ Motion found in array: ${!!motionFound}`);

              setMotions(motions.map(m => m.id === currentMotion.id ? updatedMotion : m));
            }}
            onVoteOnMotion={voteOnMotion}
            onPutToAssembly={putPointOrderToAssembly}
            onAnnounceResult={announceResult}
            remoteVoteSession={remoteVoteSession}
            remoteVoteTally={remoteVoteTally}
            remoteVoteBusy={remoteVoteBusy}
            remoteVoteError={remoteVoteError}
            onOpenRemoteVote={onOpenRemoteVote}
            onCloseRemoteVote={onCloseRemoteVote}
            unanimousConsentRequests={unanimousConsentRequests}
            onUnanimousConsent={handleUnanimousConsent}
            pendingUCRequest={pendingUCRequest}
            onUCGrant={handleUCGrant}
            onUCObjection={handleUCObjection}
            meetingSetup={meetingSetup}
            showAgendaWidget={showAgendaWidget}
            currentAgendaIndex={currentAgendaIndex}
            orderOfBusiness={meetingSetup.orderOfBusiness}
            agendaDisplayText={getAgendaDisplayText()}
            onPrevious={() => setCurrentAgendaIndex(Math.max(0, currentAgendaIndex - 1))}
            onNext={() => setCurrentAgendaIndex(Math.min(meetingSetup.orderOfBusiness.length - 1, currentAgendaIndex + 1))}
            onAdvanceAgenda={advanceAgenda}
            onCloseAgendaWidget={() => setShowAgendaWidget(false)}
            canAdvance={(() => {
              const cur = getCurrentAgendaItem();
              if (!cur || cur.completed) return false;
              if (isConsentCalendarAgendaRow(cur)) return false;
              return true;
            })()}
            pullConsentLineToFloor={pullConsentLineToFloor}
            adoptConsentAgenda={adoptConsentAgenda}
            submitMainMotionFromConsentAgenda={submitMainMotionFromConsentAgenda}
            dismissConsentSuggestion={dismissConsentSuggestion}
            currentAgendaItem={getCurrentAgendaItem()}
          />
        </main>

        <aside className="flex min-h-0 flex-col gap-3 xl:col-span-4 xl:h-[min(70vh,40rem)] xl:overflow-y-auto">
          <div data-tour="chair-script">
            <ChairScript
              chairMessage={selectedMotionType === 'POINT_ORDER' ? 'State your point of order.' : chairScript}
              refining={selectedMotionType === 'POINT_ORDER' ? false : chairScriptRefining}
              source={selectedMotionType === 'POINT_ORDER' ? 'template' : chairScriptSource}
            />
          </div>

          <div
            data-tour="speakers-card"
            className={`${cardClass} flex min-h-0 flex-1 flex-col rounded-xl p-3 shadow-pp xl:max-h-[min(60vh,36rem)] xl:overflow-y-auto`}
          >
            <div className="mb-2">
              <h3 className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-pp-ink'}`}>
                Speakers
              </h3>
            </div>
            
            {/* Current Speaker Always Visible */}
            {currentSpeaker && (
              <div className="mb-2 rounded-lg border border-pp-sage/25 bg-pp-sageSoft p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-pp-sage">Speaking: {currentSpeaker}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      speakerTime <= 10 ? 'bg-red-100 text-red-800' : 
                      speakerTime <= 30 ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {Math.floor(speakerTime / 60)}:{(speakerTime % 60).toString().padStart(2, '0')}
                    </span>
                    <button
                      onClick={() => {
                        if (speakerQueue.length > 0) {
                          const nextSpeaker = speakerQueue[0];
                          setCurrentSpeaker(nextSpeaker);
                          setSpeakerTime(speakerTimeLimit);
                          setSpeakerQueue(speakerQueue.slice(1));
                        } else {
                          setCurrentSpeaker(null);
                          setSpeakerTime(0);
                        }
                      }}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                    >
                      {speakerQueue.length > 0 ? 'Next' : 'End'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
                {/* Timer Settings */}
                <div className="rounded bg-gray-50 p-2">
                  <label className="text-xs font-medium text-gray-600 mb-2 block">Speaking Time Limit</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={Math.floor(speakerTimeLimit / 60)}
                      onChange={(e) => {
                        const minutes = parseInt(e.target.value) || 0;
                        const seconds = speakerTimeLimit % 60;
                        setSpeakerTimeLimit(minutes * 60 + seconds);
                      }}
                      className="w-12 p-1 border rounded text-xs text-center"
                    />
                    <span className="text-xs">min</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={speakerTimeLimit % 60}
                      onChange={(e) => {
                        const minutes = Math.floor(speakerTimeLimit / 60);
                        const seconds = parseInt(e.target.value) || 0;
                        setSpeakerTimeLimit(minutes * 60 + seconds);
                      }}
                      className="w-12 p-1 border rounded text-xs text-center"
                    />
                    <span className="text-xs">sec</span>
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => setSpeakerTimeLimit(60)}
                        className="px-1 py-0.5 bg-gray-200 rounded text-xs hover:bg-gray-300"
                      >
                        1m
                      </button>
                      <button
                        onClick={() => setSpeakerTimeLimit(120)}
                        className="px-1 py-0.5 bg-gray-200 rounded text-xs hover:bg-gray-300"
                      >
                        2m
                      </button>
                      <button
                        onClick={() => setSpeakerTimeLimit(300)}
                        className="px-1 py-0.5 bg-gray-200 rounded text-xs hover:bg-gray-300"
                      >
                        5m
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Add Speaker */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Speaker name"
                    className="flex-1 p-2 border rounded text-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        setSpeakerQueue([...speakerQueue, e.target.value]);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={(e) => {
                      const input = e.target.parentElement.querySelector('input');
                      if (input.value) {
                        setSpeakerQueue([...speakerQueue, input.value]);
                        input.value = '';
                      }
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
                
                {/* Speaker Queue */}
                <div>
                  <h4 className="text-xs font-medium mb-2 text-gray-600">
                    Queue ({speakerQueue.length})
                  </h4>
                  {speakerQueue.length === 0 ? (
                    <p className="text-gray-500 text-xs">No speakers queued</p>
                  ) : (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {speakerQueue.map((speaker, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-gray-200 px-1 py-0.5 rounded font-medium">
                              {index + 1}
                            </span>
                            <span>{speaker}</span>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => {
                                if (!currentSpeaker) {
                                  setCurrentSpeaker(speaker);
                                  setSpeakerTime(speakerTimeLimit);
                                  setSpeakerQueue(speakerQueue.filter((_, i) => i !== index));
                                }
                              }}
                              disabled={currentSpeaker !== null}
                              className="px-1 py-0.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                            >
                              Start
                            </button>
                            <button
                              onClick={() => setSpeakerQueue(speakerQueue.filter((_, i) => i !== index))}
                              className="px-1 py-0.5 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
          </div>

          <MeetingAssistantCard
            organizationId={orgId}
            orgSettingsPath={orgId ? `/orgs/${orgId}/settings` : null}
            meetingSetup={meetingSetup}
            currentMotion={currentMotion}
            motionTypes={motionTypes}
            agendaTitle={getAgendaDisplayText?.() || getCurrentAgendaItem?.()?.title || null}
            theme={theme}
          />

        </aside>
      </div>

      {/* Live activity feed (export uses formal Minutes document only) */}
      <div className={`${cardClass} relative rounded-2xl p-5 shadow-pp`} data-tour="activity-feed">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1.5">
            <h2 className={`text-lg font-semibold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-pp-ink'}`}>
              Activity
            </h2>
            <ContextHelpTip
              text={ACTIVITY_FEED_HINT}
              theme={theme === 'dark' ? 'dark' : 'light'}
              iconClassName="h-5 w-5 shrink-0 text-pp-muted hover:text-pp-accent"
            />
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              data-tour="activity-chair-note"
              onClick={() => {
                setChairNoteDraft('');
                setShowChairNoteModal(true);
              }}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                theme === 'dark'
                  ? 'border-gray-500 bg-gray-800 text-white hover:bg-gray-700'
                  : 'border-pp-line bg-pp-surface text-pp-ink hover:bg-pp-paper'
              }`}
            >
              <StickyNote className="h-4 w-4 shrink-0" aria-hidden />
              Chair note
            </button>
          </div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto space-y-2">
          {meetingMinutes.length === 0 ? (
            <p className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              No activity recorded yet. Use the motion menu to get started.
            </p>
          ) : (
            <>
              {/* Hierarchical Minutes Display */}
              {(() => {
                const { groupedMinutes, disposedMainIds, topLevel } =
                  buildActivityMinuteGroups(meetingMinutes);

                // Render hierarchically
                const renderMinuteEntry = (entry, isNested = false) => {
                  const indentClass = isNested ? 'ml-6 border-l-2' : 'border-l-4';
                  // Not gated on isNested: a primary amendment's own entry is always
                  // rendered nested under the main motion, but still needs its own
                  // "Originally: …" subtext when a secondary amendment altered it.
                  const originalSubtext = getMinuteEntryOriginalSubtext(entry);

                  // Status-based styling for live feed
                  const getStatusStyling = () => {
                    if (entry.type === 'consent_batch_summary' || entry.type === 'consent_item_adopted') {
                      return 'bg-emerald-50 border-emerald-500';
                    }
                    if (entry.type === 'consent_agenda_pull') {
                      return 'bg-amber-50 border-amber-400';
                    }
                    if (entry.type === 'chair_note') {
                      return 'bg-sky-50 border-sky-400';
                    }
                    // Primary: Use result status if available
                    const result = entry.result || entry.motionData?.result;
                    if (result) {
                      if (result === 'passed' || result === 'passed_by_uc') {
                        return 'bg-green-50 border-green-400';
                      } else if (result === 'failed') {
                        return 'bg-red-50 border-red-400';
                      } else if (result === 'referred_to_committee') {
                        return 'bg-blue-50 border-blue-400';
                      } else if (result === 'postponed') {
                        return 'bg-indigo-50 border-indigo-400';
                      }
                    }

                    // Secondary: Use status
                    if (entry.status === 'pending') {
                      return 'bg-yellow-50 border-yellow-400';
                    } else if (entry.status === 'completed') {
                      return entry.type === 'motion_stated' || entry.type === 'motion_completed' ? 'bg-green-50 border-green-400' :
                        entry.type === 'chair_note' ? 'bg-sky-50 border-sky-400' :
                        entry.type === 'call_to_order' ? 'bg-purple-50 border-purple-400' :
                        entry.type === 'adjournment' ? 'bg-gray-50 border-gray-400' :
                        entry.type === 'election' ? 'bg-orange-50 border-orange-400' :
                        entry.type === 'unanimous_consent' ? 'bg-purple-50 border-purple-400' :
                        entry.type === 'agenda_completion' ? 'bg-blue-50 border-blue-400' :
                        'bg-gray-50 border-gray-300';
                    }

                    // Fallback: Use entry type
                    return entry.type === 'motion_made' ? 'bg-blue-50 border-blue-400' :
                      entry.type === 'motion_seconded' ? 'bg-green-50 border-green-300' :
                      entry.type === 'chair_note' ? 'bg-sky-50 border-sky-400' :
                      entry.type === 'call_to_order' ? 'bg-purple-50 border-purple-400' :
                      entry.type === 'adjournment' ? 'bg-gray-50 border-gray-400' :
                      entry.type === 'election' ? 'bg-orange-50 border-orange-400' :
                      entry.type === 'agenda_completion' ? 'bg-blue-50 border-blue-400' :
                      entry.type === 'unanimous_consent' ? 'bg-purple-50 border-purple-400' :
                      'bg-gray-50 border-gray-300';
                  };

                  const bgClass = getStatusStyling();
                  const nestChildren =
                    entry.motionData &&
                    isCanonicalNestParentForActivity(entry, disposedMainIds) &&
                    groupedMinutes[entry.motionData.id];

                  return (
                    <div key={entry.id}>
                      <div className={`p-2 rounded ${indentClass} text-sm ${bgClass}`}>
                        {/* Main content with leading tags */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-start gap-2 flex-wrap">
                              {/* Motion type tag at front */}
                              <span className={`px-2 py-0.5 rounded text-xs font-normal whitespace-nowrap ${
                                entry.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                entry.type === 'motion_stated' || entry.type === 'motion_completed' ? 'bg-gray-100 text-gray-700' :
                                entry.type === 'motion_made' ? 'bg-gray-100 text-gray-700' :
                                entry.type === 'motion_seconded' ? 'bg-gray-100 text-gray-700' :
                                entry.type === 'amendment_result' ? 'bg-purple-100 text-purple-700' :
                                entry.type === 'call_to_order' ? 'bg-purple-100 text-purple-700' :
                                entry.type === 'adjournment' ? 'bg-gray-100 text-gray-700' :
                                entry.type === 'election' ? 'bg-orange-100 text-orange-700' :
                                entry.type === 'unanimous_consent' ? 'bg-purple-100 text-purple-700' :
                                entry.type === 'agenda_completion' ? 'bg-blue-100 text-blue-700' :
                                entry.type === 'consent_batch_summary' || entry.type === 'consent_item_adopted' ? 'bg-emerald-100 text-emerald-900' :
                                entry.type === 'consent_agenda_pull' ? 'bg-amber-100 text-amber-900' :
                                entry.type === 'chair_note' ? 'bg-sky-100 text-sky-900' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {getMinuteEntryKindLabel(entry, motionTypes)}
                              </span>
                              <div className="flex-1">
                                <div className="whitespace-pre-line font-medium text-gray-900">
                                  {getMinuteEntryDisplayText(entry, motionTypes)}
                                </div>
                                {originalSubtext && (
                                  <div className="text-xs text-gray-500 mt-0.5 italic">
                                    {originalSubtext}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              {entry.timestamp}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Status indicator */}
                            {entry.status === 'pending' && (
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                                <span className="text-xs font-medium text-yellow-700">PENDING</span>
                              </div>
                            )}

                            {/* Amended tag - show if motion has been amended */}
                            {entry.activityOnly && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700 ring-1 ring-slate-200" title="Shown in Activity only; omitted from downloaded minutes">
                                Activity only
                              </span>
                            )}

                            {(entry.isAmended || entry.motionData?.isAmended) && (
                              <span className="px-2 py-1 rounded text-xs font-bold bg-purple-100 text-purple-800">
                                AMENDED
                              </span>
                            )}

                            {/* Result tag (outcome status) - Primary tag */}
                            {(entry.result || entry.motionData?.result) && (
                              <span className={`px-2 py-1 rounded text-xs font-bold ${getMinuteEntryOutcomePillClass(entry)}`}>
                                {getMinuteEntryOutcomeLabel(entry, motionTypes)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Render nested subsidiary motions INSIDE this block (canonical parent only) */}
                        {nestChildren && (
                          <div className="ml-4 pl-3 border-l-2 border-gray-200 space-y-1">
                            {nestChildren.map(nestedEntry =>
                              renderMinuteEntry(nestedEntry, true)
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                };

                return topLevel.map(entry => renderMinuteEntry(entry));
              })()}
            </>
          )}
        </div>
      </div>

      {showChairNoteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowChairNoteModal(false);
              setChairNoteDraft('');
            }
          }}
        >
          <div
            className={`max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl shadow-xl ${
              theme === 'dark' ? 'border border-gray-600 bg-gray-800 text-white' : 'bg-white text-gray-900 shadow-md'
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="chair-note-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`flex items-center justify-between border-b px-4 py-3 ${
                theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
              }`}
            >
              <h3 id="chair-note-title" className="text-lg font-semibold">
                Add chair note
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowChairNoteModal(false);
                  setChairNoteDraft('');
                }}
                className={`rounded p-1 ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 p-4">
              <div>
                <p className={`mb-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  Suggested wording (tap a line to load it into the field — edit as needed):
                </p>
                <div className="flex max-h-44 flex-col gap-2 overflow-y-auto pr-1">
                  {chairNoteSuggestions.map((line, i) => (
                    <button
                      key={`${i}-${line.slice(0, 32)}`}
                      type="button"
                      onClick={() => setChairNoteDraft(line)}
                      className={`rounded-lg border px-3 py-2 text-left text-sm leading-snug transition-colors ${
                        theme === 'dark'
                          ? 'border-gray-600 bg-gray-900/80 hover:bg-gray-700'
                          : 'border-pp-line bg-pp-paper/80 hover:bg-pp-paper'
                      }`}
                    >
                      {line}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="chair-note-text" className="mb-1 block text-sm font-medium">
                  Note for the record
                </label>
                <textarea
                  id="chair-note-text"
                  value={chairNoteDraft}
                  onChange={(e) => setChairNoteDraft(e.target.value)}
                  rows={5}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${
                    theme === 'dark'
                      ? 'border-gray-600 bg-gray-900 text-white placeholder:text-gray-500'
                      : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'
                  }`}
                  placeholder="Type a chair note to appear in Activity and exported minutes…"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowChairNoteModal(false);
                    setChairNoteDraft('');
                  }}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                    theme === 'dark'
                      ? 'border-gray-500 text-gray-200 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitChairNote}
                  disabled={!chairNoteDraft.trim()}
                  className="rounded-lg bg-pp-accent px-3 py-2 text-sm font-medium text-white hover:bg-pp-accentHover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Add to Activity
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabled Motions */}
      {tabledMotions.length > 0 && (
        <div className={`${cardClass} rounded-lg shadow p-4`}>
          <h2 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
            Tabled Motions ({tabledMotions.length})
          </h2>
          <div className="space-y-2">
            {tabledMotions.map((motion) => (
              <div key={motion.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border-l-4 border-orange-400">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded font-medium">
                      TABLED
                    </span>
                    <span className="text-sm text-gray-600">
                      Tabled at {motion.tabledAt} on {motion.tabledDate}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    "{motion.text}"
                  </p>
                  <p className="text-xs text-gray-600">
                    Originally moved by {motion.member}
                  </p>
                </div>
                <button
                  onClick={() => handleTakeFromTable(motion.id)}
                  disabled={currentMotion !== null}
                  className="ml-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={currentMotion ? "Cannot take from table while motion is pending" : "Take this motion from the table"}
                >
                  Take from Table
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Meeting Setup Modal */}
      {showSetupWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-tour="meeting-wizard">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Start meeting</h2>
              </div>
              <p className="text-sm text-gray-500 -mt-2 mb-4">
                Confirm attendance, quorum, and this meeting&apos;s agenda before you begin. Organization
                defaults are applied automatically.
              </p>
              
              <div className="space-y-6">
                {/* Organization Info */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Organization</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                    <input
                      type="text"
                      value={meetingSetup.organizationName}
                      readOnly
                      className="w-full rounded border border-gray-200 bg-gray-50 p-2 text-gray-700"
                      placeholder="Organization"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Organization identity comes from your account. Change the name in organization admin,
                      not here.
                    </p>
                  </div>
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 opacity-70">
                    <span className="font-medium text-gray-600">Parliamentary mode: </span>
                    {parliamentaryMode === 'small_board' && 'Small board'}
                    {parliamentaryMode === 'convention' && 'Convention'}
                    {parliamentaryMode === 'committee' && 'Committee'}
                    {(!parliamentaryMode || parliamentaryMode === 'standard') && 'Standard (full RONR)'}
                    <span className="mt-0.5 block text-xs text-gray-500">
                      Future feature — not currently functional. Mode does not change meeting procedure yet.
                    </span>
                  </div>
                </div>

                {/* Who is driving */}
                <div data-tour="wizard-session-mode">
                  <h3 className="text-lg font-semibold mb-3">Who is driving?</h3>
                  <p className="mb-3 text-xs text-gray-500">
                    Solo chair keeps today&apos;s full console. Secretary-assisted lets the secretary run Motions
                    while the chair uses a lean Prompt view for script and next steps.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label
                      className={`cursor-pointer rounded-xl border p-3 ${
                        (meetingSetup.sessionMode || 'solo') === 'solo'
                          ? 'border-pp-accent bg-pp-accentSoft/40 ring-1 ring-pp-accent/30'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="sessionMode"
                        className="sr-only"
                        checked={(meetingSetup.sessionMode || 'solo') === 'solo'}
                        onChange={() => setMeetingSetup({ ...meetingSetup, sessionMode: 'solo' })}
                      />
                      <span className="block text-sm font-semibold text-gray-900">I&apos;m chairing alone</span>
                      <span className="mt-1 block text-xs text-gray-600">
                        Full console on this device. Open Observer for the room as usual.
                      </span>
                    </label>
                    <label
                      className={`cursor-pointer rounded-xl border p-3 ${
                        meetingSetup.sessionMode === 'assisted'
                          ? 'border-pp-accent bg-pp-accentSoft/40 ring-1 ring-pp-accent/30'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="sessionMode"
                        className="sr-only"
                        checked={meetingSetup.sessionMode === 'assisted'}
                        onChange={() => setMeetingSetup({ ...meetingSetup, sessionMode: 'assisted' })}
                      />
                      <span className="block text-sm font-semibold text-gray-900">Secretary will drive</span>
                      <span className="mt-1 block text-xs text-gray-600">
                        This device is the Driver console. Open Chair Dashboard for the chair&apos;s tablet or second screen.
                      </span>
                    </label>
                  </div>
                </div>

                {/* Quorum Setup */}
                <div data-tour="wizard-quorum">
                  <h3 className="text-lg font-semibold mb-3">Quorum Configuration</h3>
                  <p className="mb-3 text-xs text-gray-500">
                    Pre-filled from organization defaults. Adjust for this meeting if needed.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Total Members</label>
                      <input
                        type="number"
                        value={meetingSetup.totalMembers}
                        onChange={(e) => setMeetingSetup({ ...meetingSetup, totalMembers: parseInt(e.target.value) || 0 })}
                        className="w-full p-2 border rounded"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Present Members</label>
                      <input
                        type="number"
                        value={meetingSetup.presentMembers}
                        onChange={(e) => setMeetingSetup({ ...meetingSetup, presentMembers: parseInt(e.target.value) || 0 })}
                        className="w-full p-2 border rounded"
                        min="0"
                        max={meetingSetup.totalMembers}
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quorum Type</label>
                    <div className="flex gap-4 mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="percentage"
                          checked={meetingSetup.quorumType === 'percentage'}
                          onChange={(e) => setMeetingSetup({ ...meetingSetup, quorumType: e.target.value })}
                          className="mr-2"
                        />
                        Percentage
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="fixed"
                          checked={meetingSetup.quorumType === 'fixed'}
                          onChange={(e) => setMeetingSetup({ ...meetingSetup, quorumType: e.target.value })}
                          className="mr-2"
                        />
                        Fixed Number
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {meetingSetup.quorumType === 'percentage' ? 'Quorum Percentage' : 'Quorum Number'}
                      </label>
                      <input
                        type="number"
                        value={meetingSetup.quorumRequirement}
                        onChange={(e) => setMeetingSetup({ ...meetingSetup, quorumRequirement: parseInt(e.target.value) || 0 })}
                        className="w-full p-2 border rounded"
                        min={meetingSetup.quorumType === 'percentage' ? '1' : '1'}
                        max={meetingSetup.quorumType === 'percentage' ? '100' : meetingSetup.totalMembers}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Current Status: {getQuorumStatus(meetingSetup).text}
                    </p>
                  </div>
                  <div className="mt-4" data-tour="wizard-roll-call">
                    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-pp-line bg-pp-paper p-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={isRollCallEnabledFor(meetingSetup)}
                        onChange={(e) =>
                          setMeetingSetup({
                            ...meetingSetup,
                            rollCallOverride: e.target.checked,
                          })
                        }
                      />
                      <span>
                        <span className="text-sm font-medium text-gray-800">
                          Take roll call from the member roster
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-600">
                          Prompts a roll call against your Member Roster during this meeting and sets
                          Total/Present Members from the result. Leave off to just enter the counts above
                          manually. This only affects <strong>this meeting</strong> —
                          {orgId && hasSupabaseConfig() ? (
                            <>
                              {' '}change the organization default in{' '}
                              <Link to={`/orgs/${orgId}/settings`} className="font-medium text-pp-accent hover:underline">
                                Settings
                              </Link>.
                            </>
                          ) : (
                            ' the organization default is unchanged.'
                          )}
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                {hasSupabaseConfig() && (
                  <div data-tour="wizard-qr-voting">
                    <h3 className="text-lg font-semibold mb-3">Phone / QR voting</h3>
                    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-pp-line bg-pp-paper p-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={Boolean(meetingSetup.enableQrDeviceVoting)}
                        onChange={(e) =>
                          setMeetingSetup({
                            ...meetingSetup,
                            enableQrDeviceVoting: e.target.checked,
                          })
                        }
                      />
                      <span>
                        <span className="text-sm font-medium text-gray-800">
                          Enable QR device voting
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-600">
                          Shows a join code and QR on the Observer screen so phones can cast Aye/No/Abstain
                          or election ballots when you open a remote vote. Chair still confirms tallies.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                {/* Agenda Configuration */}
                <div data-tour="wizard-agenda">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-lg font-semibold">Agenda Configuration</h3>
                    {orgId && hasSupabaseConfig() && (
                      <Link
                        to={`/orgs/${orgId}/settings`}
                        className="text-xs font-medium text-pp-accent hover:underline"
                      >
                        Edit org default in Settings
                      </Link>
                    )}
                  </div>
                  <p className="mb-3 text-xs text-gray-500">
                    Starts from your organization&apos;s default order of business. Expand and edit for{' '}
                    <strong>this meeting only</strong>.
                  </p>
                  {(meetingSetup.organizationSettings || defaultOrganizationSettings(meetingSetup.orderOfBusiness || []))
                    .allowConsentAgenda ? (
                  <label className="mb-2 flex cursor-pointer items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50/90 p-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={meetingAgendaHasConsentCalendar(meetingSetup.orderOfBusiness)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const nextOrder = applyConsentToggleToAgenda(meetingSetup.orderOfBusiness, checked);
                        let items = Array.isArray(meetingSetup.consentAgendaItems)
                          ? [...meetingSetup.consentAgendaItems]
                          : [];
                        if (checked && items.length === 0) {
                          const org =
                            meetingSetup.organizationSettings ||
                            defaultOrganizationSettings(meetingSetup.orderOfBusiness || []);
                          if (org.defaultConsentLines?.length) {
                            items = cloneConsentLinesForMeeting(org.defaultConsentLines);
                          }
                        }
                        if (!checked) items = [];
                        if (checked) setMeetingConsentLinesOpen(true);
                        setMeetingSetup({
                          ...meetingSetup,
                          orderOfBusiness: nextOrder,
                          consentAgendaItems: items,
                        });
                      }}
                    />
                    <span>
                      <span className="text-sm font-medium text-gray-800">Include consent calendar</span>
                      <span className="mt-0.5 block text-xs text-gray-600">
                        Inserts a Consent Calendar block (usually before New Business) for non-controversial items
                        adopted by general consent.
                      </span>
                    </span>
                  </label>
                  ) : (
                    <p className="mb-3 rounded-md border border-pp-line bg-pp-paper px-3 py-2 text-xs text-pp-muted">
                      Consent calendar is disabled for this organization. An admin can enable it under Organization
                      settings.
                    </p>
                  )}

                  {meetingAgendaHasConsentCalendar(meetingSetup.orderOfBusiness) &&
                    (meetingSetup.organizationSettings || defaultOrganizationSettings(meetingSetup.orderOfBusiness || []))
                      .allowConsentAgenda && (
                    <div className="mb-3 overflow-hidden rounded-md border border-emerald-300 bg-white shadow-sm">
                      <button
                        type="button"
                        onClick={() => setMeetingConsentLinesOpen((o) => !o)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-semibold text-emerald-950 hover:bg-emerald-50/80"
                        aria-expanded={meetingConsentLinesOpen}
                      >
                        <span>Consent calendar line items (this meeting)</span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-emerald-800 transition-transform ${
                            meetingConsentLinesOpen ? 'rotate-180' : ''
                          }`}
                          aria-hidden
                        />
                      </button>
                      {meetingConsentLinesOpen && (
                        <div className="space-y-2 border-t border-emerald-100 px-3 py-3">
                          <p className="text-xs text-gray-600">
                            List each item to be read on the consent calendar (e.g. minutes approval, routine
                            reports). Members may ask to pull an item for separate consideration.
                          </p>
                          <div className="max-h-40 space-y-2 overflow-y-auto">
                            {(meetingSetup.consentAgendaItems || []).map((line, index) => (
                              <div key={line.id} className="flex items-start gap-2 rounded border border-gray-200 bg-gray-50 p-2">
                                <span className="mt-1.5 shrink-0 text-xs font-medium text-gray-500">
                                  {index + 1}.
                                </span>
                                <textarea
                                  value={line.text}
                                  onChange={(e) => {
                                    const text = e.target.value;
                                    const list = [...(meetingSetup.consentAgendaItems || [])];
                                    list[index] = { ...line, text };
                                    setMeetingSetup({ ...meetingSetup, consentAgendaItems: list });
                                  }}
                                  rows={2}
                                  className="min-w-0 flex-1 resize-y rounded border border-gray-300 p-2 text-sm"
                                  placeholder="e.g. Approval of October meeting minutes"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const list = (meetingSetup.consentAgendaItems || []).filter(
                                      (_, i) => i !== index
                                    );
                                    setMeetingSetup({ ...meetingSetup, consentAgendaItems: list });
                                  }}
                                  className="shrink-0 rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const list = [...(meetingSetup.consentAgendaItems || []), createConsentLineItem('')];
                                setMeetingSetup({ ...meetingSetup, consentAgendaItems: list });
                              }}
                              className="rounded bg-emerald-700 px-3 py-1.5 text-sm text-white hover:bg-emerald-800"
                            >
                              <Plus size={16} className="mr-1 inline" />
                              Add line item
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const org =
                                  meetingSetup.organizationSettings ||
                                  defaultOrganizationSettings(meetingSetup.orderOfBusiness || []);
                                setMeetingSetup({
                                  ...meetingSetup,
                                  consentAgendaItems: cloneConsentLinesForMeeting(org.defaultConsentLines || []),
                                });
                              }}
                              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50"
                            >
                              Load from organization default lines
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        const org =
                          meetingSetup.organizationSettings ||
                          defaultOrganizationSettings(meetingSetup.orderOfBusiness || []);
                        const { orderOfBusiness, consentAgendaItems } =
                          meetingAgendaFromOrganizationSettings(org);
                        setMeetingSetup({
                          ...meetingSetup,
                          orderOfBusiness,
                          consentAgendaItems,
                        });
                      }}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50"
                    >
                      Reset agenda from organization default
                    </button>
                  </div>
                  <div className="max-h-48 space-y-2 overflow-y-auto">
                    {meetingSetup.orderOfBusiness.map((item, index) => (
                      <div key={item.id} className="flex items-center justify-between rounded border bg-gray-50 p-2">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <span className="shrink-0 text-sm font-medium text-gray-600">{index + 1}.</span>
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) => {
                              const updatedItems = [...meetingSetup.orderOfBusiness];
                              updatedItems[index] = { ...item, title: e.target.value };
                              setMeetingSetup({ ...meetingSetup, orderOfBusiness: updatedItems });
                            }}
                            className="min-w-0 flex-1 rounded border p-1 text-sm"
                          />
                          {isConsentCalendarAgendaRow(item) && (
                            <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-900">
                              Consent
                            </span>
                          )}
                        </div>
                        <div className="ml-2 flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const updatedItems = meetingSetup.orderOfBusiness.filter((_, i) => i !== index);
                              setMeetingSetup({ ...meetingSetup, orderOfBusiness: updatedItems });
                            }}
                            className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newItem = {
                        id: Date.now(),
                        type: 'custom',
                        title: 'New Agenda Item',
                        completed: false,
                      };
                      setMeetingSetup({
                        ...meetingSetup,
                        orderOfBusiness: [...meetingSetup.orderOfBusiness, newItem],
                      });
                    }}
                    className="mt-3 rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
                  >
                    <Plus size={16} className="mr-1 inline" />
                    Add Agenda Item
                  </button>
                </div>
                
                <div className="flex gap-2 pt-4 border-t">
                  <button
                    type="button"
                    data-tour="wizard-begin"
                    onClick={onCompleteMeetingSetup}
                    className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-800"
                  >
                    Begin meeting
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

function OrgScopedApp({ forcedView }) {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { memberships, ready, switchOrganization, activeOrganizationId, session } = useAuth();
  const { skipAuth } = useModuleAccess();

  const routeMembership = memberships.find((m) => m.organization_id === orgId && m.is_active);
  const isObserverLaunch =
    typeof window !== 'undefined' &&
    (window.location.hash === '#observer' ||
      document.body.classList.contains('observer-window') ||
      new URLSearchParams(location.search).get('observer') === '1' ||
      new URLSearchParams(location.search).get('seat') === 'observer' ||
      window.location.hash === '#chair-prompt' ||
      document.body.classList.contains('chair-prompt-window') ||
      new URLSearchParams(location.search).get('chairPrompt') === '1' ||
      new URLSearchParams(location.search).get('seat') === 'prompt');

  useEffect(() => {
    if (!ready || skipAuth) return;
    if (!orgId) return;
    // Memberships hydrate after ready — don't bounce while still empty for a signed-in user
    if (session && memberships.length === 0) return;
    if (!routeMembership) {
      navigate('/orgs', { replace: true });
      return;
    }
    if (activeOrganizationId !== orgId) {
      void switchOrganization(orgId);
    }
  }, [
    ready,
    skipAuth,
    orgId,
    session,
    memberships.length,
    routeMembership,
    activeOrganizationId,
    switchOrganization,
    navigate,
  ]);

  useEffect(() => {
    if (skipAuth || !ready) return;
    if (!orgId) return;
    // Observer / Chair Dashboard popup must stay on the meeting URL — never send it to org home
    if (isObserverLaunch) return;
    if (session && memberships.length === 0) return;
    if (!routeMembership) return;

    const canMeeting = membershipHasModule(routeMembership, 'meeting');
    const canCommittees = membershipHasModule(routeMembership, 'committees');
    const canElections = membershipHasModule(routeMembership, 'elections');
    const canAdminModule =
      routeMembership.role === 'Admin' || membershipHasModule(routeMembership, 'admin');

    if (forcedView === 'meeting' && !canMeeting) navigate(`/orgs/${orgId}`, { replace: true });
    if (forcedView === 'committees' && !canCommittees) navigate(`/orgs/${orgId}`, { replace: true });
    if (forcedView === 'elections' && !canElections) navigate(`/orgs/${orgId}`, { replace: true });
    if (forcedView === 'admin' && !canAdminModule) navigate(`/orgs/${orgId}`, { replace: true });
  }, [
    skipAuth,
    ready,
    forcedView,
    orgId,
    session,
    memberships.length,
    routeMembership,
    isObserverLaunch,
    navigate,
  ]);

  return <PocketParliamentarianApp forcedView={forcedView} orgIdFromRoute={orgId} />;
}

function HomeRedirect() {
  const { ready, memberships, activeOrganizationId, session, allowOfflineAccess } = useAuth();
  const skipAuth = !hasSupabaseConfig();

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pp-paper text-pp-ink">
        <p className="text-sm text-pp-muted">Loading…</p>
      </div>
    );
  }

  if (skipAuth) {
    return <PocketParliamentarianApp />;
  }

  if (!session && !allowOfflineAccess) {
    return <LandingPage />;
  }

  if (activeOrganizationId) {
    return <Navigate to={`/orgs/${activeOrganizationId}`} replace />;
  }
  if (memberships.length === 1) {
    return <Navigate to={`/orgs/${memberships[0].organization_id}`} replace />;
  }
  return <Navigate to="/orgs" replace />;
}

function AppRoutes() {
  const { ready, session, allowOfflineAccess, memberships, activeOrganizationId } = useAuth();
  const location = useLocation();

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pp-paper text-pp-ink">
        <p className="text-sm text-pp-muted">Loading…</p>
      </div>
    );
  }

  const skipAuth = !hasSupabaseConfig();
  const authed = skipAuth || !!session || allowOfflineAccess;

  return (
    <Routes>
      <Route path="/login" element={authed && !skipAuth ? <Navigate to="/" replace /> : <AuthPage />} />
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/r/:joinCode" element={<ParticipantRemotePage />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/join" element={<JoinPage />} />
      <Route
        path="/orgs"
        element={authed ? (skipAuth ? <Navigate to="/" replace /> : <OrgPickerPage />) : <Navigate to="/login" replace />}
      />
      <Route
        path="/orgs/:orgId"
        element={authed ? (skipAuth ? <Navigate to="/" replace /> : <OrgHomePage />) : <Navigate to="/login" replace />}
      />
      <Route
        path="/orgs/:orgId/meeting"
        element={
          authed ? (
            skipAuth ? <PocketParliamentarianApp forcedView="meeting" /> : <OrgScopedApp forcedView="meeting" />
          ) : (
            <Navigate to="/login" replace state={{ from: location }} />
          )
        }
      />
      <Route
        path="/orgs/:orgId/committees"
        element={
          authed ? (
            skipAuth ? (
              <PocketParliamentarianApp forcedView="committees" />
            ) : (
              <OrgScopedApp forcedView="committees" />
            )
          ) : (
            <Navigate to="/login" replace state={{ from: location }} />
          )
        }
      />
      <Route
        path="/orgs/:orgId/elections"
        element={
          authed ? (
            skipAuth ? (
              <PocketParliamentarianApp forcedView="elections" />
            ) : (
              <OrgScopedApp forcedView="elections" />
            )
          ) : (
            <Navigate to="/login" replace state={{ from: location }} />
          )
        }
      />
      <Route
        path="/orgs/:orgId/admin"
        element={authed ? (skipAuth ? <Navigate to="/" replace /> : <AdminPage />) : <Navigate to="/login" replace />}
      />
      <Route
        path="/orgs/:orgId/roster"
        element={authed ? (skipAuth ? <Navigate to="/" replace /> : <RosterPage />) : <Navigate to="/login" replace />}
      />
      <Route
        path="/orgs/:orgId/settings"
        element={
          authed ? (skipAuth ? <Navigate to="/" replace /> : <OrgSettingsPage />) : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/orgs/:orgId/archives"
        element={
          authed ? (skipAuth ? <Navigate to="/" replace /> : <MeetingLogPage />) : <Navigate to="/login" replace />
        }
      />
      {/*
        Platform ("god mode") console. Lives OUTSIDE /orgs/:orgId on purpose —
        those routes go through OrgScopedApp, which bounces anyone without a
        membership for the URL's org, and a platform admin has no membership on
        the orgs they need to inspect.

        `authed && !skipAuth` gates rendering only; PlatformConsolePage re-checks
        isPlatformAdmin, and every request it makes is authorized server-side.
      */}
      <Route
        path="/platform/*"
        element={
          authed && !skipAuth ? (
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center bg-pp-paper">
                  <p className="text-sm text-pp-muted">Loading…</p>
                </div>
              }
            >
              <PlatformConsolePage />
            </Suspense>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route path="/" element={<HomeRedirect />} />
      <Route
        path="/*"
        element={
          authed ? (
            skipAuth ? (
              <PocketParliamentarianApp />
            ) : activeOrganizationId ? (
              <Navigate to={`/orgs/${activeOrganizationId}`} replace />
            ) : memberships.length ? (
              <Navigate to="/orgs" replace />
            ) : (
              <Navigate to="/orgs" replace />
            )
          ) : (
            <Navigate to="/login" replace state={{ from: location }} />
          )
        }
      />
    </Routes>
  );
}

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <TourProvider>
        <DevBanner />
        <AppRoutes />
        <ReportIssueButton />
        {/* Renders only while a platform support elevation is active. */}
        <ElevatedBanner />
      </TourProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
